// Dev-mode Tauri mock.
//
// When the frontend runs inside the Tauri shell, `@tauri-apps/api/core`'s
// invoke() talks to the Rust backend. When the frontend runs under
// `pnpm dev` (plain Vite, no Tauri), invoke() has nowhere to go and
// throws.
//
// This module installs a mock on window.__TAURI_INTERNALS__ *only* if:
//   1. We're in dev mode (import.meta.env.DEV is true).
//   2. The real Tauri internals aren't already present.
//
// The mock keeps in-memory state: a boolean flag for whether a guardian
// has been created, plus the record itself. That's enough for the
// US-SETUP-01 flow to be demoable in a plain browser.
//
// Native production builds (import.meta.env.DEV === false) do not install the
// mock; they rely on the real Tauri runtime. The separately configured
// marketing-site bundle may install it only when its compile-time demo flag
// and the explicit chorepopDemo URL parameter are both present.

import type {
  AllowanceSchedule,
  ChoreSchedule,
  RoutineEntry,
  RoutineInstanceEntry,
  SavingsGoal,
} from '@chorepop/core/client';
import {
  allocateDeposit,
  rebalanceForEditedBucket,
  rebalanceForNewBucket,
} from '@chorepop/core/domain';
import { safeLog } from '@chorepop/core/logging';

declare const CHOREPOP_PUBLIC_DEMO: boolean;

type AnyHandler = (payload: unknown) => unknown | Promise<unknown>;

type Guardian = {
  accountId: number;
  displayName: string;
  displayCurrencyCode: string;
  password: string; // dev-mock only — never stored anywhere persistent
};

type Child = {
  accountId: number;
  displayName: string;
  displayCurrencyCode: string;
  password: string; // dev-mock only
  guardianTypeCode: string;
};

type Bucket = {
  bucketId: number;
  childAccountId: number;
  bucketName: string;
  allocationBps: number;
  isGuardianManaged: boolean;
  requiresApprovalToWithdraw: boolean;
  percentIsFixed: boolean;
  balanceMinorUnits: number;
  displayColor: string | null;
  sortOrder: number;
  isActive: boolean;
  // US-INTEREST-01 — interest config. Defaults to disabled / 0 / null /
  // false on every seed bucket; flipped via bucket_update only.
  interestEnabled: boolean;
  interestRateBps: number;
  interestPeriodCode: 'daily' | 'weekly' | 'monthly' | 'yearly' | null;
  interestActuallyCredits: boolean;
};

type DemoBucketTemplateItem = {
  itemId: number;
  bucketName: string;
  displayColor: string | null;
  allocationBps: number;
  isGuardianManaged: boolean;
  requiresApproval: boolean;
  percentIsFixed: boolean;
  sortOrder: number;
};

type DemoBucketTemplate = {
  templateId: number;
  createdByAccountId: number;
  templateName: string;
  templateDescription: string | null;
  items: DemoBucketTemplateItem[];
  createdAtUtc: string;
};

type DemoRecoverableChild = Child & {
  deletedAtUtc: string;
  purgeAfterUtc: string;
  buckets: Bucket[];
};

/** Unified ledger entry kept in dev mock state so history_list has something to return. */
type LedgerEntry =
  | {
      kind: 'deposit';
      depositId: number;
      childAccountId: number;
      atUtc: string;
      amountMinorUnits: number;
      sourceCode: string;
      sourceLabel: string;
      note: string | null;
      allocations: { bucketId: number; bucketName: string; amountMinorUnits: number }[];
      statusCode?: string;
      statusLabel?: string;
      decisionReason?: string | null;
      decidedByDisplayName?: string | null;
    }
  | {
      kind: 'withdrawal';
      withdrawalId: number;
      childAccountId: number;
      atUtc: string;
      amountMinorUnits: number;
      bucketId: number;
      bucketName: string;
      reasonCode: string;
      reasonLabel: string;
      reasonNote: string | null;
      statusCode?: string;
      statusLabel?: string;
      decisionReason?: string | null;
      decidedByDisplayName?: string | null;
      // US-WITHDRAWAL-05 — guardian reconciliation flag. Defaults to
      // false; flipped via withdrawal_set_reconciled.
      reconciled: boolean;
    }
  | {
      kind: 'transfer';
      transferId: number;
      transferMode: 'child_request' | 'guardian_direct';
      childAccountId: number;
      atUtc: string;
      amountMinorUnits: number;
      sourceBucketId: number;
      sourceBucketName: string;
      targetBucketId: number | null;
      targetBucketName: string | null;
      note: string | null;
      statusCode: 'approved' | 'rejected' | 'cancelled' | 'posted';
      statusLabel: string;
      decisionReason: string | null;
      decidedByDisplayName: string | null;
    };

const DEFAULT_BUCKETS: Omit<Bucket, 'bucketId' | 'childAccountId'>[] = [
  {
    bucketName: 'Spend',
    allocationBps: 3500,
    isGuardianManaged: false,
    requiresApprovalToWithdraw: false,
    percentIsFixed: false,
    balanceMinorUnits: 0,
    displayColor: '#f07a2e',
    sortOrder: 0,
    isActive: true,
    interestEnabled: false,
    interestRateBps: 0,
    interestPeriodCode: null,
    interestActuallyCredits: false,
  },
  {
    bucketName: 'Save',
    allocationBps: 5000,
    isGuardianManaged: true,
    requiresApprovalToWithdraw: true,
    percentIsFixed: true,
    balanceMinorUnits: 0,
    displayColor: '#2d6a9f',
    sortOrder: 1,
    isActive: true,
    interestEnabled: false,
    interestRateBps: 0,
    interestPeriodCode: null,
    interestActuallyCredits: false,
  },
  {
    bucketName: 'Tithe',
    allocationBps: 1500,
    isGuardianManaged: false,
    requiresApprovalToWithdraw: false,
    percentIsFixed: true,
    balanceMinorUnits: 0,
    displayColor: '#4a9b5f',
    sortOrder: 2,
    isActive: true,
    interestEnabled: false,
    interestRateBps: 0,
    interestPeriodCode: null,
    interestActuallyCredits: false,
  },
];

// Lockout window per REQ-9.6.
const FAILURE_WINDOW_MS = 60_000;
const FAILURE_THRESHOLD = 3;
const LOCKOUT_DURATION_MS = 5 * 60_000;

type MockState = {
  accountsExist: boolean;
  guardian: Guardian | null;
  children: Child[];
  buckets: Bucket[];
  activeSessionId: string | null;
  activeSessionExpiresAtUtc: string | null;
  // Per-account failure timestamps and active lockouts. Keyed by
  // accountId so the dev mock matches the real backend's per-account
  // isolation.
  failureTimestampsMs: Map<number, number[]>;
  lockoutExpiresAtMs: Map<number, number>;
  nextAccountId: number;
  nextBucketId: number;
  nextDepositId: number;
  nextWithdrawalId: number;
  nextChoreId: number;
  nextChoreCompletionId: number;
  choreSchedules: ChoreSchedule[];
  nextChoreScheduleId: number;
  legacyBucketTemplates: {
    templateId: number;
    createdByAccountId: number;
    templateName: string;
    configJson: string;
    createdAtUtc: string;
  }[];
  bucketTemplates: DemoBucketTemplate[];
  nextBucketTemplateId: number;
  routines: RoutineEntry[];
  routineInstances: RoutineInstanceEntry[];
  nextRecurringTaskId: number;
  nextRoutineInstanceId: number;
  goals: SavingsGoal[];
  nextGoalId: number;
  allowances: AllowanceSchedule[];
  nextAllowanceScheduleId: number;
  lastAuthAccountId: number | null;
  ledger: LedgerEntry[];
  /** Map of child_account_id → current guardian_type_code. Set to 'parent' on child create. */
  guardianTypeByChildId: Map<number, string>;
  /** Active chores, by chore_id. */
  chores: Map<
    number,
    {
      choreId: number;
      /** US-CHORE-07: null for master-list chores. */
      childAccountId: number | null;
      /** US-CHORE-07: the guardian who created the chore. */
      createdByAccountId: number;
      choreName: string;
      choreDescription: string | null;
      choreTypeCode: string;
      choreTypeLabel: string;
      zoneName: string | null;
      rewardMinorUnits: number;
      recurrencePattern: string | null;
      isActive: boolean;
      /** US-CHORE-07: only meaningful when childAccountId is null. */
      requiresApprovalToClaim: boolean;
      createdAtUtc: string;
    }
  >;
  /** Set of chore_ids that currently have a claimed (non-terminal) completion. */
  openClaims: Set<number>;
  /** Per-completion record (US-CHORE-04). Keyed by chore_completion_id. */
  choreCompletions: Map<
    number,
    {
      choreCompletionId: number;
      choreId: number;
      childAccountId: number;
      childDisplayName: string;
      choreName: string;
      choreTypeLabel: string;
      rewardMinorUnits: number;
      claimedAtUtc: string;
      status: 'claimed' | 'approved' | 'rejected';
      // US-UI-CHORE-REJECTIONS — rejection details for the
      // most-recent-rejected lookup. Populated by
      // chore_completion_reject; null otherwise.
      decidedAtUtc: string | null;
      decisionReason: string | null;
      /** US-CHORE-10 (10c) — set when the child archives a
       *  rejected-and-removed completion. Filtered out of child-side
       *  list queries. */
      childArchived: boolean;
    }
  >;
  /** Pending deposits submitted by children, awaiting guardian approval. */
  pendingDeposits: {
    depositId: number;
    childAccountId: number;
    childDisplayName: string;
    amountMinorUnits: number;
    sourceCode: string;
    sourceLabel: string;
    note: string | null;
    requestedAtUtc: string;
  }[];
  childApprovalNotices: {
    childApprovalNoticeId: number;
    childAccountId: number;
    noticeType: 'deposit' | 'withdrawal';
    sourceEntityId: number;
    amountMinorUnits: number;
    currencyCode: string;
    approvedAtUtc: string;
  }[];
  nextChildApprovalNoticeId: number;
  recoverableChildren: DemoRecoverableChild[];
  loginSecurityAlerts: {
    loginSecurityAlertId: number;
    targetAccountId: number;
    targetDisplayName: string;
    targetRoleCode: 'guardian' | 'child';
    credentialKind: 'password' | 'pin';
    lockedAtUtc: string;
    lockoutExpiresAtUtc: string;
  }[];
  nextLoginSecurityAlertId: number;
  validSettingsTokens: Set<string>;
  /** Pending withdrawals submitted by children from approval-required buckets. */
  pendingWithdrawals: {
    withdrawalId: number;
    childAccountId: number;
    childDisplayName: string;
    bucketId: number;
    bucketName: string;
    amountMinorUnits: number;
    reasonCode: string;
    reasonLabel: string;
    reasonNote: string | null;
    requestedAtUtc: string;
  }[];
  /** US-SETUP-04: additional guardians beyond the family creator. The
   *  creator is tracked as `state.guardian`; added guardians live here.
   *  This lets the mock honor "remove anyone else" rules while keeping
   *  the creator singleton elsewhere in the mock simple. */
  additionalGuardians: {
    accountId: number;
    displayName: string;
    displayCurrencyCode: string;
    isActive: boolean;
    deletedAtUtc: string | undefined;
    purgeAfterUtc: string | undefined;
  }[];
  /** US-AUTH-07 — PINs in the dev mock.
   *
   *  Because the dev mock only backs Playwright specs, we store PIN as
   *  plaintext keyed by accountId. No hashing (the real backend uses
   *  Argon2id). Per-device isolation isn't modelled here since Playwright
   *  runs on "one device" — so there's a single shared namespace. */
  pinsByAccount: Map<number, string>;
  pinFailuresByAccount: Map<number, { count: number; firstMissAtMs: number }>;
  pinLockoutsByAccount: Map<number, number>;
  /** US-FX-03: in-memory mirror of the exchange_rates table.
   *  Append-only; ordered by effectiveAtUtc desc when listed. */
  exchangeRates: {
    exchangeRateId: number;
    fromCurrencyCode: string;
    toCurrencyCode: string;
    ratePpm: number;
    effectiveAtUtc: string;
    enteredByDisplayName: string;
    createdAtUtc: string;
  }[];
  nextExchangeRateId: number;
  /** US-CHORE-08/09 — pending chore claims (master-list, approval-gated).
   *  Map by id. Append-only; status flips in place. */
  pendingChoreClaims: Map<
    number,
    {
      pendingChoreClaimId: number;
      choreId: number;
      requestingChildAccountId: number;
      status: 'pending' | 'approved' | 'rejected';
      requestedAtUtc: string;
      decidedAtUtc: string | null;
      decidedByAccountId: number | null;
      decisionReason: string | null;
    }
  >;
  nextPendingChoreClaimId: number;
  /** US-BUCKET-09/10/11/12 — bucket transfer rows (append-only;
   *  status flips in place). Map by id. */
  bucketTransfers: Map<
    number,
    {
      bucketTransferId: number;
      childAccountId: number;
      sourceBucketId: number;
      /** US-BUCKET-12: nullable for zero-balance deletion-only requests. */
      targetBucketId: number | null;
      /** US-BUCKET-12: amount = 0 allowed for zero-balance deletion. */
      amountMinorUnits: number;
      note: string | null;
      status: 'pending' | 'approved' | 'rejected' | 'cancelled';
      requestedAtUtc: string;
      decidedAtUtc: string | null;
      decidedByAccountId: number | null;
      decisionReason: string | null;
      /** US-BUCKET-12: when 1, approve also deactivates source. */
      deactivateSourceOnApprove: boolean;
    }
  >;
  nextBucketTransferId: number;
  familyChallenge: {
    familyChallengeId: number;
    challengeName: string;
    targetDaysPerChild: number;
    startsOn: string;
    endsOn: string;
    children: { childAccountId: number; displayName: string; completedDays: number }[];
  } | null;
  nextFamilyChallengeId: number;
};

const state: MockState = {
  accountsExist: false,
  guardian: null,
  children: [],
  buckets: [],
  activeSessionId: null,
  activeSessionExpiresAtUtc: null,
  failureTimestampsMs: new Map(),
  lockoutExpiresAtMs: new Map(),
  nextAccountId: 2,
  nextBucketId: 1,
  nextDepositId: 1,
  nextWithdrawalId: 1,
  nextChoreId: 1,
  nextChoreCompletionId: 1,
  choreSchedules: [],
  nextChoreScheduleId: 1,
  legacyBucketTemplates: [],
  bucketTemplates: [],
  nextBucketTemplateId: 1,
  routines: [],
  routineInstances: [],
  nextRecurringTaskId: 1,
  nextRoutineInstanceId: 1,
  goals: [],
  nextGoalId: 1,
  allowances: [],
  nextAllowanceScheduleId: 1,
  lastAuthAccountId: null,
  ledger: [],
  guardianTypeByChildId: new Map(),
  chores: new Map(),
  openClaims: new Set(),
  choreCompletions: new Map(),
  pendingDeposits: [],
  childApprovalNotices: [],
  nextChildApprovalNoticeId: 1,
  recoverableChildren: [],
  loginSecurityAlerts: [],
  nextLoginSecurityAlertId: 1,
  validSettingsTokens: new Set(),
  pendingWithdrawals: [],
  additionalGuardians: [],
  pinsByAccount: new Map(),
  pinFailuresByAccount: new Map(),
  pinLockoutsByAccount: new Map(),
  exchangeRates: [],
  nextExchangeRateId: 1,
  pendingChoreClaims: new Map(),
  nextPendingChoreClaimId: 1,
  bucketTransfers: new Map(),
  nextBucketTransferId: 1,
  familyChallenge: null,
  nextFamilyChallengeId: 1,
};

const PUBLIC_DEMO_PASSWORD = '1234567890';
const PUBLIC_DEMO_PIN = '123456';

export function isPublicDemoRequested(): boolean {
  if (typeof window === 'undefined') return false;
  const environment = (
    import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }
  ).env;
  const publicDemoBuild = typeof CHOREPOP_PUBLIC_DEMO !== 'undefined' && CHOREPOP_PUBLIC_DEMO;
  return (
    new URLSearchParams(window.location.search).get('chorepopDemo') === '1' &&
    (environment?.DEV === true || publicDemoBuild)
  );
}

/** Seed the browser-only public demo before Solid mounts. */
function seedPublicDemoState(): void {
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (
        key &&
        (/^chorepop:account:\d+:/.test(key) ||
          /^chorepop:acknowledged-approval-notices:\d+$/.test(key) ||
          /^chorepop:hidden-family-content:\d+$/.test(key))
      ) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // The in-memory seed still resets when browser storage is unavailable.
  }

  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1_000).toISOString();
  const thisMorning = new Date(now.setHours(8, 15, 0, 0)).toISOString();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;

  state.accountsExist = true;
  state.guardian = {
    accountId: 1,
    displayName: 'Alex',
    displayCurrencyCode: 'GBP',
    password: PUBLIC_DEMO_PASSWORD,
  };
  state.children = [
    {
      accountId: 2,
      displayName: 'Sam',
      displayCurrencyCode: 'GBP',
      password: PUBLIC_DEMO_PASSWORD,
      guardianTypeCode: 'parent',
    },
    {
      accountId: 3,
      displayName: 'Maya',
      displayCurrencyCode: 'GBP',
      password: PUBLIC_DEMO_PASSWORD,
      guardianTypeCode: 'parent',
    },
  ];
  state.guardianTypeByChildId = new Map([
    [2, 'parent'],
    [3, 'parent'],
  ]);
  state.buckets = [
    { ...DEFAULT_BUCKETS[0]!, bucketId: 1, childAccountId: 2, balanceMinorUnits: 1_425 },
    { ...DEFAULT_BUCKETS[1]!, bucketId: 2, childAccountId: 2, balanceMinorUnits: 4_850 },
    {
      ...DEFAULT_BUCKETS[2]!,
      bucketId: 3,
      childAccountId: 2,
      bucketName: 'Give',
      balanceMinorUnits: 725,
    },
    { ...DEFAULT_BUCKETS[0]!, bucketId: 4, childAccountId: 3, balanceMinorUnits: 910 },
    { ...DEFAULT_BUCKETS[1]!, bucketId: 5, childAccountId: 3, balanceMinorUnits: 3_200 },
    {
      ...DEFAULT_BUCKETS[2]!,
      bucketId: 6,
      childAccountId: 3,
      bucketName: 'Give',
      balanceMinorUnits: 540,
    },
  ];
  state.nextAccountId = 4;
  state.nextBucketId = 7;
  state.pinsByAccount = new Map([
    [1, PUBLIC_DEMO_PIN],
    [2, PUBLIC_DEMO_PIN],
    [3, PUBLIC_DEMO_PIN],
  ]);
  state.chores = new Map([
    [
      1,
      {
        choreId: 1,
        childAccountId: 2,
        createdByAccountId: 1,
        choreName: 'Put away clean clothes',
        choreDescription: 'Fold everything and put it in the right drawers.',
        choreTypeCode: 'one_time',
        choreTypeLabel: 'Chore',
        zoneName: null,
        rewardMinorUnits: 150,
        recurrencePattern: null,
        isActive: true,
        requiresApprovalToClaim: false,
        createdAtUtc: yesterday,
      },
    ],
    [
      2,
      {
        choreId: 2,
        childAccountId: 2,
        createdByAccountId: 1,
        choreName: 'Help unload the dishwasher',
        choreDescription: null,
        choreTypeCode: 'one_time',
        choreTypeLabel: 'Chore',
        zoneName: null,
        rewardMinorUnits: 100,
        recurrencePattern: null,
        isActive: true,
        requiresApprovalToClaim: false,
        createdAtUtc: yesterday,
      },
    ],
    [
      3,
      {
        choreId: 3,
        childAccountId: 3,
        createdByAccountId: 1,
        choreName: 'Water the plants',
        choreDescription: 'Use the small watering can.',
        choreTypeCode: 'one_time',
        choreTypeLabel: 'Chore',
        zoneName: null,
        rewardMinorUnits: 125,
        recurrencePattern: null,
        isActive: true,
        requiresApprovalToClaim: false,
        createdAtUtc: yesterday,
      },
    ],
  ]);
  state.nextChoreId = 4;
  state.choreSchedules = [
    {
      scheduleId: 1,
      createdByAccountId: 1,
      childAccountId: 3,
      childDisplayName: 'Maya',
      choreName: 'Feed the pet',
      choreDescription: 'Top up the bowl before breakfast.',
      zoneName: null,
      rewardMinorUnits: 75,
      frequency: 'daily',
      dayOfWeek: null,
      dayOfMonth: null,
      startDate: today,
      isActive: true,
      pausedUntilDate: null,
      lastGeneratedDate: null,
      currentStreak: 0,
      bestStreak: 0,
    },
  ];
  state.nextChoreScheduleId = 2;
  state.legacyBucketTemplates = [];
  state.bucketTemplates = [];
  state.nextBucketTemplateId = 1;
  state.choreCompletions = new Map([
    [
      1,
      {
        choreCompletionId: 1,
        choreId: 3,
        childAccountId: 3,
        childDisplayName: 'Maya',
        choreName: 'Water the plants',
        choreTypeLabel: 'Chore',
        rewardMinorUnits: 125,
        claimedAtUtc: thisMorning,
        status: 'claimed',
        decidedAtUtc: null,
        decisionReason: null,
        childArchived: false,
      },
    ],
  ]);
  state.openClaims = new Set([3]);
  state.nextChoreCompletionId = 2;
  state.routines = [
    {
      recurringTaskId: 1,
      childAccountId: 2,
      taskName: 'Morning ready',
      taskNotes: 'Get ready before school.',
      rewardMinorUnits: 200,
      missBehaviour: 'expire_quietly',
      penaltyBucketId: null,
      penaltyMinorUnits: 0,
      isPaused: false,
      pauseUntilDate: null,
      repeatIntervalWeeks: 1,
      scheduleDays: [now.getDay()],
      visibleTime: '06:30',
      deadlineTime: '08:15',
      subTasks: [
        { subTaskId: 1, sortOrder: 0, subTaskName: 'Get dressed' },
        { subTaskId: 2, sortOrder: 1, subTaskName: 'Brush teeth' },
        { subTaskId: 3, sortOrder: 2, subTaskName: 'Pack school bag' },
      ],
    },
  ];
  state.routineInstances = [
    {
      instanceId: 1,
      recurringTaskId: 1,
      childAccountId: 2,
      childDisplayName: 'Sam',
      scheduledDate: today,
      taskName: 'Morning ready',
      rewardMinorUnits: 200,
      deadlineTime: '08:15',
      status: 'pending_child',
      submittedAtUtc: null,
      decidedAtUtc: null,
      rejectionReason: null,
      subTasks: state.routines[0]!.subTasks,
      tickedSubTaskIds: [],
    },
  ];
  state.nextRecurringTaskId = 2;
  state.nextRoutineInstanceId = 2;
  state.goals = [
    {
      goalId: 1,
      childAccountId: 2,
      bucketId: 2,
      bucketName: 'Save',
      currentMinorUnits: 4_850,
      goalName: 'New bicycle',
      goalNotes: 'Saving for a blue bicycle.',
      targetMinorUnits: 10_000,
      status: 'active',
      progressPct: 49,
      matchBps: null,
      matchFixedMinorUnits: null,
      goalEmoji: '🚲',
      goalType: 'saving',
      createdAtUtc: yesterday,
      completedAtUtc: null,
    },
    {
      goalId: 2,
      childAccountId: 2,
      bucketId: 3,
      bucketName: 'Give',
      currentMinorUnits: 725,
      goalName: 'Animal shelter',
      goalNotes: 'Help buy food for rescued pets.',
      targetMinorUnits: 2_500,
      status: 'active',
      progressPct: 29,
      matchBps: null,
      matchFixedMinorUnits: null,
      goalEmoji: '🐾',
      goalType: 'giving',
      createdAtUtc: yesterday,
      completedAtUtc: null,
    },
  ];
  state.nextGoalId = 3;
  state.allowances = [
    {
      scheduleId: 1,
      childAccountId: 2,
      childDisplayName: 'Sam',
      createdByAccountId: 1,
      frequency: 'weekly',
      amountMinorUnits: 500,
      dayOfWeek: 6,
      dayOfMonth: null,
      startDate: today,
      isActive: true,
      pausedUntilDate: null,
      lastGeneratedDate: null,
      label: 'Weekly pocket money',
    },
  ];
  state.nextAllowanceScheduleId = 2;
  state.pendingDeposits = [
    {
      depositId: 1,
      childAccountId: 2,
      childDisplayName: 'Sam',
      amountMinorUnits: 500,
      sourceCode: 'gift',
      sourceLabel: 'Gift',
      note: 'Birthday money from Gran',
      requestedAtUtc: thisMorning,
    },
  ];
  state.childApprovalNotices = [];
  state.nextChildApprovalNoticeId = 1;
  state.recoverableChildren = [];
  state.loginSecurityAlerts = [];
  state.nextLoginSecurityAlertId = 1;
  state.validSettingsTokens = new Set();
  state.nextDepositId = 2;
  state.ledger = [
    {
      kind: 'deposit',
      depositId: 20,
      childAccountId: 2,
      atUtc: yesterday,
      amountMinorUnits: 300,
      sourceCode: 'chore',
      sourceLabel: 'Chore',
      note: 'Garden tidy-up',
      allocations: [
        { bucketId: 1, bucketName: 'Spend', amountMinorUnits: 105 },
        { bucketId: 2, bucketName: 'Save', amountMinorUnits: 150 },
        { bucketId: 3, bucketName: 'Give', amountMinorUnits: 45 },
      ],
      statusCode: 'approved',
      statusLabel: 'Approved',
      decisionReason: null,
      decidedByDisplayName: 'Alex',
    },
  ];
  state.familyChallenge = null;
  state.nextFamilyChallengeId = 1;
}

function publicDemoToday(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function requirePublicDemoAccount(): number {
  if (state.activeSessionId === null || state.lastAuthAccountId === null) {
    throw { kind: 'unauthorized', message: 'Please sign in to the demo again.' };
  }
  return state.lastAuthAccountId;
}

function requirePublicDemoGuardian(): number {
  const accountId = requirePublicDemoAccount();
  if (!state.guardian || accountId !== state.guardian.accountId) {
    throw { kind: 'unauthorized', message: 'This demo action is for guardians.' };
  }
  return accountId;
}

function demoRoutineInstance(instanceId: number): RoutineInstanceEntry {
  const instance = state.routineInstances.find((entry) => entry.instanceId === instanceId);
  if (!instance) {
    throw { kind: 'not_found', message: 'That routine occurrence was not found.' };
  }
  return instance;
}

function demoGoal(goalId: number): SavingsGoal {
  const goal = state.goals.find((entry) => entry.goalId === goalId);
  if (!goal) throw { kind: 'not_found', message: 'That goal was not found.' };
  return goal;
}

function demoChild(childAccountId: number): Child {
  const child = state.children.find((entry) => entry.accountId === childAccountId);
  if (!child) throw { kind: 'not_found', message: 'Choose a child in this demo family.' };
  return child;
}

function demoAddTemplateBuckets(
  childAccountId: number,
  items: {
    bucketName: string;
    displayColor: string | null;
    allocationBps: number;
    isGuardianManaged: boolean;
    requiresApprovalToWithdraw: boolean;
    percentIsFixed: boolean;
  }[],
): number {
  demoChild(childAccountId);
  let nextSortOrder =
    Math.max(
      -1,
      ...state.buckets
        .filter((bucket) => bucket.childAccountId === childAccountId)
        .map((bucket) => bucket.sortOrder),
    ) + 1;
  for (const item of items) {
    state.buckets.push({
      bucketId: state.nextBucketId++,
      childAccountId,
      bucketName: item.bucketName,
      allocationBps: item.allocationBps,
      isGuardianManaged: item.isGuardianManaged,
      requiresApprovalToWithdraw: item.requiresApprovalToWithdraw,
      percentIsFixed: item.percentIsFixed,
      balanceMinorUnits: 0,
      displayColor: item.displayColor,
      sortOrder: nextSortOrder++,
      isActive: true,
      interestEnabled: false,
      interestRateBps: 0,
      interestPeriodCode: null,
      interestActuallyCredits: false,
    });
  }
  return items.length;
}

function demoChoreEntry(choreId: number) {
  const chore = state.chores.get(choreId);
  if (!chore) throw { kind: 'not_found', message: 'That chore was not found.' };
  const rejected = Array.from(state.choreCompletions.values())
    .filter((completion) => completion.choreId === choreId && completion.status === 'rejected')
    .sort((left, right) => (right.decidedAtUtc ?? '').localeCompare(left.decidedAtUtc ?? ''))[0];
  return {
    ...chore,
    childDisplayName:
      chore.childAccountId === null
        ? null
        : (state.children.find((child) => child.accountId === chore.childAccountId)?.displayName ??
          null),
    hasOpenClaim: state.openClaims.has(choreId),
    lastRejectedAtUtc: rejected?.decidedAtUtc ?? null,
    lastRejectionReason: rejected?.decisionReason ?? null,
    lastRejectedCompletionId: rejected?.choreCompletionId ?? null,
  };
}

const publicDemoHandlers: Record<string, AnyHandler> = {
  runtime_platform: async () => 'ios',
  device_register: async () => ({ deviceId: 'public-demo', assignment: null }),
  biometric_status_for_account: async () => ({ enabled: false, biometryLabel: null }),
  family_content_policy_status: async () => ({
    roleCode:
      state.lastAuthAccountId !== null &&
      state.children.some((child) => child.accountId === state.lastAuthAccountId)
        ? 'child'
        : 'guardian',
    familyTermsVersion: '2026-08-29',
    familyTermsAccepted: true,
    childRulesVersion: '2026-08-29',
    childRulesAccepted: true,
  }),
  routines_generate_today: async () => ({ created: 0, missedProcessed: 0 }),
  allowance_generate_due: async () => ({ created: 0 }),
  routine_create: async (payload) => {
    const accountId = requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          childAccountId: number;
          taskName: string;
          taskNotes?: string;
          rewardMinorUnits: number;
          missBehaviour: RoutineEntry['missBehaviour'];
          penaltyBucketId?: number | null;
          penaltyMinorUnits: number;
          scheduleDays: number[];
          repeatIntervalWeeks: number;
          visibleTime: string;
          deadlineTime: string;
          subTasks: string[];
        };
      }
    ).input;
    if (!state.children.some((child) => child.accountId === input.childAccountId)) {
      throw { kind: 'not_found', message: 'Choose a child in this demo family.' };
    }
    const recurringTaskId = state.nextRecurringTaskId++;
    const subTasks = input.subTasks.map((name, index) => ({
      subTaskId: recurringTaskId * 100 + index + 1,
      sortOrder: index,
      subTaskName: name,
    }));
    const routine: RoutineEntry = {
      recurringTaskId,
      childAccountId: input.childAccountId,
      taskName: input.taskName.trim(),
      taskNotes: input.taskNotes?.trim() || null,
      rewardMinorUnits: input.rewardMinorUnits,
      missBehaviour: input.missBehaviour,
      penaltyBucketId: input.penaltyBucketId ?? null,
      penaltyMinorUnits: input.penaltyMinorUnits,
      isPaused: false,
      pauseUntilDate: null,
      repeatIntervalWeeks: input.repeatIntervalWeeks,
      scheduleDays: [...input.scheduleDays],
      visibleTime: input.visibleTime,
      deadlineTime: input.deadlineTime,
      subTasks,
    };
    state.routines.push(routine);
    if (routine.scheduleDays.includes(new Date().getDay())) {
      const child = state.children.find((entry) => entry.accountId === routine.childAccountId)!;
      state.routineInstances.push({
        instanceId: state.nextRoutineInstanceId++,
        recurringTaskId,
        childAccountId: routine.childAccountId,
        childDisplayName: child.displayName,
        scheduledDate: publicDemoToday(),
        taskName: routine.taskName,
        rewardMinorUnits: routine.rewardMinorUnits,
        deadlineTime: routine.deadlineTime,
        status: 'pending_child',
        submittedAtUtc: null,
        decidedAtUtc: null,
        rejectionReason: null,
        subTasks,
        tickedSubTaskIds: [],
      });
    }
    safeLog('info', '[dev-mock] public routine_create OK', { accountId, recurringTaskId });
    return { recurringTaskId };
  },
  routine_instances_today: async () => {
    const accountId = requirePublicDemoAccount();
    const entries = state.routineInstances.filter(
      (instance) => instance.scheduledDate === publicDemoToday(),
    );
    return accountId === state.guardian?.accountId
      ? entries
      : entries.filter((instance) => instance.childAccountId === accountId);
  },
  routines_list_for_guardian: async () => {
    requirePublicDemoGuardian();
    return state.routines;
  },
  routines_list_for_child: async () => {
    const accountId = requirePublicDemoAccount();
    return state.routines.filter((routine) => routine.childAccountId === accountId);
  },
  routine_tick_sub_task: async (payload) => {
    const accountId = requirePublicDemoAccount();
    const input = (payload as { input: { instanceId: number; subTaskId: number } }).input;
    const instance = demoRoutineInstance(input.instanceId);
    if (instance.childAccountId !== accountId || instance.status !== 'pending_child') {
      throw { kind: 'unauthorized', message: 'This routine cannot be changed here.' };
    }
    instance.tickedSubTaskIds = instance.tickedSubTaskIds.includes(input.subTaskId)
      ? instance.tickedSubTaskIds.filter((id) => id !== input.subTaskId)
      : [...instance.tickedSubTaskIds, input.subTaskId];
    return undefined;
  },
  routine_submit: async (payload) => {
    const accountId = requirePublicDemoAccount();
    const input = (payload as { input: { instanceId: number } }).input;
    const instance = demoRoutineInstance(input.instanceId);
    if (instance.childAccountId !== accountId || instance.status !== 'pending_child') {
      throw { kind: 'unauthorized', message: 'This routine cannot be submitted here.' };
    }
    if (instance.subTasks.some((task) => !instance.tickedSubTaskIds.includes(task.subTaskId))) {
      throw { kind: 'validation', message: 'Tick every routine step before submitting.' };
    }
    instance.status = 'pending_guardian';
    instance.submittedAtUtc = new Date().toISOString();
    return undefined;
  },
  routine_resubmit: async (payload) => {
    const accountId = requirePublicDemoAccount();
    const input = (payload as { input: { instanceId: number } }).input;
    const instance = demoRoutineInstance(input.instanceId);
    if (
      instance.childAccountId !== accountId ||
      instance.status !== 'rejected' ||
      instance.scheduledDate !== publicDemoToday()
    ) {
      throw { kind: 'validation', message: 'Only a routine rejected today can be resubmitted.' };
    }
    instance.status = 'pending_guardian';
    instance.submittedAtUtc = new Date().toISOString();
    instance.decidedAtUtc = null;
    instance.rejectionReason = null;
    return undefined;
  },
  routine_pending_approvals: async () => {
    requirePublicDemoGuardian();
    return state.routineInstances.filter((instance) => instance.status === 'pending_guardian');
  },
  routine_approve: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { instanceId: number } }).input;
    const instance = demoRoutineInstance(input.instanceId);
    if (instance.status !== 'pending_guardian') {
      throw { kind: 'validation', message: 'That routine is not waiting for approval.' };
    }
    instance.status = 'approved';
    instance.decidedAtUtc = new Date().toISOString();
    if (instance.rewardMinorUnits > 0 && instance.childAccountId !== undefined) {
      const childBuckets = state.buckets
        .filter((bucket) => bucket.childAccountId === instance.childAccountId)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const splits = allocateDeposit(
        instance.rewardMinorUnits,
        childBuckets.map((bucket) => ({
          bucketId: bucket.bucketId,
          allocationBps: bucket.allocationBps,
          sortOrder: bucket.sortOrder,
        })),
      );
      for (const split of splits) {
        const bucket = childBuckets.find((entry) => entry.bucketId === split.bucketId);
        if (bucket) bucket.balanceMinorUnits += split.amountMinorUnits;
      }
      const depositId = state.nextDepositId++;
      state.ledger.push({
        kind: 'deposit',
        depositId,
        childAccountId: instance.childAccountId,
        atUtc: instance.decidedAtUtc,
        amountMinorUnits: instance.rewardMinorUnits,
        sourceCode: 'chore',
        sourceLabel: 'Routine reward',
        note: instance.taskName,
        allocations: splits.map((split) => ({
          bucketId: split.bucketId,
          bucketName:
            childBuckets.find((bucket) => bucket.bucketId === split.bucketId)?.bucketName ?? '',
          amountMinorUnits: split.amountMinorUnits,
        })),
      });
      state.childApprovalNotices.push({
        childApprovalNoticeId: state.nextChildApprovalNoticeId++,
        childAccountId: instance.childAccountId,
        noticeType: 'deposit',
        sourceEntityId: depositId,
        amountMinorUnits: instance.rewardMinorUnits,
        currencyCode: state.guardian?.displayCurrencyCode ?? 'GBP',
        approvedAtUtc: instance.decidedAtUtc,
      });
    }
    return undefined;
  },
  routine_reject: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { instanceId: number; reason: string } }).input;
    const instance = demoRoutineInstance(input.instanceId);
    if (instance.status !== 'pending_guardian') {
      throw { kind: 'validation', message: 'That routine is not waiting for approval.' };
    }
    instance.status = 'rejected';
    instance.decidedAtUtc = new Date().toISOString();
    instance.rejectionReason = input.reason.trim();
    return undefined;
  },
  routine_update: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: Partial<RoutineEntry> & { recurringTaskId: number; subTasks?: string[] };
      }
    ).input;
    const routine = state.routines.find((entry) => entry.recurringTaskId === input.recurringTaskId);
    if (!routine) throw { kind: 'not_found', message: 'That routine was not found.' };
    const nextSubTasks = input.subTasks?.map((name, index) => ({
      subTaskId: routine.recurringTaskId * 100 + index + 1,
      sortOrder: index,
      subTaskName: name,
    }));
    Object.assign(routine, {
      ...(input.taskName !== undefined ? { taskName: input.taskName } : {}),
      ...(input.taskNotes !== undefined ? { taskNotes: input.taskNotes } : {}),
      ...(input.rewardMinorUnits !== undefined ? { rewardMinorUnits: input.rewardMinorUnits } : {}),
      ...(input.missBehaviour !== undefined ? { missBehaviour: input.missBehaviour } : {}),
      ...(input.penaltyBucketId !== undefined ? { penaltyBucketId: input.penaltyBucketId } : {}),
      ...(input.penaltyMinorUnits !== undefined
        ? { penaltyMinorUnits: input.penaltyMinorUnits }
        : {}),
      ...(input.scheduleDays !== undefined ? { scheduleDays: [...input.scheduleDays] } : {}),
      ...(input.repeatIntervalWeeks !== undefined
        ? { repeatIntervalWeeks: input.repeatIntervalWeeks }
        : {}),
      ...(input.visibleTime !== undefined ? { visibleTime: input.visibleTime } : {}),
      ...(input.deadlineTime !== undefined ? { deadlineTime: input.deadlineTime } : {}),
      ...(nextSubTasks ? { subTasks: nextSubTasks } : {}),
    });
    return undefined;
  },
  routine_pause: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { recurringTaskId: number; pauseUntilDate: string | null } })
      .input;
    const routine = state.routines.find((entry) => entry.recurringTaskId === input.recurringTaskId);
    if (!routine) throw { kind: 'not_found', message: 'That routine was not found.' };
    routine.isPaused = true;
    routine.pauseUntilDate = input.pauseUntilDate;
    return undefined;
  },
  routine_resume: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { recurringTaskId: number } }).input;
    const routine = state.routines.find((entry) => entry.recurringTaskId === input.recurringTaskId);
    if (!routine) throw { kind: 'not_found', message: 'That routine was not found.' };
    routine.isPaused = false;
    routine.pauseUntilDate = null;
    return undefined;
  },
  routine_delete: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { recurringTaskId: number } }).input;
    state.routines = state.routines.filter(
      (entry) => entry.recurringTaskId !== input.recurringTaskId,
    );
    state.routineInstances = state.routineInstances.filter(
      (entry) => entry.recurringTaskId !== input.recurringTaskId,
    );
    return undefined;
  },
  routine_history: async (payload) => {
    requirePublicDemoAccount();
    const input = (payload as { input: { recurringTaskId: number } }).input;
    return state.routineInstances.filter(
      (instance) => instance.recurringTaskId === input.recurringTaskId,
    );
  },
  routine_streaks: async (payload) => {
    requirePublicDemoAccount();
    const input = (payload as { input: { recurringTaskId: number } }).input;
    const approved = state.routineInstances.filter(
      (instance) =>
        instance.recurringTaskId === input.recurringTaskId && instance.status === 'approved',
    ).length;
    return {
      recurringTaskId: input.recurringTaskId,
      currentStreak: approved,
      bestStreak: approved,
    };
  },
  routine_badge_counts: async () => {
    const accountId = requirePublicDemoAccount();
    return {
      pendingApprovalCount:
        accountId === state.guardian?.accountId
          ? state.routineInstances.filter((instance) => instance.status === 'pending_guardian')
              .length
          : 0,
      pendingChildTodayCount: state.routineInstances.filter(
        (instance) =>
          instance.childAccountId === accountId &&
          instance.scheduledDate === publicDemoToday() &&
          instance.status === 'pending_child',
      ).length,
    };
  },
  family_challenge_current: async () => state.familyChallenge,
  family_challenge_start: async (payload) => {
    const input = (
      payload as {
        input: { challengeName: string; targetDaysPerChild: number; sessionId: string };
      }
    ).input;
    const starts = new Date();
    starts.setHours(12, 0, 0, 0);
    const ends = new Date(starts);
    ends.setDate(ends.getDate() + 6);
    const toLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    state.familyChallenge = {
      familyChallengeId: state.nextFamilyChallengeId++,
      challengeName: input.challengeName,
      targetDaysPerChild: input.targetDaysPerChild,
      startsOn: toLocalDate(starts),
      endsOn: toLocalDate(ends),
      children: state.children.map((child) => ({
        childAccountId: child.accountId,
        displayName: child.displayName,
        completedDays: 0,
      })),
    };
    return state.familyChallenge;
  },
  family_challenge_end: async (payload) => {
    const input = (payload as { input: { familyChallengeId: number; sessionId: string } }).input;
    if (
      !state.familyChallenge ||
      state.familyChallenge.familyChallengeId !== input.familyChallengeId
    ) {
      throw { kind: 'not_found', message: 'That family challenge is no longer active.' };
    }
    state.familyChallenge = null;
    return null;
  },
  allowance_create: async (payload) => {
    const createdByAccountId = requirePublicDemoGuardian();
    const input = (
      payload as {
        input: Omit<
          AllowanceSchedule,
          | 'scheduleId'
          | 'childDisplayName'
          | 'createdByAccountId'
          | 'isActive'
          | 'pausedUntilDate'
          | 'lastGeneratedDate'
        >;
      }
    ).input;
    const child = state.children.find((entry) => entry.accountId === input.childAccountId);
    if (!child) throw { kind: 'not_found', message: 'Choose a child in this demo family.' };
    const scheduleId = state.nextAllowanceScheduleId++;
    state.allowances.push({
      scheduleId,
      childAccountId: child.accountId,
      childDisplayName: child.displayName,
      createdByAccountId,
      frequency: input.frequency,
      amountMinorUnits: input.amountMinorUnits,
      dayOfWeek: input.dayOfWeek,
      dayOfMonth: input.dayOfMonth,
      startDate: input.startDate,
      isActive: true,
      pausedUntilDate: null,
      lastGeneratedDate: null,
      label: input.label || 'Pocket money',
    });
    return scheduleId;
  },
  allowance_list: async () => {
    const accountId = requirePublicDemoAccount();
    return accountId === state.guardian?.accountId
      ? state.allowances
      : state.allowances.filter((allowance) => allowance.childAccountId === accountId);
  },
  allowance_pause: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { scheduleId: number; pauseUntilDate: string | null } })
      .input;
    const allowance = state.allowances.find((entry) => entry.scheduleId === input.scheduleId);
    if (!allowance) throw { kind: 'not_found', message: 'That allowance was not found.' };
    allowance.pausedUntilDate = input.pauseUntilDate;
    return undefined;
  },
  allowance_cancel: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { scheduleId: number } }).input;
    const allowance = state.allowances.find((entry) => entry.scheduleId === input.scheduleId);
    if (!allowance) throw { kind: 'not_found', message: 'That allowance was not found.' };
    allowance.isActive = false;
    return undefined;
  },
  goal_create: async (payload) => {
    const childAccountId = requirePublicDemoAccount();
    if (childAccountId === state.guardian?.accountId) {
      throw { kind: 'unauthorized', message: 'Choose a child profile to create a goal.' };
    }
    const input = (
      payload as {
        input: {
          bucketId?: number | null;
          goalName: string;
          goalNotes?: string;
          targetMinorUnits: number;
          matchBps?: number | null;
          matchFixedMinorUnits?: number | null;
          goalEmoji?: string | null;
          goalType?: 'saving' | 'giving';
        };
      }
    ).input;
    const bucket = state.buckets.find(
      (entry) => entry.bucketId === input.bucketId && entry.childAccountId === childAccountId,
    );
    const goalId = state.nextGoalId++;
    const currentMinorUnits = bucket?.balanceMinorUnits ?? 0;
    state.goals.push({
      goalId,
      childAccountId,
      bucketId: bucket?.bucketId ?? null,
      bucketName: bucket?.bucketName ?? null,
      currentMinorUnits,
      goalName: input.goalName.trim(),
      goalNotes: input.goalNotes?.trim() || null,
      targetMinorUnits: input.targetMinorUnits,
      status: 'active',
      progressPct: Math.min(100, Math.round((currentMinorUnits / input.targetMinorUnits) * 100)),
      matchBps: input.matchBps ?? null,
      matchFixedMinorUnits: input.matchFixedMinorUnits ?? null,
      goalEmoji: input.goalEmoji ?? null,
      goalType: input.goalType ?? 'saving',
      createdAtUtc: new Date().toISOString(),
      completedAtUtc: null,
    });
    return goalId;
  },
  goals_list: async () => {
    const accountId = requirePublicDemoAccount();
    return state.goals
      .filter(
        (goal) => accountId === state.guardian?.accountId || goal.childAccountId === accountId,
      )
      .map((goal) => {
        const bucket = state.buckets.find((entry) => entry.bucketId === goal.bucketId);
        const currentMinorUnits = bucket?.balanceMinorUnits ?? goal.currentMinorUnits;
        return {
          ...goal,
          currentMinorUnits,
          progressPct: Math.min(100, Math.round((currentMinorUnits / goal.targetMinorUnits) * 100)),
        };
      });
  },
  goal_complete: async (payload) => {
    requirePublicDemoAccount();
    const input = (payload as { input: { goalId: number } }).input;
    const goal = demoGoal(input.goalId);
    goal.status = 'completed';
    goal.completedAtUtc = new Date().toISOString();
    return undefined;
  },
  goal_cancel: async (payload) => {
    requirePublicDemoAccount();
    const input = (payload as { input: { goalId: number } }).input;
    demoGoal(input.goalId).status = 'cancelled';
    return undefined;
  },
  goal_add_match: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { goalId: number; amountMinorUnits: number } }).input;
    const goal = demoGoal(input.goalId);
    if (input.amountMinorUnits <= 0) {
      throw { kind: 'validation', message: 'Enter a positive match amount.' };
    }
    const bucket = state.buckets.find((entry) => entry.bucketId === goal.bucketId);
    if (bucket) bucket.balanceMinorUnits += input.amountMinorUnits;
    return { contributionId: Date.now(), depositId: null };
  },
  chore_update: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          choreId: number;
          choreName: string;
          choreDescription: string | null;
          zoneName: string | null;
          rewardMinorUnits: number;
        };
      }
    ).input;
    const chore = state.chores.get(input.choreId);
    if (!chore) throw { kind: 'not_found', message: 'That chore was not found.' };
    const name = input.choreName.trim();
    if (!name) throw { kind: 'validation', message: 'Chore name is required.' };
    chore.choreName = name;
    chore.choreDescription = input.choreDescription?.trim() || null;
    chore.zoneName = input.zoneName?.trim() || null;
    chore.rewardMinorUnits = input.rewardMinorUnits;
    return demoChoreEntry(chore.choreId);
  },
  chore_schedule_create: async (payload) => {
    const createdByAccountId = requirePublicDemoGuardian();
    const input = (
      payload as {
        input: Omit<
          ChoreSchedule,
          | 'scheduleId'
          | 'createdByAccountId'
          | 'childDisplayName'
          | 'isActive'
          | 'pausedUntilDate'
          | 'lastGeneratedDate'
          | 'currentStreak'
          | 'bestStreak'
        >;
      }
    ).input;
    const child =
      input.childAccountId === null || input.childAccountId === undefined
        ? null
        : demoChild(input.childAccountId);
    const scheduleId = state.nextChoreScheduleId++;
    state.choreSchedules.push({
      scheduleId,
      createdByAccountId,
      childAccountId: child?.accountId ?? null,
      childDisplayName: child?.displayName ?? null,
      choreName: input.choreName.trim(),
      choreDescription: input.choreDescription?.trim() || null,
      zoneName: input.zoneName?.trim() || null,
      rewardMinorUnits: input.rewardMinorUnits,
      frequency: input.frequency,
      dayOfWeek: input.dayOfWeek ?? null,
      dayOfMonth: input.dayOfMonth ?? null,
      startDate: input.startDate,
      isActive: true,
      pausedUntilDate: null,
      lastGeneratedDate: null,
      currentStreak: 0,
      bestStreak: 0,
    });
    return scheduleId;
  },
  chore_schedules_list: async () => {
    requirePublicDemoGuardian();
    return state.choreSchedules;
  },
  chore_schedule_pause: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { scheduleId: number; pauseUntilDate: string | null } })
      .input;
    const schedule = state.choreSchedules.find((entry) => entry.scheduleId === input.scheduleId);
    if (!schedule) throw { kind: 'not_found', message: 'That chore schedule was not found.' };
    schedule.pausedUntilDate = input.pauseUntilDate;
    return undefined;
  },
  chore_schedule_cancel: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { scheduleId: number } }).input;
    const schedule = state.choreSchedules.find((entry) => entry.scheduleId === input.scheduleId);
    if (!schedule) throw { kind: 'not_found', message: 'That chore schedule was not found.' };
    schedule.isActive = false;
    return undefined;
  },
  chore_schedules_generate: async () => {
    const guardianId = requirePublicDemoGuardian();
    const today = publicDemoToday();
    let created = 0;
    for (const schedule of state.choreSchedules) {
      if (!schedule.isActive || schedule.pausedUntilDate) continue;
      if (schedule.lastGeneratedDate === today) continue;
      const childIds =
        schedule.childAccountId === null
          ? state.children.map((child) => child.accountId)
          : [schedule.childAccountId];
      for (const childAccountId of childIds) {
        const choreId = state.nextChoreId++;
        state.chores.set(choreId, {
          choreId,
          childAccountId,
          createdByAccountId: guardianId,
          choreName: schedule.choreName,
          choreDescription: schedule.choreDescription,
          choreTypeCode: 'recurring',
          choreTypeLabel: 'Recurring',
          zoneName: schedule.zoneName,
          rewardMinorUnits: schedule.rewardMinorUnits,
          recurrencePattern: schedule.frequency,
          isActive: true,
          requiresApprovalToClaim: false,
          createdAtUtc: new Date().toISOString(),
        });
        created += 1;
      }
      schedule.lastGeneratedDate = today;
    }
    return { created };
  },
  template_create: async (payload) => {
    const createdByAccountId = requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          templateName: string;
          buckets: {
            bucketName: string;
            allocationBps: number;
            percentIsFixed: boolean;
            requiresApprovalToWithdraw: boolean;
            isGuardianManaged: boolean;
            displayColor: string | null;
          }[];
        };
      }
    ).input;
    const templateId = state.nextBucketTemplateId++;
    state.legacyBucketTemplates.push({
      templateId,
      createdByAccountId,
      templateName: input.templateName.trim(),
      configJson: JSON.stringify(input.buckets),
      createdAtUtc: new Date().toISOString(),
    });
    return templateId;
  },
  templates_list: async () => {
    requirePublicDemoGuardian();
    return state.legacyBucketTemplates;
  },
  template_delete: async (payload) => {
    requirePublicDemoGuardian();
    const templateId = (payload as { input: { templateId: number } }).input.templateId;
    state.legacyBucketTemplates = state.legacyBucketTemplates.filter(
      (entry) => entry.templateId !== templateId,
    );
    return undefined;
  },
  template_apply: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { templateId: number; childAccountId: number } }).input;
    const template = state.legacyBucketTemplates.find(
      (entry) => entry.templateId === input.templateId,
    );
    if (!template) throw { kind: 'not_found', message: 'That bucket template was not found.' };
    const items = JSON.parse(template.configJson) as {
      bucketName: string;
      allocationBps: number;
      percentIsFixed: boolean;
      requiresApprovalToWithdraw: boolean;
      isGuardianManaged: boolean;
      displayColor: string | null;
    }[];
    return demoAddTemplateBuckets(input.childAccountId, items);
  },
  bucket_template_create: async (payload) => {
    const createdByAccountId = requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          templateName: string;
          templateDescription?: string;
          items: DemoBucketTemplateItem[];
        };
      }
    ).input;
    const templateId = state.nextBucketTemplateId++;
    state.bucketTemplates.push({
      templateId,
      createdByAccountId,
      templateName: input.templateName.trim(),
      templateDescription: input.templateDescription?.trim() || null,
      items: input.items.map((item, index) => ({ ...item, itemId: index + 1 })),
      createdAtUtc: new Date().toISOString(),
    });
    return templateId;
  },
  bucket_templates_list: async () => {
    requirePublicDemoGuardian();
    return state.bucketTemplates;
  },
  bucket_template_save_from_child: async (payload) => {
    const createdByAccountId = requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          childAccountId: number;
          templateName: string;
          templateDescription?: string;
        };
      }
    ).input;
    demoChild(input.childAccountId);
    const items = state.buckets
      .filter((bucket) => bucket.childAccountId === input.childAccountId && bucket.isActive)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((bucket, index) => ({
        itemId: index + 1,
        bucketName: bucket.bucketName,
        displayColor: bucket.displayColor,
        allocationBps: bucket.allocationBps,
        isGuardianManaged: bucket.isGuardianManaged,
        requiresApproval: bucket.requiresApprovalToWithdraw,
        percentIsFixed: bucket.percentIsFixed,
        sortOrder: index,
      }));
    const templateId = state.nextBucketTemplateId++;
    state.bucketTemplates.push({
      templateId,
      createdByAccountId,
      templateName: input.templateName.trim(),
      templateDescription: input.templateDescription?.trim() || null,
      items,
      createdAtUtc: new Date().toISOString(),
    });
    return templateId;
  },
  bucket_template_apply: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { templateId: number; childAccountId: number } }).input;
    const template = state.bucketTemplates.find((entry) => entry.templateId === input.templateId);
    if (!template) throw { kind: 'not_found', message: 'That bucket template was not found.' };
    const bucketsAdded = demoAddTemplateBuckets(
      input.childAccountId,
      template.items.map((item) => ({
        ...item,
        requiresApprovalToWithdraw: item.requiresApproval,
      })),
    );
    return { bucketsAdded };
  },
  bucket_template_delete: async (payload) => {
    requirePublicDemoGuardian();
    const templateId = (payload as { input: { templateId: number } }).input.templateId;
    state.bucketTemplates = state.bucketTemplates.filter(
      (entry) => entry.templateId !== templateId,
    );
    return undefined;
  },
  bucket_balance_adjust: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { bucketId: number; newBalanceMinorUnits: number } }).input;
    const bucket = state.buckets.find((entry) => entry.bucketId === input.bucketId);
    if (!bucket) throw { kind: 'not_found', message: 'That bucket was not found.' };
    const oldBalanceMinorUnits = bucket.balanceMinorUnits;
    bucket.balanceMinorUnits = input.newBalanceMinorUnits;
    return {
      adjustmentId: Date.now(),
      bucketId: bucket.bucketId,
      oldBalanceMinorUnits,
      newBalanceMinorUnits: bucket.balanceMinorUnits,
      deltaMinorUnits: bucket.balanceMinorUnits - oldBalanceMinorUnits,
    };
  },
  bucket_auto_rebalance: async (payload) => {
    requirePublicDemoGuardian();
    const input = payload as { childAccountId: number };
    const buckets = state.buckets
      .filter((bucket) => bucket.childAccountId === input.childAccountId && bucket.isActive)
      .sort((left, right) => left.sortOrder - right.sortOrder);
    if (buckets.length === 0) throw { kind: 'not_found', message: 'No buckets were found.' };
    const base = Math.floor(10_000 / buckets.length);
    let assigned = 0;
    return buckets.map((bucket, index) => {
      bucket.allocationBps = index === buckets.length - 1 ? 10_000 - assigned : base;
      assigned += bucket.allocationBps;
      return { bucketId: bucket.bucketId, newAllocationBps: bucket.allocationBps };
    });
  },
  bucket_update_priorities: async (payload) => {
    requirePublicDemoGuardian();
    const input = payload as {
      childAccountId: number;
      priorities: { bucketId: number; priority: number }[];
    };
    for (const priority of input.priorities) {
      const bucket = state.buckets.find(
        (entry) =>
          entry.bucketId === priority.bucketId && entry.childAccountId === input.childAccountId,
      );
      if (bucket) bucket.sortOrder = priority.priority;
    }
    return undefined;
  },
  bucket_update_splits: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          childAccountId: number;
          splits: { bucketId: number; allocationBps: number }[];
        };
      }
    ).input;
    if (input.splits.reduce((sum, split) => sum + split.allocationBps, 0) !== 10_000) {
      throw { kind: 'validation', message: 'Bucket percentages must add up to 100%.' };
    }
    for (const split of input.splits) {
      const bucket = state.buckets.find(
        (entry) =>
          entry.bucketId === split.bucketId && entry.childAccountId === input.childAccountId,
      );
      if (!bucket) throw { kind: 'not_found', message: 'One of those buckets was not found.' };
      bucket.allocationBps = split.allocationBps;
    }
    return undefined;
  },
  family_currency_update: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { newCurrencyCode: 'GBP' | 'USD' } }).input;
    if (!state.guardian) throw { kind: 'not_found', message: 'Demo family not found.' };
    const previousCurrencyCode = state.guardian.displayCurrencyCode as 'GBP' | 'USD';
    state.guardian.displayCurrencyCode = input.newCurrencyCode;
    for (const child of state.children) child.displayCurrencyCode = input.newCurrencyCode;
    for (const guardian of state.additionalGuardians) {
      guardian.displayCurrencyCode = input.newCurrencyCode;
    }
    return {
      previousCurrencyCode,
      newCurrencyCode: input.newCurrencyCode,
      accountCount: 1 + state.children.length + state.additionalGuardians.length,
    };
  },
  family_content_policy_accept: async () => {
    requirePublicDemoAccount();
    return undefined;
  },
  family_content_reporter_context: async () => {
    const accountId = requirePublicDemoAccount();
    if (state.guardian?.accountId === accountId) {
      return { accountId, roleCode: 'guardian', displayName: state.guardian.displayName };
    }
    const child = demoChild(accountId);
    return { accountId, roleCode: 'child', displayName: child.displayName };
  },
  family_data_delete_local: async () => {
    requirePublicDemoGuardian();
    resetDevTauriMock();
    return { deleted: true };
  },
  child_recovery_list: async () => {
    requirePublicDemoGuardian();
    return state.recoverableChildren.map((child) => ({
      childAccountId: child.accountId,
      displayName: child.displayName,
      deletedAtUtc: child.deletedAtUtc,
      purgeAfterUtc: child.purgeAfterUtc,
    }));
  },
  child_restore: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: { childAccountId: number; displayName: string; newPassword: string };
      }
    ).input;
    const index = state.recoverableChildren.findIndex(
      (entry) => entry.accountId === input.childAccountId,
    );
    if (index < 0) throw { kind: 'not_found', message: 'That child is no longer recoverable.' };
    const restored = state.recoverableChildren.splice(index, 1)[0]!;
    restored.displayName = input.displayName.trim();
    restored.password = input.newPassword;
    state.children.push(restored);
    state.buckets.push(...restored.buckets);
    state.guardianTypeByChildId.set(restored.accountId, restored.guardianTypeCode);
    return { childAccountId: restored.accountId, displayName: restored.displayName };
  },
  login_security_alerts_list: async () => state.loginSecurityAlerts,
  login_security_alert_ignore: async (payload) => {
    requirePublicDemoGuardian();
    const alertId = (payload as { input: { loginSecurityAlertId: number } }).input
      .loginSecurityAlertId;
    state.loginSecurityAlerts = state.loginSecurityAlerts.filter(
      (entry) => entry.loginSecurityAlertId !== alertId,
    );
    return undefined;
  },
  guardian_challenge: async (payload) => {
    const input = (
      payload as {
        input: {
          accountId: number;
          credential: { kind: 'password'; password: string } | { kind: 'pin'; pin: string };
        };
      }
    ).input;
    if (!state.guardian || input.accountId !== state.guardian.accountId) {
      return { outcome: 'notGuardian' };
    }
    const valid =
      input.credential.kind === 'password'
        ? input.credential.password === state.guardian.password
        : input.credential.pin === state.pinsByAccount.get(state.guardian.accountId);
    if (!valid) return { outcome: 'failed', attemptsRemaining: 2 };
    const settingsToken = `demo-settings-${crypto.randomUUID()}`;
    state.validSettingsTokens.add(settingsToken);
    return { outcome: 'success', settingsToken };
  },
  family_account_password_reset: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: { settingsToken: string; loginSecurityAlertId: number; newPassword: string };
      }
    ).input;
    if (!state.validSettingsTokens.delete(input.settingsToken)) {
      throw { kind: 'unauthorized', message: 'Confirm the guardian account again.' };
    }
    const alert = state.loginSecurityAlerts.find(
      (entry) => entry.loginSecurityAlertId === input.loginSecurityAlertId,
    );
    if (!alert) throw { kind: 'not_found', message: 'That sign-in warning is no longer active.' };
    if (state.guardian?.accountId === alert.targetAccountId) {
      state.guardian.password = input.newPassword;
    } else {
      const child = demoChild(alert.targetAccountId);
      child.password = input.newPassword;
    }
    state.failureTimestampsMs.delete(alert.targetAccountId);
    state.lockoutExpiresAtMs.delete(alert.targetAccountId);
    state.pinsByAccount.delete(alert.targetAccountId);
    state.pinFailuresByAccount.delete(alert.targetAccountId);
    state.pinLockoutsByAccount.delete(alert.targetAccountId);
    state.loginSecurityAlerts = state.loginSecurityAlerts.filter(
      (entry) => entry.loginSecurityAlertId !== input.loginSecurityAlertId,
    );
    return { accountId: alert.targetAccountId };
  },
  gift_create: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: { childAccountId: number; amountMinorUnits: number; note?: string | null };
      }
    ).input;
    const child = demoChild(input.childAccountId);
    if (input.amountMinorUnits <= 0) {
      throw { kind: 'validation', message: 'Enter a positive gift amount.' };
    }
    const depositId = state.nextDepositId++;
    state.pendingDeposits.push({
      depositId,
      childAccountId: child.accountId,
      childDisplayName: child.displayName,
      amountMinorUnits: input.amountMinorUnits,
      sourceCode: 'gift',
      sourceLabel: 'Gift',
      note: input.note?.trim() || null,
      requestedAtUtc: new Date().toISOString(),
    });
    return { depositId, autoAllocated: false };
  },
  gift_pending_list_for_guardian: async () => {
    requirePublicDemoGuardian();
    return state.pendingDeposits
      .filter((entry) => entry.sourceCode === 'gift')
      .map((entry) => ({ ...entry, createdAtUtc: entry.requestedAtUtc }));
  },
  gift_my_pending_list: async () => {
    const accountId = requirePublicDemoAccount();
    return state.pendingDeposits
      .filter((entry) => entry.sourceCode === 'gift' && entry.childAccountId === accountId)
      .map((entry) => ({ ...entry, createdAtUtc: entry.requestedAtUtc }));
  },
  gift_debug_deposits: async () => {
    const accountId = requirePublicDemoAccount();
    return state.pendingDeposits
      .filter((entry) => entry.childAccountId === accountId && entry.sourceCode === 'gift')
      .map((entry) => ({
        depositId: entry.depositId,
        amountMinorUnits: entry.amountMinorUnits,
        statusCode: 'pending',
        sourceCode: entry.sourceCode,
        childAccountId: entry.childAccountId,
        actorAccountId: state.guardian?.accountId ?? 1,
        requestedAtUtc: entry.requestedAtUtc,
      }));
  },
  gift_allocate: async (payload) => {
    requirePublicDemoGuardian();
    const input = (
      payload as {
        input: {
          depositId: number;
          allocations: { bucketId: number; amountMinorUnits: number }[];
        };
      }
    ).input;
    const index = state.pendingDeposits.findIndex((entry) => entry.depositId === input.depositId);
    if (index < 0) throw { kind: 'not_found', message: 'That pending gift was not found.' };
    const gift = state.pendingDeposits[index]!;
    if (
      input.allocations.reduce((sum, entry) => sum + entry.amountMinorUnits, 0) !==
      gift.amountMinorUnits
    ) {
      throw { kind: 'validation', message: 'Gift allocations must equal the full gift amount.' };
    }
    for (const allocation of input.allocations) {
      const bucket = state.buckets.find(
        (entry) =>
          entry.bucketId === allocation.bucketId && entry.childAccountId === gift.childAccountId,
      );
      if (!bucket) throw { kind: 'not_found', message: 'One of those buckets was not found.' };
      bucket.balanceMinorUnits += allocation.amountMinorUnits;
    }
    state.pendingDeposits.splice(index, 1);
    return undefined;
  },
  gift_cancel: async (payload) => {
    requirePublicDemoGuardian();
    const depositId = (payload as { input: { depositId: number } }).input.depositId;
    state.pendingDeposits = state.pendingDeposits.filter((entry) => entry.depositId !== depositId);
    return undefined;
  },
  report_monthly_statement: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { childAccountId: number; year: number; month: number } })
      .input;
    const child = demoChild(input.childAccountId);
    const buckets = state.buckets.filter((entry) => entry.childAccountId === child.accountId);
    const entries = state.ledger.filter((entry) => {
      if (entry.childAccountId !== child.accountId) return false;
      const date = new Date(entry.atUtc);
      return date.getFullYear() === input.year && date.getMonth() + 1 === input.month;
    });
    const totalDeposited = entries
      .filter((entry) => entry.kind === 'deposit')
      .reduce((sum, entry) => sum + entry.amountMinorUnits, 0);
    const totalWithdrawn = entries
      .filter((entry) => entry.kind === 'withdrawal')
      .reduce((sum, entry) => sum + entry.amountMinorUnits, 0);
    const totalTransferred = entries
      .filter((entry) => entry.kind === 'transfer')
      .reduce((sum, entry) => sum + entry.amountMinorUnits, 0);
    const closingBalanceMinorUnits = buckets.reduce(
      (sum, bucket) => sum + bucket.balanceMinorUnits,
      0,
    );
    const periodLabel = new Intl.DateTimeFormat('en-GB', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(input.year, input.month - 1, 1));
    return {
      childAccountId: child.accountId,
      childDisplayName: child.displayName,
      currency: child.displayCurrencyCode,
      periodLabel,
      openingBalanceMinorUnits: closingBalanceMinorUnits - totalDeposited + totalWithdrawn,
      closingBalanceMinorUnits,
      totalDeposited,
      totalWithdrawn,
      totalTransferred,
      depositCount: entries.filter((entry) => entry.kind === 'deposit').length,
      withdrawalCount: entries.filter((entry) => entry.kind === 'withdrawal').length,
      buckets: buckets.map((bucket) => ({
        bucketId: bucket.bucketId,
        bucketName: bucket.bucketName,
        openingMinorUnits: bucket.balanceMinorUnits,
        closingMinorUnits: bucket.balanceMinorUnits,
        totalDeposited: 0,
        totalWithdrawn: 0,
      })),
    };
  },
  report_chore_trends: async (payload) => {
    requirePublicDemoGuardian();
    const input = (payload as { input: { childAccountId?: number } }).input;
    const completions = Array.from(state.choreCompletions.values()).filter(
      (entry) =>
        input.childAccountId === undefined || entry.childAccountId === input.childAccountId,
    );
    const completed = completions.filter((entry) => entry.status === 'approved').length;
    const rejected = completions.filter((entry) => entry.status === 'rejected').length;
    const pending = completions.filter((entry) => entry.status === 'claimed').length;
    const totalDecided = completed + rejected;
    return {
      weeks: [
        {
          weekStart: publicDemoToday(),
          completed,
          rejected,
          missed: 0,
          pending,
        },
      ],
      totalCompleted: completed,
      totalRejected: rejected,
      totalMissed: 0,
      completionRatePct: totalDecided === 0 ? 0 : Math.round((completed / totalDecided) * 100),
    };
  },
  network_get_status: async () => ({ role: 'standalone', unresolvedConflicts: 0 }),
  guardian_all_chores_count: async () => state.chores.size,
  startup_error_get: async () => null,
  get_startup_error: async () => null,
};

/** Materialize terminal child transfer requests into the unified history
 * shape. Guardian-direct transfers are stored in `ledger` at the time they
 * occur because they have no pending lifecycle. */
function terminalTransferHistory(childAccountId: number): LedgerEntry[] {
  return Array.from(state.bucketTransfers.values())
    .filter(
      (transfer) => transfer.childAccountId === childAccountId && transfer.status !== 'pending',
    )
    .map((transfer) => {
      const source = state.buckets.find((bucket) => bucket.bucketId === transfer.sourceBucketId);
      const target =
        transfer.targetBucketId === null
          ? null
          : (state.buckets.find((bucket) => bucket.bucketId === transfer.targetBucketId) ?? null);
      const decider =
        transfer.decidedByAccountId === state.guardian?.accountId
          ? state.guardian.displayName
          : (state.children.find((child) => child.accountId === transfer.decidedByAccountId)
              ?.displayName ?? null);
      // The filter above excludes pending requests, but TypeScript does not
      // narrow a mutable object property through Array.filter. Materialise the
      // terminal union explicitly so this mock cannot leak pending transfers
      // into the history contract.
      const statusCode =
        transfer.status === 'approved'
          ? 'approved'
          : transfer.status === 'rejected'
            ? 'rejected'
            : 'cancelled';
      return {
        kind: 'transfer' as const,
        transferId: transfer.bucketTransferId,
        transferMode: 'child_request' as const,
        childAccountId: transfer.childAccountId,
        atUtc: transfer.decidedAtUtc ?? transfer.requestedAtUtc,
        amountMinorUnits: transfer.amountMinorUnits,
        sourceBucketId: transfer.sourceBucketId,
        sourceBucketName: source?.bucketName ?? '',
        targetBucketId: transfer.targetBucketId,
        targetBucketName: target?.bucketName ?? null,
        note: transfer.note,
        statusCode,
        statusLabel: statusCode.slice(0, 1).toUpperCase() + statusCode.slice(1),
        decisionReason: transfer.decisionReason,
        decidedByDisplayName: decider,
      };
    });
}

const handlers: Record<string, AnyHandler> = {
  accounts_exist: async () => state.accountsExist,
  child_approval_notices_list: async (payload) => {
    const input = (payload as { input?: { sessionId?: string } })?.input;
    if (!input?.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const child = state.children.find((entry) => entry.accountId === state.lastAuthAccountId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'approval notices are available only to child accounts',
      };
    }
    return state.childApprovalNotices
      .filter((notice) => notice.childAccountId === child.accountId)
      .map(({ childAccountId, ...notice }) => notice);
  },

  // US-DEV-TESTDATA — dev mock always returns false; the test-data
  // build is a Rust feature flag and isn't exercised in the
  // browser-side dev preview environment.
  is_test_data_build: async () => false,

  guardian_create: async (payload) => {
    const p = payload as {
      input?: { displayName?: string; password?: string; displayCurrencyCode?: string };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    const displayName = (input.displayName ?? '').trim();
    const password = input.password ?? '';
    const currency = input.displayCurrencyCode ?? '';

    if (displayName.length === 0) {
      throw { kind: 'validation', message: 'Display name is required.' };
    }
    if (password.length < 10) {
      throw {
        kind: 'validation',
        message: `Password must be at least 10 characters (was ${password.length}).`,
      };
    }
    if (!['GBP', 'USD', 'EUR', 'CAD', 'AUD'].includes(currency)) {
      throw { kind: 'validation', message: `Unknown currency code '${currency}'.` };
    }
    if (state.accountsExist) {
      throw { kind: 'conflict', message: 'A guardian account already exists on this device.' };
    }

    state.accountsExist = true;
    state.guardian = {
      accountId: 1,
      displayName,
      displayCurrencyCode: currency,
      password,
    };
    safeLog('info', '[dev-mock] guardian_create OK', state.guardian);
    return { accountId: 1, displayName };
  },

  accounts_list: async () => {
    if (!state.guardian) return [];
    const list = [
      {
        accountId: state.guardian.accountId,
        displayName: state.guardian.displayName,
        roleCode: 'guardian',
        displayCurrencyCode: state.guardian.displayCurrencyCode,
        displayAvatar: null,
      },
      ...state.children
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((c) => ({
          accountId: c.accountId,
          displayName: c.displayName,
          roleCode: 'child',
          displayCurrencyCode: c.displayCurrencyCode,
          displayAvatar: null,
        })),
    ];
    return list;
  },

  child_create: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        displayName?: string;
        password?: string;
        displayCurrencyCode?: string;
        guardianTypeCode?: string;
      };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const displayName = (input.displayName ?? '').trim();
    const password = input.password ?? '';
    const currency = input.displayCurrencyCode ?? '';
    if (displayName.length === 0) {
      throw { kind: 'validation', message: 'Child display name is required.' };
    }
    if (password.length < 10) {
      throw {
        kind: 'validation',
        message: `Password must be at least 10 characters (was ${password.length}).`,
      };
    }
    if (!['GBP', 'USD', 'EUR', 'CAD', 'AUD'].includes(currency)) {
      throw { kind: 'validation', message: `Unknown currency code '${currency}'.` };
    }
    if (
      state.guardian?.displayName === displayName ||
      state.children.some((c) => c.displayName === displayName)
    ) {
      throw {
        kind: 'conflict',
        message: `An account named '${displayName}' already exists.`,
      };
    }

    const accountId = state.nextAccountId++;
    state.children.push({
      accountId,
      displayName,
      displayCurrencyCode: currency,
      password,
      guardianTypeCode: input.guardianTypeCode ?? 'parent',
    });
    // US-BUCKET-01: every new child gets the three default buckets.
    for (const tmpl of DEFAULT_BUCKETS) {
      state.buckets.push({
        bucketId: state.nextBucketId++,
        childAccountId: accountId,
        ...tmpl,
      });
    }
    safeLog('info', '[dev-mock] child_create OK', {
      accountId,
      displayName,
      bucketsCreated: DEFAULT_BUCKETS.length,
    });
    // US-SETUP-03: stamp the initial guardian relationship as 'parent'.
    // The guardian can later change this via child_guardian_type_update.
    state.guardianTypeByChildId.set(accountId, 'parent');
    return { accountId, displayName };
  },

  child_password_reset: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        childAccountId?: number;
        newPassword?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    // Caller must be the guardian.
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: "only guardians can reset a child's password" };
    }
    const child = state.children.find((c) => c.accountId === input.childAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
    }
    const newPassword = input.newPassword ?? '';
    if (newPassword.length < 10) {
      throw {
        kind: 'validation',
        message: `Password must be at least 10 characters (was ${newPassword.length}).`,
      };
    }
    child.password = newPassword;
    // Clear lockout state in case the child was locked out before reset.
    state.failureTimestampsMs.delete(child.accountId);
    state.lockoutExpiresAtMs.delete(child.accountId);
    safeLog('info', '[dev-mock] child_password_reset OK', { childAccountId: child.accountId });
    return { childAccountId: child.accountId };
  },

  deposit_create: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        childAccountId?: number;
        amountMinorUnits?: number;
        sourceCode?: string;
        note?: string;
        fx?: { currencyCode?: string; ratePpm?: number } | null;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can record deposits in US-DEPOSIT-01',
      };
    }
    const child = state.children.find((c) => c.accountId === input.childAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
    }
    const amount = input.amountMinorUnits ?? 0;
    if (amount <= 0) {
      throw { kind: 'validation', message: 'deposit amount must be positive' };
    }
    const validSources = ['chore', 'allowance', 'gift', 'found', 'other'];
    if (!input.sourceCode || !validSources.includes(input.sourceCode)) {
      throw { kind: 'validation', message: `unknown deposit source '${input.sourceCode}'` };
    }

    // US-FX-01 — when fx is supplied, validate the rate and source
    // currency against the child's display currency, then compute the
    // display amount via the same arithmetic the backend uses.
    const validCodes = new Set([
      'GBP',
      'USD',
      'EUR',
      'CAD',
      'AUD',
      'JPY',
      'CHF',
      'SEK',
      'NOK',
      'DKK',
    ]);
    let displayAmount = amount;
    if (input.fx) {
      const code = (input.fx.currencyCode ?? '').toUpperCase();
      const rate = input.fx.ratePpm ?? 0;
      if (!validCodes.has(code)) {
        throw {
          kind: 'validation',
          message: `currency code '${input.fx.currencyCode}' is not recognised`,
        };
      }
      if (code === child.displayCurrencyCode) {
        throw {
          kind: 'validation',
          message: `source currency '${code}' matches the child's display currency`,
        };
      }
      if (rate <= 0) {
        throw { kind: 'validation', message: 'exchange rate must be positive' };
      }
      // Half-up rounding to match backend.
      const raw = amount * rate;
      displayAmount = Math.floor((raw + 500_000) / 1_000_000);
      if (displayAmount <= 0) {
        throw {
          kind: 'validation',
          message: 'computed display amount is zero — increase the source amount or check the rate',
        };
      }
    }

    const childBuckets = state.buckets
      .filter((b) => b.childAccountId === child.accountId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (childBuckets.length === 0) {
      throw { kind: 'validation', message: 'this child has no active buckets to allocate into' };
    }

    // Allocate against the DISPLAY amount — buckets are stored in the
    // child's display currency. The source amount is preserved on the
    // ledger entry as the "what was actually handed over" record.
    const splits = allocateDeposit(
      displayAmount,
      childBuckets.map((b) => ({
        bucketId: b.bucketId,
        allocationBps: b.allocationBps,
        sortOrder: b.sortOrder,
      })),
    );

    // Credit bucket balances in-memory.
    for (const split of splits) {
      const bucket = childBuckets.find((b) => b.bucketId === split.bucketId);
      if (bucket) bucket.balanceMinorUnits += split.amountMinorUnits;
    }

    const depositId = state.nextDepositId++;
    const sourceLabels: Record<string, string> = {
      chore: 'Chore reward',
      allowance: 'Allowance',
      gift: 'Gift',
      found: 'Found money',
      other: 'Other',
    };
    const allocations = splits.map((s) => ({
      bucketId: s.bucketId,
      bucketName: childBuckets.find((b) => b.bucketId === s.bucketId)?.bucketName ?? '',
      amountMinorUnits: s.amountMinorUnits,
    }));
    state.ledger.push({
      kind: 'deposit',
      depositId,
      childAccountId: child.accountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: displayAmount,
      sourceCode: input.sourceCode ?? 'other',
      sourceLabel: sourceLabels[input.sourceCode ?? 'other'] ?? 'Other',
      note: input.note ?? null,
      allocations,
    });
    safeLog('info', '[dev-mock] deposit_create OK', {
      depositId,
      childAccountId: child.accountId,
      amountMinorUnits: amount,
      splits: splits.length,
    });
    return {
      depositId,
      amountMinorUnits: amount,
      allocations,
    };
  },

  deposit_submit_pending: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        amountMinorUnits?: number;
        sourceCode?: string;
        note?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (state.lastAuthAccountId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }
    // Child-only.
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only children submit pending deposits; guardians can land a deposit directly',
      };
    }
    const child = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'session does not match a child account' };
    }
    const amount = input.amountMinorUnits ?? 0;
    if (amount <= 0) {
      throw { kind: 'validation', message: 'deposit amount must be positive' };
    }
    const validSources: Record<string, string> = {
      chore: 'Chore reward',
      allowance: 'Allowance',
      gift: 'Gift',
      found: 'Found money',
      other: 'Other',
    };
    const sourceCode = input.sourceCode ?? '';
    if (!validSources[sourceCode]) {
      throw { kind: 'validation', message: `unknown deposit source '${sourceCode}'` };
    }

    const depositId = state.nextDepositId++;
    const now = new Date().toISOString();
    state.pendingDeposits.push({
      depositId,
      childAccountId: child.accountId,
      childDisplayName: child.displayName,
      amountMinorUnits: amount,
      sourceCode,
      sourceLabel: validSources[sourceCode],
      note: input.note ?? null,
      requestedAtUtc: now,
    });
    safeLog('info', '[dev-mock] deposit_submit_pending OK', {
      depositId,
      amount,
      sourceCode,
    });
    return { depositId, amountMinorUnits: amount };
  },

  pending_deposits_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians see pending-approval queues',
      };
    }
    // Only pending deposits for linked children.
    const linkedChildIds = new Set(state.children.map((c) => c.accountId));
    return state.pendingDeposits
      .filter((d) => linkedChildIds.has(d.childAccountId))
      .slice()
      .sort((a, b) => a.requestedAtUtc.localeCompare(b.requestedAtUtc));
  },

  deposit_approve: async (payload) => {
    const p = payload as { input?: { sessionId?: string; depositId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending deposits',
      };
    }
    const idx = state.pendingDeposits.findIndex((d) => d.depositId === input.depositId);
    if (idx === -1) {
      // Either doesn't exist, or was already decided (which we represent
      // as "not in the pending list"). Backend distinguishes NotFound from
      // Conflict; this mock can't, so treat unknown id as Conflict since
      // the most-likely cause of UI-level "I got an error" is a double-click.
      throw {
        kind: 'conflict',
        message: 'this deposit has already been decided',
      };
    }
    const pending = state.pendingDeposits[idx]!;
    const child = state.children.find((c) => c.accountId === pending.childAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
    }

    // Allocate using the child's current active buckets.
    const childBuckets = state.buckets
      .filter((b) => b.childAccountId === pending.childAccountId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (childBuckets.length === 0) {
      throw {
        kind: 'validation',
        message: 'this child has no active buckets to allocate into',
      };
    }
    const splits = allocateDeposit(
      pending.amountMinorUnits,
      childBuckets.map((b) => ({
        bucketId: b.bucketId,
        allocationBps: b.allocationBps,
        sortOrder: b.sortOrder,
      })),
    );
    for (const split of splits) {
      const bucket = childBuckets.find((b) => b.bucketId === split.bucketId);
      if (bucket) bucket.balanceMinorUnits += split.amountMinorUnits;
    }

    // Promote from pending to approved in the ledger.
    const allocations = splits.map((s) => ({
      bucketId: s.bucketId,
      bucketName: childBuckets.find((b) => b.bucketId === s.bucketId)?.bucketName ?? '',
      amountMinorUnits: s.amountMinorUnits,
    }));
    const approvedAtUtc = new Date().toISOString();
    state.ledger.push({
      kind: 'deposit',
      depositId: pending.depositId,
      childAccountId: pending.childAccountId,
      atUtc: approvedAtUtc,
      amountMinorUnits: pending.amountMinorUnits,
      sourceCode: pending.sourceCode,
      sourceLabel: pending.sourceLabel,
      note: pending.note,
      allocations,
      statusCode: 'approved',
      statusLabel: 'Approved',
      decisionReason: null,
      decidedByDisplayName: state.guardian.displayName,
    });
    state.childApprovalNotices.push({
      childApprovalNoticeId: state.nextChildApprovalNoticeId++,
      childAccountId: pending.childAccountId,
      noticeType: 'deposit',
      sourceEntityId: pending.depositId,
      amountMinorUnits: pending.amountMinorUnits,
      currencyCode: child.displayCurrencyCode,
      approvedAtUtc,
    });
    state.pendingDeposits.splice(idx, 1);
    safeLog('info', '[dev-mock] deposit_approve OK', {
      depositId: pending.depositId,
      amount: pending.amountMinorUnits,
    });
    return {
      depositId: pending.depositId,
      amountMinorUnits: pending.amountMinorUnits,
      allocations,
    };
  },

  deposit_reject: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; depositId?: number; reason?: string };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending deposits',
      };
    }
    const reason = (input.reason ?? '').trim();
    if (reason === '') {
      throw {
        kind: 'validation',
        message: 'a reason is required when rejecting a deposit',
      };
    }
    const idx = state.pendingDeposits.findIndex((d) => d.depositId === input.depositId);
    if (idx === -1) {
      throw {
        kind: 'conflict',
        message: 'this deposit has already been decided',
      };
    }
    const pending = state.pendingDeposits[idx]!;
    state.ledger.push({
      kind: 'deposit',
      depositId: pending.depositId,
      childAccountId: pending.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: pending.amountMinorUnits,
      sourceCode: pending.sourceCode,
      sourceLabel: pending.sourceLabel,
      note: pending.note,
      allocations: [],
      statusCode: 'rejected',
      statusLabel: 'Rejected',
      decisionReason: reason,
      decidedByDisplayName: state.guardian.displayName,
    });
    state.pendingDeposits.splice(idx, 1);
    safeLog('info', '[dev-mock] deposit_reject OK', {
      depositId: pending.depositId,
      reason,
    });
    return { depositId: pending.depositId };
  },

  my_pending_deposits_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const callerId = state.lastAuthAccountId;
    const isGuardian = state.guardian?.accountId === callerId;
    if (callerId === null || isGuardian) {
      throw {
        kind: 'unauthorized',
        message: 'only the child who submitted a request can cancel it',
      };
    }
    return state.pendingDeposits
      .filter((d) => d.childAccountId === callerId)
      .slice()
      .sort((a, b) => b.requestedAtUtc.localeCompare(a.requestedAtUtc))
      .map((d) => ({
        depositId: d.depositId,
        amountMinorUnits: d.amountMinorUnits,
        sourceCode: d.sourceCode,
        sourceLabel: d.sourceLabel,
        note: d.note,
        requestedAtUtc: d.requestedAtUtc,
      }));
  },

  deposit_cancel: async (payload) => {
    const p = payload as { input?: { sessionId?: string; depositId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const callerId = state.lastAuthAccountId;
    const isGuardian = state.guardian?.accountId === callerId;
    if (callerId === null || isGuardian) {
      throw {
        kind: 'unauthorized',
        message: 'only the child who submitted a request can cancel it',
      };
    }
    const idx = state.pendingDeposits.findIndex((d) => d.depositId === input.depositId);
    if (idx === -1) {
      throw {
        kind: 'conflict',
        message: 'this deposit has already been decided',
      };
    }
    const pending = state.pendingDeposits[idx]!;
    if (pending.childAccountId !== callerId) {
      throw {
        kind: 'not_found',
        message: `deposit ${input.depositId} not found`,
      };
    }
    state.ledger.push({
      kind: 'deposit',
      depositId: pending.depositId,
      childAccountId: pending.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: pending.amountMinorUnits,
      sourceCode: pending.sourceCode,
      sourceLabel: pending.sourceLabel,
      note: pending.note,
      allocations: [],
      statusCode: 'cancelled',
      statusLabel: 'Cancelled',
      decisionReason: null,
      decidedByDisplayName:
        state.children.find((child) => child.accountId === callerId)?.displayName ?? null,
    });
    state.pendingDeposits.splice(idx, 1);
    safeLog('info', '[dev-mock] deposit_cancel OK', {
      depositId: pending.depositId,
    });
    return { depositId: pending.depositId };
  },

  withdrawal_create: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        bucketId?: number;
        amountMinorUnits?: number;
        reasonCode?: string;
        reasonNote?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    // Caller must be a child.
    if (state.lastAuthAccountId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message:
          'this command can only be called by a child account; guardians should use the approval flow',
      };
    }
    const actorChild = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!actorChild) {
      throw { kind: 'unauthorized', message: 'session does not match a child account' };
    }
    const amount = input.amountMinorUnits ?? 0;
    if (amount <= 0) {
      throw { kind: 'validation', message: 'withdrawal amount must be positive' };
    }
    if (input.reasonCode === 'savings_transfer') {
      throw {
        kind: 'validation',
        message: 'moving money between buckets must use Move money, not a spend request',
      };
    }
    const validReasons = [
      'toy',
      'food_snack',
      'video_game',
      'book_media',
      'clothing',
      'gift',
      'experience',
      'charitable_giving',
      'other',
    ];
    if (!input.reasonCode || !validReasons.includes(input.reasonCode)) {
      throw { kind: 'validation', message: `unknown withdrawal reason '${input.reasonCode}'` };
    }
    const bucket = state.buckets.find(
      (b) => b.bucketId === input.bucketId && b.childAccountId === actorChild.accountId,
    );
    if (!bucket) {
      throw {
        kind: 'not_found',
        message: 'bucket not found or does not belong to this child',
      };
    }
    if (bucket.requiresApprovalToWithdraw) {
      throw {
        kind: 'validation',
        message: `bucket '${bucket.bucketName}' requires guardian approval; use the approval flow instead`,
      };
    }
    if (amount > bucket.balanceMinorUnits) {
      throw {
        kind: 'validation',
        message: `insufficient funds: '${bucket.bucketName}' has a balance of ${bucket.balanceMinorUnits}, requested ${amount}`,
      };
    }

    bucket.balanceMinorUnits -= amount;
    const withdrawalId = state.nextWithdrawalId++;
    const reasonLabels: Record<string, string> = {
      toy: 'Toy',
      food_snack: 'Food or snack',
      video_game: 'Video game',
      book_media: 'Book or media',
      clothing: 'Clothing',
      gift: 'Gift for someone',
      experience: 'Outing or experience',
      charitable_giving: 'Charitable giving',
      other: 'Other',
    };
    state.ledger.push({
      kind: 'withdrawal',
      withdrawalId,
      childAccountId: actorChild.accountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: amount,
      bucketId: bucket.bucketId,
      bucketName: bucket.bucketName,
      reasonCode: input.reasonCode,
      reasonLabel: reasonLabels[input.reasonCode] ?? 'Other',
      reasonNote: input.reasonNote ?? null,
      reconciled: false,
    });
    safeLog('info', '[dev-mock] withdrawal_create OK', {
      withdrawalId,
      bucketName: bucket.bucketName,
      amountMinorUnits: amount,
    });
    return {
      withdrawalId,
      bucketId: bucket.bucketId,
      bucketName: bucket.bucketName,
      amountMinorUnits: amount,
      newBalanceMinorUnits: bucket.balanceMinorUnits,
    };
  },

  withdrawal_submit_pending: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        bucketId?: number;
        amountMinorUnits?: number;
        reasonCode?: string;
        reasonNote?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (state.lastAuthAccountId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }
    // Child-only.
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'this command can only be called by a child account',
      };
    }
    const actorChild = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!actorChild) {
      throw { kind: 'unauthorized', message: 'session does not match a child account' };
    }
    const amount = input.amountMinorUnits ?? 0;
    if (amount <= 0) {
      throw { kind: 'validation', message: 'withdrawal amount must be positive' };
    }
    if (input.reasonCode === 'savings_transfer') {
      throw {
        kind: 'validation',
        message: 'moving money between buckets must use Move money, not a spend request',
      };
    }
    const validReasons: Record<string, string> = {
      toy: 'Toy',
      food_snack: 'Food or snack',
      video_game: 'Video game',
      book_media: 'Book or media',
      clothing: 'Clothing',
      gift: 'Gift for someone',
      experience: 'Outing or experience',
      charitable_giving: 'Charitable giving',
      other: 'Other',
    };
    const reasonCode = input.reasonCode ?? '';
    if (!validReasons[reasonCode]) {
      throw { kind: 'validation', message: `unknown withdrawal reason '${reasonCode}'` };
    }
    const bucket = state.buckets.find(
      (b) => b.bucketId === input.bucketId && b.childAccountId === actorChild.accountId,
    );
    if (!bucket) {
      throw {
        kind: 'not_found',
        message: 'bucket not found or does not belong to this child',
      };
    }
    if (!bucket.requiresApprovalToWithdraw) {
      throw {
        kind: 'validation',
        message: `bucket '${bucket.bucketName}' does not require approval; use the spend flow instead`,
      };
    }
    // Up-front overdraft check (balance not debited here — debit happens at approval).
    if (amount > bucket.balanceMinorUnits) {
      throw {
        kind: 'validation',
        message: `insufficient funds: '${bucket.bucketName}' has a balance of ${bucket.balanceMinorUnits}, requested ${amount}`,
      };
    }

    const withdrawalId = state.nextWithdrawalId++;
    state.pendingWithdrawals.push({
      withdrawalId,
      childAccountId: actorChild.accountId,
      childDisplayName: actorChild.displayName,
      bucketId: bucket.bucketId,
      bucketName: bucket.bucketName,
      amountMinorUnits: amount,
      reasonCode,
      reasonLabel: validReasons[reasonCode],
      reasonNote: input.reasonNote ?? null,
      requestedAtUtc: new Date().toISOString(),
    });
    safeLog('info', '[dev-mock] withdrawal_submit_pending OK', {
      withdrawalId,
      bucketName: bucket.bucketName,
      amountMinorUnits: amount,
    });
    return {
      withdrawalId,
      bucketId: bucket.bucketId,
      bucketName: bucket.bucketName,
      amountMinorUnits: amount,
    };
  },

  pending_withdrawals_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can list pending withdrawals',
      };
    }
    const linkedChildIds = new Set(state.children.map((c) => c.accountId));
    return state.pendingWithdrawals
      .filter((w) => linkedChildIds.has(w.childAccountId))
      .slice()
      .sort((a, b) => a.requestedAtUtc.localeCompare(b.requestedAtUtc));
  },

  withdrawal_approve: async (payload) => {
    const p = payload as { input?: { sessionId?: string; withdrawalId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending withdrawals',
      };
    }
    const idx = state.pendingWithdrawals.findIndex((w) => w.withdrawalId === input.withdrawalId);
    if (idx === -1) {
      // Same convention as deposit_approve — unknown id treated as Conflict
      // since the most-likely cause is a double-click race after a peer
      // already decided it.
      throw {
        kind: 'conflict',
        message: 'this withdrawal has already been decided',
      };
    }
    const pending = state.pendingWithdrawals[idx]!;
    const bucket = state.buckets.find(
      (b) => b.bucketId === pending.bucketId && b.childAccountId === pending.childAccountId,
    );
    if (!bucket) {
      throw {
        kind: 'validation',
        message: `bucket '${pending.bucketName}' is no longer active; reject this withdrawal with a reason instead`,
      };
    }
    // REQ-4.4 acceptance criterion: re-check balance under "tx" — if it
    // dropped, refuse and leave the row pending so the guardian can
    // re-evaluate later.
    if (pending.amountMinorUnits > bucket.balanceMinorUnits) {
      throw {
        kind: 'validation',
        message: `insufficient funds: '${pending.bucketName}' has a balance of ${bucket.balanceMinorUnits}, requested ${pending.amountMinorUnits}. The withdrawal stays pending so you can re-evaluate.`,
      };
    }

    bucket.balanceMinorUnits -= pending.amountMinorUnits;
    state.ledger.push({
      kind: 'withdrawal',
      withdrawalId: pending.withdrawalId,
      childAccountId: pending.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: pending.amountMinorUnits,
      bucketId: pending.bucketId,
      bucketName: pending.bucketName,
      reasonCode: pending.reasonCode,
      reasonLabel: pending.reasonLabel,
      reasonNote: pending.reasonNote,
      statusCode: 'posted',
      statusLabel: 'Posted',
      decisionReason: null,
      decidedByDisplayName: state.guardian.displayName,
      reconciled: false,
    });
    state.pendingWithdrawals.splice(idx, 1);
    safeLog('info', '[dev-mock] withdrawal_approve OK', {
      withdrawalId: pending.withdrawalId,
      amount: pending.amountMinorUnits,
    });
    return {
      withdrawalId: pending.withdrawalId,
      bucketId: pending.bucketId,
      bucketName: pending.bucketName,
      amountMinorUnits: pending.amountMinorUnits,
      newBalanceMinorUnits: bucket.balanceMinorUnits,
    };
  },

  withdrawal_reject: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; withdrawalId?: number; reason?: string };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending withdrawals',
      };
    }
    const reason = (input.reason ?? '').trim();
    if (reason === '') {
      throw {
        kind: 'validation',
        message: 'a reason is required when rejecting a withdrawal',
      };
    }
    const idx = state.pendingWithdrawals.findIndex((w) => w.withdrawalId === input.withdrawalId);
    if (idx === -1) {
      throw {
        kind: 'conflict',
        message: 'this withdrawal has already been decided',
      };
    }
    const pending = state.pendingWithdrawals[idx]!;
    state.ledger.push({
      kind: 'withdrawal',
      withdrawalId: pending.withdrawalId,
      childAccountId: pending.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: pending.amountMinorUnits,
      bucketId: pending.bucketId,
      bucketName: pending.bucketName,
      reasonCode: pending.reasonCode,
      reasonLabel: pending.reasonLabel,
      reasonNote: pending.reasonNote,
      statusCode: 'rejected',
      statusLabel: 'Rejected',
      decisionReason: reason,
      decidedByDisplayName: state.guardian.displayName,
      reconciled: false,
    });
    state.pendingWithdrawals.splice(idx, 1);
    safeLog('info', '[dev-mock] withdrawal_reject OK', {
      withdrawalId: pending.withdrawalId,
      reason,
    });
    return { withdrawalId: pending.withdrawalId };
  },

  my_pending_withdrawals_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    // Caller must be a child.
    const callerId = state.lastAuthAccountId;
    const isGuardian = state.guardian?.accountId === callerId;
    if (callerId === null || isGuardian) {
      throw {
        kind: 'unauthorized',
        message: 'only the child who submitted a request can cancel it',
      };
    }
    return state.pendingWithdrawals
      .filter((w) => w.childAccountId === callerId)
      .slice()
      .sort((a, b) => b.requestedAtUtc.localeCompare(a.requestedAtUtc))
      .map((w) => ({
        withdrawalId: w.withdrawalId,
        bucketId: w.bucketId,
        bucketName: w.bucketName,
        amountMinorUnits: w.amountMinorUnits,
        reasonCode: w.reasonCode,
        reasonLabel: w.reasonLabel,
        reasonNote: w.reasonNote,
        requestedAtUtc: w.requestedAtUtc,
      }));
  },

  withdrawal_cancel: async (payload) => {
    const p = payload as { input?: { sessionId?: string; withdrawalId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const callerId = state.lastAuthAccountId;
    const isGuardian = state.guardian?.accountId === callerId;
    if (callerId === null || isGuardian) {
      throw {
        kind: 'unauthorized',
        message: 'only the child who submitted a request can cancel it',
      };
    }
    const idx = state.pendingWithdrawals.findIndex((w) => w.withdrawalId === input.withdrawalId);
    if (idx === -1) {
      // Either doesn't exist or was already decided (then removed from pending).
      // Backend distinguishes NotFound from Conflict; this mock returns the more
      // common outcome: someone already decided in the brief window.
      throw {
        kind: 'conflict',
        message: 'this withdrawal has already been decided',
      };
    }
    const pending = state.pendingWithdrawals[idx]!;
    if (pending.childAccountId !== callerId) {
      // Don't leak existence — same as backend NotFound.
      throw {
        kind: 'not_found',
        message: `withdrawal ${input.withdrawalId} not found`,
      };
    }
    state.ledger.push({
      kind: 'withdrawal',
      withdrawalId: pending.withdrawalId,
      childAccountId: pending.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: pending.amountMinorUnits,
      bucketId: pending.bucketId,
      bucketName: pending.bucketName,
      reasonCode: pending.reasonCode,
      reasonLabel: pending.reasonLabel,
      reasonNote: pending.reasonNote,
      statusCode: 'cancelled',
      statusLabel: 'Cancelled',
      decisionReason: null,
      decidedByDisplayName:
        state.children.find((child) => child.accountId === callerId)?.displayName ?? null,
      reconciled: false,
    });
    state.pendingWithdrawals.splice(idx, 1);
    safeLog('info', '[dev-mock] withdrawal_cancel OK', {
      withdrawalId: pending.withdrawalId,
    });
    return { withdrawalId: pending.withdrawalId };
  },

  // US-WITHDRAWAL-05 — guardian marks/unmarks a posted withdrawal
  // as reconciled. Children get Unauthorized; double-mark/unmark
  // returns Conflict; non-existent withdrawal returns NotFound.
  withdrawal_set_reconciled: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; withdrawalId?: number; reconciled?: boolean };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can reconcile withdrawals',
      };
    }
    const wid = input.withdrawalId;
    if (typeof wid !== 'number') {
      throw { kind: 'validation', message: 'withdrawalId is required' };
    }
    const target = state.ledger.find((e) => e.kind === 'withdrawal' && e.withdrawalId === wid);
    if (!target || target.kind !== 'withdrawal') {
      throw { kind: 'not_found', message: `withdrawal ${wid} not found` };
    }
    const wantReconciled = !!input.reconciled;
    if (target.reconciled === wantReconciled) {
      throw {
        kind: 'conflict',
        message: wantReconciled
          ? 'withdrawal is already reconciled'
          : 'withdrawal is not currently reconciled',
      };
    }
    target.reconciled = wantReconciled;
    safeLog('info', '[dev-mock] withdrawal_set_reconciled OK', {
      withdrawalId: wid,
      reconciled: wantReconciled,
    });
    return null;
  },

  history_list: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        childAccountId?: number;
        limit?: number;
        atFromUtc?: string;
        atToUtc?: string;
        kindFilter?: string;
        bucketIdFilter?: number;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (state.lastAuthAccountId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }

    // Determine target child.
    let targetChildId: number;
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      if (input.childAccountId === undefined || input.childAccountId === null) {
        throw {
          kind: 'validation',
          message: 'childAccountId is required for guardians',
        };
      }
      if (!state.children.find((c) => c.accountId === input.childAccountId)) {
        throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
      }
      targetChildId = input.childAccountId;
    } else {
      // Child — always own history.
      targetChildId = state.lastAuthAccountId;
    }

    const limit = Math.min(Math.max(input.limit ?? 100, 1), 500);
    const targetChild = state.children.find((c) => c.accountId === targetChildId);
    const targetChildName = targetChild?.displayName ?? null;
    const guardianName = state.guardian?.displayName ?? null;
    const transferHistory = terminalTransferHistory(targetChildId);
    const entries = [...state.ledger, ...transferHistory]
      .filter((e) => e.childAccountId === targetChildId)
      // US-HISTORY-04 — apply optional filter fields. Mirror the
      // backend's HistoryFilter semantics: missing field = permissive.
      .filter((e) => {
        if (input.atFromUtc && e.atUtc < input.atFromUtc) return false;
        if (input.atToUtc && e.atUtc > input.atToUtc) return false;
        if (input.kindFilter && e.kind !== input.kindFilter) return false;
        if (input.bucketIdFilter !== undefined && input.bucketIdFilter !== null) {
          if (e.kind === 'withdrawal') {
            if (e.bucketId !== input.bucketIdFilter) return false;
          } else if (e.kind === 'deposit') {
            // deposit: matches if any allocation references the bucket.
            const allocations =
              (e as unknown as { allocations?: { bucketId: number }[] }).allocations ?? [];
            if (!allocations.some((a) => a.bucketId === input.bucketIdFilter)) return false;
          } else if (
            e.sourceBucketId !== input.bucketIdFilter &&
            e.targetBucketId !== input.bucketIdFilter
          ) {
            return false;
          }
        }
        return true;
      })
      .slice()
      .sort((a, b) => {
        if (a.atUtc !== b.atUtc) return b.atUtc.localeCompare(a.atUtc);
        const aId =
          a.kind === 'deposit'
            ? a.depositId
            : a.kind === 'withdrawal'
              ? a.withdrawalId
              : a.transferId;
        const bId =
          b.kind === 'deposit'
            ? b.depositId
            : b.kind === 'withdrawal'
              ? b.withdrawalId
              : b.transferId;
        return bId - aId;
      })
      .slice(0, limit)
      .map((e) => {
        if (e.kind === 'deposit') {
          return {
            ...e,
            statusCode: e.statusCode ?? 'approved',
            statusLabel: e.statusLabel ?? 'Approved',
            decisionReason: e.decisionReason ?? null,
            decidedByDisplayName: e.decidedByDisplayName ?? guardianName,
          };
        }
        if (e.kind === 'withdrawal') {
          return {
            ...e,
            statusCode: e.statusCode ?? 'posted',
            statusLabel: e.statusLabel ?? 'Posted',
            decisionReason: e.decisionReason ?? null,
            decidedByDisplayName: e.decidedByDisplayName ?? targetChildName,
          };
        }
        return e;
      });
    return entries;
  },

  // US-HISTORY-03 — consolidated history across all linked children.
  // Guardian-only. Iterates per child, applies filter, merges and
  // sorts newest-first, then trims to limit.
  consolidated_history_list: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        limit?: number;
        atFromUtc?: string;
        atToUtc?: string;
        kindFilter?: string;
        bucketIdFilter?: number;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'consolidated history is guardian-only' };
    }
    const limit = Math.min(Math.max(input.limit ?? 100, 1), 500);
    const guardianName = state.guardian.displayName;

    const merged: { childAccountId: number; childDisplayName: string; entry: unknown }[] = [];
    for (const child of state.children) {
      const childEntries = [...state.ledger, ...terminalTransferHistory(child.accountId)]
        .filter((e) => e.childAccountId === child.accountId)
        .filter((e) => {
          if (input.atFromUtc && e.atUtc < input.atFromUtc) return false;
          if (input.atToUtc && e.atUtc > input.atToUtc) return false;
          if (input.kindFilter && e.kind !== input.kindFilter) return false;
          if (input.bucketIdFilter !== undefined && input.bucketIdFilter !== null) {
            if (e.kind === 'withdrawal') {
              if (e.bucketId !== input.bucketIdFilter) return false;
            } else if (e.kind === 'deposit') {
              const allocations =
                (e as unknown as { allocations?: { bucketId: number }[] }).allocations ?? [];
              if (!allocations.some((a) => a.bucketId === input.bucketIdFilter)) return false;
            } else if (
              e.sourceBucketId !== input.bucketIdFilter &&
              e.targetBucketId !== input.bucketIdFilter
            ) {
              return false;
            }
          }
          return true;
        })
        .map((e) => {
          if (e.kind === 'deposit') return { ...e, decidedByDisplayName: guardianName };
          if (e.kind === 'withdrawal') return { ...e, decidedByDisplayName: child.displayName };
          return e;
        });
      for (const entry of childEntries) {
        merged.push({
          childAccountId: child.accountId,
          childDisplayName: child.displayName,
          entry,
        });
      }
    }
    merged.sort((a, b) => {
      const ae = a.entry as {
        atUtc: string;
        depositId?: number;
        withdrawalId?: number;
        transferId?: number;
      };
      const be = b.entry as {
        atUtc: string;
        depositId?: number;
        withdrawalId?: number;
        transferId?: number;
      };
      if (ae.atUtc !== be.atUtc) return be.atUtc.localeCompare(ae.atUtc);
      const aId = ae.depositId ?? ae.withdrawalId ?? ae.transferId ?? 0;
      const bId = be.depositId ?? be.withdrawalId ?? be.transferId ?? 0;
      return bId - aId;
    });
    return merged.slice(0, limit);
  },

  // US-HISTORY-05 — guardian audit log view. Surfaces audit_events
  // visible to the guardian (own actor or subject = linked child).
  // The dev mock doesn't maintain a parallel audit table — most
  // handlers don't bother emitting audit events synthetically — so
  // this returns an empty list. The backend covers the full
  // semantics; the mock is here so the UI's audit log screen
  // doesn't crash when invoked in the dev preview environment.
  audit_log_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'audit log is guardian-only' };
    }
    return [];
  },

  chore_types_list: async () => {
    // Mirror seed in 0001_initial.sql.
    return [
      { typeCode: 'zone', typeLabel: 'Zone', sortOrder: 1 },
      { typeCode: 'a_la_carte', typeLabel: 'A la carte', sortOrder: 2 },
      { typeCode: 'recurring', typeLabel: 'Recurring', sortOrder: 3 },
    ];
  },

  chore_create: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        childAccountId?: number | null;
        choreName?: string;
        choreDescription?: string;
        choreTypeCode?: string;
        zoneName?: string;
        rewardMinorUnits?: number;
        recurrencePattern?: string;
        requiresApprovalToClaim?: boolean;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'only guardians can create chores' };
    }
    // US-CHORE-07: childAccountId is now nullable. When null, the
    // chore lives on the master list ("any child"). When set, it's
    // assigned to that specific child.
    let childAccountId: number | null = null;
    if (input.childAccountId !== null && input.childAccountId !== undefined) {
      const child = state.children.find((c) => c.accountId === input.childAccountId);
      if (!child) {
        throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
      }
      childAccountId = child.accountId;
    }

    const requiresApproval = input.requiresApprovalToClaim === true;
    if (requiresApproval && childAccountId !== null) {
      throw {
        kind: 'validation',
        message: 'requires_approval_to_claim is only meaningful for master-list chores',
      };
    }

    const name = (input.choreName ?? '').trim();
    if (name === '') {
      throw { kind: 'validation', message: 'chore name is required' };
    }
    if (name.length > 120) {
      throw { kind: 'validation', message: 'chore name must be 120 characters or fewer' };
    }
    const description = input.choreDescription?.trim();
    if (description && description.length > 500) {
      throw { kind: 'validation', message: 'description must be 500 characters or fewer' };
    }
    const zoneName = input.zoneName?.trim();
    if (zoneName && zoneName.length > 80) {
      throw { kind: 'validation', message: 'zone name must be 80 characters or fewer' };
    }
    const recurrence = input.recurrencePattern?.trim();
    if (recurrence && recurrence.length > 120) {
      throw { kind: 'validation', message: 'recurrence pattern must be 120 characters or fewer' };
    }
    const reward = input.rewardMinorUnits ?? 0;
    if (reward < 0) {
      throw { kind: 'validation', message: 'reward cannot be negative' };
    }

    const validTypes: Record<string, string> = {
      zone: 'Zone',
      a_la_carte: 'A la carte',
      recurring: 'Recurring',
    };
    const typeCode = input.choreTypeCode ?? '';
    if (!validTypes[typeCode]) {
      throw { kind: 'validation', message: `unknown chore type '${typeCode}'` };
    }

    const choreId = state.nextChoreId++;
    const now = new Date().toISOString();
    const chore = {
      choreId,
      childAccountId,
      createdByAccountId: state.guardian.accountId,
      choreName: name,
      choreDescription: description && description !== '' ? description : null,
      choreTypeCode: typeCode,
      choreTypeLabel: validTypes[typeCode],
      zoneName: zoneName && zoneName !== '' ? zoneName : null,
      rewardMinorUnits: reward,
      recurrencePattern: recurrence && recurrence !== '' ? recurrence : null,
      isActive: true,
      requiresApprovalToClaim: requiresApproval,
      createdAtUtc: now,
    };
    state.chores.set(choreId, chore);
    safeLog('info', '[dev-mock] chore_create OK', {
      choreId,
      choreName: name,
      reward,
      typeCode,
      masterList: childAccountId === null,
      requiresApproval,
    });
    return {
      ...chore,
      hasOpenClaim: false,
      lastRejectedAtUtc: null,
      lastRejectionReason: null,
      lastRejectedCompletionId: null,
    };
  },

  chores_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string; childAccountId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (state.lastAuthAccountId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }

    let targetChildId: number | null;
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      if (
        input.childAccountId !== undefined &&
        input.childAccountId !== null &&
        !state.children.find((c) => c.accountId === input.childAccountId)
      ) {
        throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
      }
      targetChildId = input.childAccountId ?? null;
    } else {
      targetChildId = state.lastAuthAccountId;
    }

    // US-UI-CHORE-REJECTIONS — for each chore, find the latest
    // completion (by claimedAtUtc desc, then completion id desc).
    // If that latest completion's status is `rejected`, surface
    // its decidedAtUtc + decisionReason on the entry. Re-claims
    // (newer claimed/approved completions) implicitly clear it.
    const completionsForChild = Array.from(state.choreCompletions.values()).filter(
      (cc) => targetChildId === null || cc.childAccountId === targetChildId,
    );
    const lastRejectedByChore = new Map<
      number,
      {
        choreCompletionId: number;
        decidedAtUtc: string;
        decisionReason: string | null;
      }
    >();
    const choreIds = new Set(completionsForChild.map((cc) => cc.choreId));
    for (const choreId of choreIds) {
      const forChore = completionsForChild
        .filter((cc) => cc.choreId === choreId)
        .sort((a, b) => {
          if (a.claimedAtUtc !== b.claimedAtUtc) {
            return b.claimedAtUtc.localeCompare(a.claimedAtUtc);
          }
          return b.choreCompletionId - a.choreCompletionId;
        });
      const latest = forChore[0];
      // US-CHORE-10: filter out archived rows so they don't keep
      // showing as rejected in the chore list.
      if (latest && latest.status === 'rejected' && latest.decidedAtUtc && !latest.childArchived) {
        lastRejectedByChore.set(choreId, {
          choreCompletionId: latest.choreCompletionId,
          decidedAtUtc: latest.decidedAtUtc,
          decisionReason: latest.decisionReason,
        });
      }
    }

    return Array.from(state.chores.values())
      .filter((c) => (targetChildId === null || c.childAccountId === targetChildId) && c.isActive)
      .sort((a, b) => {
        if (a.createdAtUtc !== b.createdAtUtc) {
          return b.createdAtUtc.localeCompare(a.createdAtUtc);
        }
        return b.choreId - a.choreId;
      })
      .map((c) => {
        const lr = lastRejectedByChore.get(c.choreId);
        return {
          ...c,
          childDisplayName:
            state.children.find((child) => child.accountId === c.childAccountId)?.displayName ??
            null,
          hasOpenClaim: state.openClaims.has(c.choreId),
          lastRejectedAtUtc: lr?.decidedAtUtc ?? null,
          lastRejectionReason: lr?.decisionReason ?? null,
          lastRejectedCompletionId: lr?.choreCompletionId ?? null,
        };
      });
  },

  // US-UI-CHORE-REJECTIONS — single int for the child Home badge.
  // Counts chores whose latest completion is `rejected`. Guardians
  // get 0 (the badge surface is child-only).
  chores_rejected_count: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      return 0;
    }
    const completions = Array.from(state.choreCompletions.values()).filter(
      (cc) => cc.childAccountId === actorId,
    );
    const choreIds = new Set(completions.map((cc) => cc.choreId));
    let count = 0;
    for (const choreId of choreIds) {
      const forChore = completions
        .filter((cc) => cc.choreId === choreId)
        .sort((a, b) => {
          if (a.claimedAtUtc !== b.claimedAtUtc) {
            return b.claimedAtUtc.localeCompare(a.claimedAtUtc);
          }
          return b.choreCompletionId - a.choreCompletionId;
        });
      if (forChore[0]?.status === 'rejected') count++;
    }
    return count;
  },

  chore_claim: async (payload) => {
    const p = payload as { input?: { sessionId?: string; choreId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (state.lastAuthAccountId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }
    // Caller must be a child.
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only the child assigned to a chore can mark it done',
      };
    }
    const actorChild = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!actorChild) {
      throw { kind: 'unauthorized', message: 'session does not match a child account' };
    }
    const chore = state.chores.get(input.choreId ?? -1);
    if (!chore || !chore.isActive) {
      throw { kind: 'not_found', message: 'chore not found or inactive' };
    }
    if (chore.childAccountId !== actorChild.accountId) {
      throw { kind: 'unauthorized', message: 'this chore is not assigned to you' };
    }
    if (state.openClaims.has(chore.choreId)) {
      throw {
        kind: 'conflict',
        message: 'this chore is already marked done and waiting on your guardian',
      };
    }

    const completionId = state.nextChoreCompletionId++;
    const now = new Date().toISOString();
    state.openClaims.add(chore.choreId);
    state.choreCompletions.set(completionId, {
      choreCompletionId: completionId,
      choreId: chore.choreId,
      childAccountId: actorChild.accountId,
      childDisplayName: actorChild.displayName,
      choreName: chore.choreName,
      choreTypeLabel: chore.choreTypeLabel,
      rewardMinorUnits: chore.rewardMinorUnits,
      claimedAtUtc: now,
      status: 'claimed',
      decidedAtUtc: null,
      decisionReason: null,
      childArchived: false,
    });
    safeLog('info', '[dev-mock] chore_claim OK', {
      choreCompletionId: completionId,
      choreId: chore.choreId,
    });
    return {
      choreCompletionId: completionId,
      choreId: chore.choreId,
      claimedAtUtc: now,
    };
  },

  pending_chores_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending chore completions',
      };
    }
    const linkedChildIds = new Set(state.children.map((c) => c.accountId));
    return Array.from(state.choreCompletions.values())
      .filter((cc) => cc.status === 'claimed' && linkedChildIds.has(cc.childAccountId))
      .sort((a, b) => {
        if (a.claimedAtUtc !== b.claimedAtUtc) {
          return a.claimedAtUtc.localeCompare(b.claimedAtUtc);
        }
        return a.choreCompletionId - b.choreCompletionId;
      })
      .map(({ status, decidedAtUtc, decisionReason, childArchived, ...rest }) => {
        // US-CHORE-10 (10a): surface recurrencePattern from the chore
        // record so the UI can gate the Reject+remove button to
        // non-recurring chores.
        const chore = state.chores.get(rest.choreId);
        return {
          ...rest,
          recurrencePattern: chore?.recurrencePattern ?? null,
        };
      });
  },

  chore_completion_approve: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        choreCompletionId?: number;
        adjustedRewardMinorUnits?: number | null;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending chore completions',
      };
    }
    // US-CHORE-04 — validate adjustment before doing any state work.
    const adjustment =
      input.adjustedRewardMinorUnits === undefined || input.adjustedRewardMinorUnits === null
        ? null
        : input.adjustedRewardMinorUnits;
    if (adjustment !== null && adjustment < 0) {
      throw {
        kind: 'validation',
        message: 'adjusted reward cannot be negative',
      };
    }
    const cc = state.choreCompletions.get(input.choreCompletionId ?? -1);
    if (!cc || cc.status !== 'claimed') {
      // Backend distinguishes NotFound vs Conflict; mock collapses to Conflict
      // because the UI-level cause of "approve fired but state moved" is a
      // double-click race. Same behaviour as deposit_approve.
      throw {
        kind: 'conflict',
        message: 'this chore completion has already been decided',
      };
    }

    cc.status = 'approved';
    state.openClaims.delete(cc.choreId);

    // US-CHORE-04 — effective reward is the override if provided,
    // otherwise the chore's default. Both the deposit amount and the
    // returned rewardMinorUnits use the effective value.
    const effectiveReward = adjustment !== null ? adjustment : cc.rewardMinorUnits;

    // Zero-reward chore — flip status only.
    if (effectiveReward <= 0) {
      safeLog('info', '[dev-mock] chore_completion_approve OK (zero reward)', {
        choreCompletionId: cc.choreCompletionId,
        adjustment,
      });
      return {
        choreCompletionId: cc.choreCompletionId,
        rewardMinorUnits: 0,
        depositId: null,
        allocations: [],
      };
    }

    // Allocate reward + credit buckets.
    const childBuckets = state.buckets
      .filter((b) => b.childAccountId === cc.childAccountId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (childBuckets.length === 0) {
      throw {
        kind: 'validation',
        message: 'this child has no active buckets to allocate the reward into',
      };
    }
    const splits = allocateDeposit(
      effectiveReward,
      childBuckets.map((b) => ({
        bucketId: b.bucketId,
        allocationBps: b.allocationBps,
        sortOrder: b.sortOrder,
      })),
    );
    for (const split of splits) {
      const bucket = childBuckets.find((b) => b.bucketId === split.bucketId);
      if (bucket) bucket.balanceMinorUnits += split.amountMinorUnits;
    }

    const allocations = splits.map((s) => ({
      bucketId: s.bucketId,
      bucketName: childBuckets.find((b) => b.bucketId === s.bucketId)?.bucketName ?? '',
      amountMinorUnits: s.amountMinorUnits,
    }));
    const depositId = state.nextDepositId++;
    state.ledger.push({
      kind: 'deposit',
      depositId,
      childAccountId: cc.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: effectiveReward,
      sourceCode: 'chore',
      sourceLabel: 'Chore reward',
      note: `Chore: ${cc.choreName}`,
      allocations,
    });
    safeLog('info', '[dev-mock] chore_completion_approve OK', {
      choreCompletionId: cc.choreCompletionId,
      depositId,
      reward: effectiveReward,
      adjustment,
    });
    return {
      choreCompletionId: cc.choreCompletionId,
      rewardMinorUnits: effectiveReward,
      depositId,
      allocations,
    };
  },

  chore_completion_reject: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        choreCompletionId?: number;
        reason?: string;
        removeChore?: boolean;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can decide on pending chore completions',
      };
    }
    const reason = (input.reason ?? '').trim();
    if (reason === '') {
      throw {
        kind: 'validation',
        message: 'a reason is required when rejecting a chore claim',
      };
    }
    const cc = state.choreCompletions.get(input.choreCompletionId ?? -1);
    if (!cc || cc.status !== 'claimed') {
      throw {
        kind: 'conflict',
        message: 'this chore completion has already been decided',
      };
    }
    // US-CHORE-10 (10a): when removeChore is set, validate that the
    // underlying chore is non-recurring BEFORE we flip the completion
    // status, so the validation error short-circuits cleanly. Then
    // also flip chores.is_active = 0 in the same logical step.
    const removeChore = input.removeChore === true;
    if (removeChore) {
      const chore = state.chores.get(cc.choreId);
      if (!chore) {
        throw { kind: 'not_found', message: `chore ${cc.choreId} not found` };
      }
      if (chore.recurrencePattern !== null && chore.recurrencePattern !== undefined) {
        throw {
          kind: 'validation',
          message:
            "recurring chores can't be removed at rejection — they'll come back next period " +
            "regardless. Use the chore's Deactivate action instead.",
        };
      }
      chore.isActive = false;
    }
    cc.status = 'rejected';
    cc.decidedAtUtc = new Date().toISOString();
    cc.decisionReason = reason;
    state.openClaims.delete(cc.choreId);
    safeLog('info', '[dev-mock] chore_completion_reject OK', {
      choreCompletionId: cc.choreCompletionId,
      reason,
      removeChore,
    });
    return { choreCompletionId: cc.choreCompletionId, choreRemoved: removeChore };
  },

  // US-CHORE-10 (10b) — child re-submits a rejected chore completion.
  // Updates the existing row's status back to 'claimed' and clears
  // decision metadata. The chore must still be active; if the
  // guardian rejected-and-removed it, the row drops into the
  // archive flow instead.
  chore_completion_resubmit: async (payload) => {
    const p = payload as { input?: { sessionId?: string; choreCompletionId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    const child = state.children.find((c) => c.accountId === actorId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'this action is only available to child accounts',
      };
    }
    const cc = state.choreCompletions.get(input.choreCompletionId ?? -1);
    if (!cc || cc.childAccountId !== child.accountId) {
      throw {
        kind: 'not_found',
        message: `chore completion ${input.choreCompletionId} not found`,
      };
    }
    if (cc.status !== 'rejected') {
      throw {
        kind: 'conflict',
        message: `chore completion ${cc.choreCompletionId} is ${cc.status}, not rejected`,
      };
    }
    const chore = state.chores.get(cc.choreId);
    if (!chore || !chore.isActive) {
      throw {
        kind: 'validation',
        message:
          'this chore was removed by a guardian — it can be archived from your view but cannot be re-submitted',
      };
    }
    cc.status = 'claimed';
    cc.claimedAtUtc = new Date().toISOString();
    cc.decidedAtUtc = null;
    cc.decisionReason = null;
    state.openClaims.add(cc.choreId);
    safeLog('info', '[dev-mock] chore_completion_resubmit OK', {
      choreCompletionId: cc.choreCompletionId,
    });
    return { choreCompletionId: cc.choreCompletionId };
  },

  // US-CHORE-10 (10c) — child archives a rejected-and-removed completion.
  // Sets `childArchived = true`. Chore must be inactive (otherwise the
  // child should re-submit instead).
  chore_completion_archive: async (payload) => {
    const p = payload as { input?: { sessionId?: string; choreCompletionId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    const child = state.children.find((c) => c.accountId === actorId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'this action is only available to child accounts',
      };
    }
    const cc = state.choreCompletions.get(input.choreCompletionId ?? -1);
    if (!cc || cc.childAccountId !== child.accountId) {
      throw {
        kind: 'not_found',
        message: `chore completion ${input.choreCompletionId} not found`,
      };
    }
    if (cc.status !== 'rejected') {
      throw {
        kind: 'conflict',
        message: `chore completion ${cc.choreCompletionId} is ${cc.status}, not rejected`,
      };
    }
    const chore = state.chores.get(cc.choreId);
    if (chore?.isActive) {
      throw {
        kind: 'validation',
        message:
          'this chore is still active — re-submit it instead of archiving. Archive is only for chores that were removed by a guardian.',
      };
    }
    cc.childArchived = true;
    safeLog('info', '[dev-mock] chore_completion_archive OK', {
      choreCompletionId: cc.choreCompletionId,
    });
    return { choreCompletionId: cc.choreCompletionId };
  },

  // US-CHORE-10 (10c) — child views rejected-and-removed completions
  // they haven't yet archived.
  chores_list_rejected_removed: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    const child = state.children.find((c) => c.accountId === actorId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'this action is only available to child accounts',
      };
    }
    const out = Array.from(state.choreCompletions.values())
      .filter((cc) => {
        if (cc.childAccountId !== child.accountId) return false;
        if (cc.status !== 'rejected') return false;
        if (cc.childArchived) return false;
        const chore = state.chores.get(cc.choreId);
        if (!chore) return false;
        return !chore.isActive;
      })
      .sort((a, b) => {
        const at = a.decidedAtUtc ?? '';
        const bt = b.decidedAtUtc ?? '';
        if (at !== bt) return bt.localeCompare(at);
        return b.choreCompletionId - a.choreCompletionId;
      })
      .map((cc) => ({
        choreCompletionId: cc.choreCompletionId,
        choreId: cc.choreId,
        choreName: cc.choreName,
        decidedAtUtc: cc.decidedAtUtc ?? '',
        decisionReason: cc.decisionReason,
      }));
    return out;
  },

  chore_deactivate: async (payload) => {
    const p = payload as { input?: { sessionId?: string; choreId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can deactivate chores',
      };
    }
    const chore = state.chores.get(input.choreId ?? -1);
    if (!chore) {
      throw { kind: 'not_found', message: `chore ${input.choreId} not found` };
    }
    if (!chore.isActive) {
      throw { kind: 'conflict', message: `chore ${input.choreId} is already deactivated` };
    }
    chore.isActive = false;
    safeLog('info', '[dev-mock] chore_deactivate OK', { choreId: chore.choreId });
    return { choreId: chore.choreId };
  },

  chore_reactivate: async (payload) => {
    const p = payload as { input?: { sessionId?: string; choreId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can reactivate chores',
      };
    }
    const chore = state.chores.get(input.choreId ?? -1);
    if (!chore) {
      throw { kind: 'not_found', message: `chore ${input.choreId} not found` };
    }
    if (chore.isActive) {
      throw { kind: 'conflict', message: `chore ${input.choreId} is already active` };
    }
    chore.isActive = true;
    safeLog('info', '[dev-mock] chore_reactivate OK', { choreId: chore.choreId });
    return { choreId: chore.choreId };
  },

  inactive_chores_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string; childAccountId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can list deactivated chores',
      };
    }
    if (
      input.childAccountId !== undefined &&
      input.childAccountId !== null &&
      !state.children.find((c) => c.accountId === input.childAccountId)
    ) {
      throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
    }
    return Array.from(state.chores.values())
      .filter(
        (c) =>
          !c.isActive &&
          (input.childAccountId === undefined ||
            input.childAccountId === null ||
            c.childAccountId === input.childAccountId),
      )
      .sort((a, b) => {
        if (a.createdAtUtc !== b.createdAtUtc) {
          return b.createdAtUtc.localeCompare(a.createdAtUtc);
        }
        return b.choreId - a.choreId;
      })
      .map((c) => ({ ...c, hasOpenClaim: false }));
  },

  guardian_types_list: async () => {
    // Mirror the seed in 0001_initial.sql.
    return [
      { typeCode: 'parent', typeLabel: 'Parent', sortOrder: 1 },
      { typeCode: 'step_parent', typeLabel: 'Step-Parent', sortOrder: 2 },
      { typeCode: 'grandparent', typeLabel: 'Grandparent', sortOrder: 3 },
      { typeCode: 'aunt_uncle', typeLabel: 'Aunt/Uncle', sortOrder: 4 },
      { typeCode: 'legal_guardian', typeLabel: 'Legal Guardian', sortOrder: 5 },
      { typeCode: 'foster_parent', typeLabel: 'Foster Parent', sortOrder: 6 },
      { typeCode: 'sibling', typeLabel: 'Adult Sibling', sortOrder: 7 },
    ];
  },

  child_guardian_type_update: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; childAccountId?: number; guardianTypeCode?: string };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: "only guardians can change a child's relationship type",
      };
    }
    const child = state.children.find((c) => c.accountId === input.childAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
    }
    const validTypes = [
      'parent',
      'step_parent',
      'grandparent',
      'aunt_uncle',
      'legal_guardian',
      'foster_parent',
      'sibling',
    ];
    if (!input.guardianTypeCode || !validTypes.includes(input.guardianTypeCode)) {
      throw {
        kind: 'validation',
        message: `unknown guardian type '${input.guardianTypeCode}'`,
      };
    }
    state.guardianTypeByChildId.set(child.accountId, input.guardianTypeCode);
    safeLog('info', '[dev-mock] child_guardian_type_update OK', {
      childAccountId: child.accountId,
      guardianTypeCode: input.guardianTypeCode,
    });
    return {
      childAccountId: child.accountId,
      guardianTypeCode: input.guardianTypeCode,
    };
  },

  child_deactivate: async (payload) => {
    const p = payload as { input?: { sessionId?: string; childAccountId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'only guardians can deactivate a child account' };
    }
    if (input.childAccountId === state.guardian.accountId) {
      throw {
        kind: 'validation',
        message: 'you cannot deactivate the account you are signed in with',
      };
    }
    const idx = state.children.findIndex((c) => c.accountId === input.childAccountId);
    if (idx === -1) {
      throw { kind: 'unauthorized', message: 'you are not the guardian of that account' };
    }
    // Remove the child from active listings while retaining an in-memory
    // recovery snapshot. Refreshing the public demo discards this snapshot
    // and restores the original synthetic family.
    const removedChild = state.children.splice(idx, 1)[0]!;
    const deletedAtUtc = new Date().toISOString();
    const purgeAfter = new Date();
    purgeAfter.setDate(purgeAfter.getDate() + 30);
    const purgeAfterUtc = purgeAfter.toISOString();
    const removedBuckets = state.buckets.filter(
      (bucket) => bucket.childAccountId === removedChild.accountId,
    );
    state.recoverableChildren.push({
      ...removedChild,
      deletedAtUtc,
      purgeAfterUtc,
      buckets: removedBuckets.map((bucket) => ({ ...bucket })),
    });
    state.buckets = state.buckets.filter((b) => b.childAccountId !== removedChild.accountId);
    state.failureTimestampsMs.delete(removedChild.accountId);
    state.lockoutExpiresAtMs.delete(removedChild.accountId);
    state.guardianTypeByChildId.delete(removedChild.accountId);
    safeLog('info', '[dev-mock] child_deactivate OK', { childAccountId: removedChild.accountId });
    return { childAccountId: removedChild.accountId, deletedAtUtc, purgeAfterUtc };
  },

  // -------------------------------------------------------------------------
  // US-SETUP-04 — add / remove additional guardians
  // -------------------------------------------------------------------------

  guardian_add: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        displayName?: string;
        password?: string;
        displayCurrencyCode?: string;
        childRelationships?: { childAccountId?: number; guardianTypeCode?: string }[];
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    // Only guardians can add guardians. The mock auth path just checks
    // that the current session matches the creator — added guardians
    // logging in don't go through the same path here since the mock
    // keeps `state.guardian` as the creator singleton.
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      // Also accept a session belonging to an additional guardian.
      const addlMatch = state.additionalGuardians.some(
        (g) => g.accountId === state.lastAuthAccountId && g.isActive,
      );
      if (!addlMatch) {
        throw { kind: 'unauthorized', message: 'only guardians can add other guardians' };
      }
    }
    const displayName = (input.displayName ?? '').trim();
    if (!displayName) {
      throw { kind: 'validation', message: 'guardian display name is required' };
    }
    if ((input.password ?? '').length < 10) {
      throw { kind: 'validation', message: 'password must be at least 10 characters' };
    }
    const activeChildIds = state.children
      .map((child) => child.accountId)
      .sort((left, right) => left - right);
    const relationshipChildIds = (input.childRelationships ?? [])
      .map((relationship) => relationship.childAccountId)
      .filter((childId): childId is number => childId !== undefined)
      .sort((left, right) => left - right);
    if (
      relationshipChildIds.length !== new Set(relationshipChildIds).size ||
      relationshipChildIds.length !== activeChildIds.length ||
      relationshipChildIds.some((childId, index) => childId !== activeChildIds[index])
    ) {
      throw {
        kind: 'validation',
        message: "choose the new guardian's relationship to every active child",
      };
    }
    const guardianTypeCodes = new Set([
      'parent',
      'step_parent',
      'grandparent',
      'aunt_uncle',
      'legal_guardian',
      'foster_parent',
      'sibling',
      'other',
    ]);
    if (
      (input.childRelationships ?? []).some(
        (relationship) =>
          !relationship.guardianTypeCode || !guardianTypeCodes.has(relationship.guardianTypeCode),
      )
    ) {
      throw { kind: 'validation', message: 'unknown guardian type' };
    }
    // Duplicate-name check mirrors the UNIQUE constraint.
    const clash =
      state.guardian?.displayName === displayName ||
      state.children.some((c) => c.displayName === displayName) ||
      state.additionalGuardians.some((g) => g.displayName === displayName);
    if (clash) {
      throw {
        kind: 'conflict',
        message: `an account named '${displayName}' already exists`,
      };
    }
    const accountId = state.nextAccountId++;
    state.additionalGuardians.push({
      accountId,
      displayName,
      displayCurrencyCode: input.displayCurrencyCode ?? 'GBP',
      isActive: true,
      deletedAtUtc: undefined,
      purgeAfterUtc: undefined,
    });
    safeLog('info', '[dev-mock] guardian_add OK', { accountId, displayName });
    return { accountId, displayName };
  },

  guardian_deactivate: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; guardianAccountId?: number };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }

    const targetId = input.guardianAccountId;
    if (targetId == null) {
      throw { kind: 'validation', message: 'guardianAccountId is required' };
    }

    // Identify who's asking.
    const actorIsCreator = !!state.guardian && state.lastAuthAccountId === state.guardian.accountId;
    const actorIsAddl = state.additionalGuardians.some(
      (g) => g.accountId === state.lastAuthAccountId && g.isActive,
    );
    if (!actorIsCreator && !actorIsAddl) {
      throw { kind: 'unauthorized', message: 'only guardians can manage guardian accounts' };
    }

    // Family creator target: refuse.
    if (state.guardian && targetId === state.guardian.accountId) {
      return { outcome: 'cannot_remove_family_creator' };
    }

    // Target must be an additional guardian we know about.
    const addl = state.additionalGuardians.find((g) => g.accountId === targetId);
    if (!addl) {
      throw { kind: 'not_found', message: 'guardian not found' };
    }
    if (!addl.isActive) {
      if (!addl.deletedAtUtc || !addl.purgeAfterUtc) {
        throw { kind: 'conflict', message: 'guardian was removed before recovery was available' };
      }
      return {
        outcome: 'success',
        guardianAccountId: targetId,
        deletedAtUtc: addl.deletedAtUtc,
        purgeAfterUtc: addl.purgeAfterUtc,
      };
    }

    // Non-creator actors can only self-remove.
    const isSelf = state.lastAuthAccountId === targetId;
    if (!isSelf && !actorIsCreator) {
      return { outcome: 'not_authorized' };
    }

    // Last-remaining-guardian check.
    const activeCount =
      (state.guardian ? 1 : 0) + state.additionalGuardians.filter((g) => g.isActive).length;
    if (activeCount <= 1) {
      return { outcome: 'last_remaining_guardian' };
    }

    const deletedAtUtc = new Date().toISOString();
    const purgeAfterUtc = new Date(Date.now() + 30 * 24 * 60 * 60 * 1_000).toISOString();
    addl.isActive = false;
    addl.deletedAtUtc = deletedAtUtc;
    addl.purgeAfterUtc = purgeAfterUtc;
    safeLog('info', '[dev-mock] guardian_deactivate OK', {
      guardianAccountId: targetId,
      isSelf,
    });
    return { outcome: 'success', guardianAccountId: targetId, deletedAtUtc, purgeAfterUtc };
  },

  guardian_recovery_list: async (payload) => {
    const input = (payload as { input?: { sessionId?: string } })?.input;
    if (!input?.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only the family creator can manage deleted guardian accounts',
      };
    }
    return state.additionalGuardians
      .filter(
        (guardian) =>
          !guardian.isActive &&
          guardian.deletedAtUtc &&
          guardian.purgeAfterUtc &&
          guardian.purgeAfterUtc > new Date().toISOString(),
      )
      .map((guardian) => ({
        guardianAccountId: guardian.accountId,
        displayName: guardian.displayName,
        deletedAtUtc: guardian.deletedAtUtc,
        purgeAfterUtc: guardian.purgeAfterUtc,
      }));
  },

  guardian_restore: async (payload) => {
    const input = (
      payload as {
        input?: {
          sessionId?: string;
          guardianAccountId?: number;
          displayName?: string;
          newPassword?: string;
        };
      }
    )?.input;
    if (!input?.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only the family creator can manage deleted guardian accounts',
      };
    }
    const guardian = state.additionalGuardians.find(
      (candidate) => candidate.accountId === input.guardianAccountId && !candidate.isActive,
    );
    if (
      !guardian ||
      !guardian.purgeAfterUtc ||
      guardian.purgeAfterUtc <= new Date().toISOString()
    ) {
      throw { kind: 'not_found', message: 'deleted guardian account not found' };
    }
    if (!(input.displayName ?? '').trim()) {
      throw { kind: 'validation', message: 'guardian display name is required' };
    }
    if ((input.newPassword ?? '').length < 10) {
      throw { kind: 'validation', message: 'password must be at least 10 characters' };
    }
    guardian.displayName = input.displayName!.trim();
    guardian.isActive = true;
    guardian.deletedAtUtc = undefined;
    guardian.purgeAfterUtc = undefined;
    return { guardianAccountId: guardian.accountId, displayName: guardian.displayName };
  },

  // US-SETUP-05 — change the display currency on an account.
  // Guardian-only; cross-family target rejected. Same-code →
  // Conflict; unknown code → Validation. Mock validates the code
  // against the same hard-coded list used by AddChild.
  account_currency_update: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; accountId?: number; newCurrencyCode?: string };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can change an account currency',
      };
    }
    const targetId = input.accountId;
    const newCode = (input.newCurrencyCode ?? '').toUpperCase();
    const validCodes = new Set([
      'GBP',
      'USD',
      'EUR',
      'CAD',
      'AUD',
      'JPY',
      'CHF',
      'SEK',
      'NOK',
      'DKK',
    ]);
    if (!validCodes.has(newCode)) {
      throw {
        kind: 'validation',
        message: `currency code '${input.newCurrencyCode}' is not recognised`,
      };
    }
    // Locate the target — could be the calling guardian themselves,
    // a linked child, or a linked additional guardian.
    if (state.guardian.accountId === targetId) {
      if (state.guardian.displayCurrencyCode === newCode) {
        throw { kind: 'conflict', message: 'account already uses that currency' };
      }
      state.guardian.displayCurrencyCode = newCode;
      safeLog('info', '[dev-mock] account_currency_update OK (self guardian)', {
        accountId: targetId,
        newCode,
      });
      return null;
    }
    const childIdx = state.children.findIndex((c) => c.accountId === targetId);
    if (childIdx >= 0) {
      const child = state.children[childIdx];
      if (!child) {
        throw { kind: 'not_found', message: `account ${targetId} not found` };
      }
      if (child.displayCurrencyCode === newCode) {
        throw { kind: 'conflict', message: 'account already uses that currency' };
      }
      child.displayCurrencyCode = newCode;
      safeLog('info', '[dev-mock] account_currency_update OK (child)', {
        accountId: targetId,
        newCode,
      });
      return null;
    }
    const addlIdx = state.additionalGuardians.findIndex((g) => g.accountId === targetId);
    if (addlIdx >= 0) {
      const addl = state.additionalGuardians[addlIdx];
      if (!addl) {
        throw { kind: 'not_found', message: `account ${targetId} not found` };
      }
      if (addl.displayCurrencyCode === newCode) {
        throw { kind: 'conflict', message: 'account already uses that currency' };
      }
      addl.displayCurrencyCode = newCode;
      safeLog('info', '[dev-mock] account_currency_update OK (additional guardian)', {
        accountId: targetId,
        newCode,
      });
      return null;
    }
    throw { kind: 'not_found', message: `account ${targetId} not found` };
  },

  buckets_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const requested = p?.input?.sessionId;
    if (!requested || state.activeSessionId !== requested) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    // Build a name lookup so each bucket can carry its child's display name.
    const nameOf = (childId: number): string => {
      if (state.guardian?.accountId === childId) return state.guardian.displayName;
      return (
        state.children.find((c) => c.accountId === childId)?.displayName ?? `Child #${childId}`
      );
    };
    const enrich = (b: Bucket) => ({
      bucketId: b.bucketId,
      childAccountId: b.childAccountId,
      childDisplayName: nameOf(b.childAccountId),
      bucketName: b.bucketName,
      allocationBps: b.allocationBps,
      isGuardianManaged: b.isGuardianManaged,
      requiresApprovalToWithdraw: b.requiresApprovalToWithdraw,
      percentIsFixed: b.percentIsFixed,
      balanceMinorUnits: b.balanceMinorUnits,
      displayColor: b.displayColor,
      sortOrder: b.sortOrder,
      // US-INTEREST-01 — surface the four interest fields so the
      // EditBucketScreen can seed the form with current values.
      interestEnabled: b.interestEnabled,
      interestRateBps: b.interestRateBps,
      interestPeriodCode: b.interestPeriodCode,
      interestActuallyCredits: b.interestActuallyCredits,
    });
    const allBuckets = state.buckets.slice().sort((a, b) => {
      const an = nameOf(a.childAccountId);
      const bn = nameOf(b.childAccountId);
      return an.localeCompare(bn) || a.sortOrder - b.sortOrder;
    });

    if (state.lastAuthAccountId === null) return [];
    if (state.guardian && state.lastAuthAccountId === state.guardian.accountId) {
      return allBuckets.map(enrich);
    }
    return allBuckets.filter((b) => b.childAccountId === state.lastAuthAccountId).map(enrich);
  },

  // US-INTEREST-02 — synthesize a small history of recent
  // accruals based on the bucket's CURRENT rate + balance + period.
  // Real backend reads from interest_accruals; the dev mock doesn't
  // simulate the full sweep, so we generate 4 plausible periods per
  // call. Auth: caller must own the bucket OR be the guardian.
  bucket_interest_accruals_list: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; bucketId?: number; limit?: number | null };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session' };
    }
    const bucket = state.buckets.find((b) => b.bucketId === input.bucketId && b.isActive);
    if (!bucket) {
      throw { kind: 'not_found', message: `bucket ${input.bucketId} not found` };
    }
    const isOwner = actorId === bucket.childAccountId;
    const isGuardian = state.guardian && actorId === state.guardian.accountId;
    if (!isOwner && !isGuardian) {
      throw {
        kind: 'unauthorized',
        message: "you cannot view this bucket's interest history",
      };
    }
    if (!bucket.interestEnabled || bucket.interestRateBps <= 0) {
      return [];
    }

    // Period in days for the synthesis. Mirrors interest_periods seed.
    const periodDays =
      bucket.interestPeriodCode === 'daily'
        ? 1
        : bucket.interestPeriodCode === 'weekly'
          ? 7
          : bucket.interestPeriodCode === 'monthly'
            ? 30
            : 365;
    const cap = Math.min(Math.max(input.limit ?? 12, 1), 100);
    const periods = Math.min(4, cap);
    const accruals = [] as Array<{
      interestAccrualId: number;
      periodStartAtUtc: string;
      periodEndAtUtc: string;
      principalMinorUnits: number;
      interestMinorUnits: number;
      rateBpsSnapshot: number;
      wasCredited: boolean;
    }>;
    const ms = (d: number) => d * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let principal = bucket.balanceMinorUnits;
    for (let i = 0; i < periods; i++) {
      const interest = Math.floor((principal * bucket.interestRateBps) / 10_000);
      if (interest <= 0) break;
      const endMs = now - ms(periodDays * i);
      const startMs = endMs - ms(periodDays);
      accruals.push({
        interestAccrualId: i + 1,
        periodStartAtUtc: new Date(startMs).toISOString(),
        periodEndAtUtc: new Date(endMs).toISOString(),
        principalMinorUnits: principal,
        interestMinorUnits: interest,
        rateBpsSnapshot: bucket.interestRateBps,
        wasCredited: bucket.interestActuallyCredits,
      });
      // Pretend the previous period started with a slightly lower
      // principal so the history reads naturally.
      principal = Math.max(0, principal - interest);
    }
    return accruals;
  },

  bucket_add: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        childAccountId?: number;
        bucketName?: string;
        displayColor?: string | null;
        allocationBps?: number;
        isGuardianManaged?: boolean;
        requiresApprovalToWithdraw?: boolean;
        percentIsFixed?: boolean;
      };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }

    // --- Auth ---
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }

    const childId = input.childAccountId ?? -1;
    const actorIsGuardian = !!state.guardian && state.guardian.accountId === actorId;
    const actorIsChildSelf = !actorIsGuardian && actorId === childId;

    // --- Permission gating ---
    if (actorIsChildSelf) {
      if (input.isGuardianManaged) {
        throw { kind: 'validation', message: 'children cannot create guardian-managed buckets' };
      }
      if (input.percentIsFixed) {
        throw {
          kind: 'validation',
          message: "children cannot create fixed-percentage buckets (that's a guardian privilege)",
        };
      }
    } else if (actorIsGuardian) {
      // Guardian must be linked to child — in the mock, guardian is linked to
      // everyone in `state.children`. If the child is neither the guardian
      // themselves nor in the children list, return NotFound.
      const linked = state.children.some((c) => c.accountId === childId);
      if (!linked) {
        throw {
          kind: 'not_found',
          message: `child account ${childId} not linked to this guardian`,
        };
      }
    } else {
      throw {
        kind: 'unauthorized',
        message: 'only guardians or the child themselves can add buckets',
      };
    }

    // --- Field validation ---
    const trimmedName = (input.bucketName ?? '').trim();
    if (trimmedName.length === 0) {
      throw { kind: 'validation', message: 'bucket name cannot be empty' };
    }
    if ([...trimmedName].length > 50) {
      throw { kind: 'validation', message: 'bucket name must be 50 characters or fewer' };
    }
    const bps = input.allocationBps ?? 0;
    if (bps <= 0 || bps >= 10000) {
      throw {
        kind: 'validation',
        message: `bucket percentage must be between 1 and 9999 bps (got ${bps})`,
      };
    }
    if (input.displayColor !== null && input.displayColor !== undefined) {
      if (!/^#[0-9a-fA-F]{6}$/.test(input.displayColor)) {
        throw { kind: 'validation', message: 'display_color must be #rrggbb hex format' };
      }
    }

    // --- Unique-name check (active or inactive — the DB UNIQUE constraint is
    //     on (child_account_id, bucket_name) regardless of is_active) ---
    const nameTaken = state.buckets.some(
      (b) =>
        b.childAccountId === childId && b.bucketName.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (nameTaken) {
      throw {
        kind: 'conflict',
        message: `a bucket named '${trimmedName}' already exists for this child`,
      };
    }

    // --- Rebalance ---
    const existing = state.buckets
      .filter((b) => b.childAccountId === childId)
      .map((b) => ({
        bucketId: b.bucketId,
        allocationBps: b.allocationBps,
        percentIsFixed: b.percentIsFixed,
        sortOrder: b.sortOrder,
        priority: b.sortOrder + 1,
      }));

    const rebalance = rebalanceForNewBucket(existing, bps);
    if (!rebalance.ok) {
      switch (rebalance.error.kind) {
        case 'newBucketBpsOutOfRange':
          throw {
            kind: 'validation',
            message: `bucket percentage must be between 1 and 9999 bps (got ${rebalance.error.bps})`,
          };
        case 'currentAllocationsDoNotSumTo10000':
          throw {
            kind: 'internal',
            message: `child's current bucket allocations sum to ${rebalance.error.actual} bps, not 10000; data inconsistency`,
          };
        case 'noFlexibleBuckets':
          throw {
            kind: 'validation',
            message:
              'no flexible buckets available. Edit an existing bucket first (make it flexible), then add.',
          };
        case 'flexiblePoolTooSmall':
          throw {
            kind: 'validation',
            message: `flexible pool is ${rebalance.error.availableBps} bps but you asked for ${rebalance.error.requestedBps}. Reduce a fixed bucket's percentage first (edit a bucket).`,
          };
      }
    }

    // --- Apply shrinks ---
    for (const rr of rebalance.value) {
      const b = state.buckets.find((b) => b.bucketId === rr.bucketId);
      if (b) b.allocationBps = rr.newAllocationBps;
    }

    // --- Insert new bucket ---
    const nextSortOrder =
      Math.max(
        -1,
        ...state.buckets.filter((b) => b.childAccountId === childId).map((b) => b.sortOrder),
      ) + 1;
    const newBucket: Bucket = {
      bucketId: state.nextBucketId++,
      childAccountId: childId,
      bucketName: trimmedName,
      allocationBps: bps,
      isGuardianManaged: !!input.isGuardianManaged,
      requiresApprovalToWithdraw: !!input.requiresApprovalToWithdraw,
      percentIsFixed: !!input.percentIsFixed,
      balanceMinorUnits: 0,
      displayColor: input.displayColor ?? null,
      sortOrder: nextSortOrder,
      isActive: true,
      // US-INTEREST-01 — new buckets default to interest disabled.
      // Guardian flips it on later via bucket_update.
      interestEnabled: false,
      interestRateBps: 0,
      interestPeriodCode: null,
      interestActuallyCredits: false,
    };
    state.buckets.push(newBucket);

    safeLog('info', '[dev-mock] bucket_add OK', {
      newBucketId: newBucket.bucketId,
      childId,
      allocationBps: bps,
      percentIsFixed: newBucket.percentIsFixed,
    });

    return {
      newBucketId: newBucket.bucketId,
      flexibleRebalance: rebalance.value.map((rr) => ({
        bucketId: rr.bucketId,
        newAllocationBps: rr.newAllocationBps,
      })),
    };
  },

  bucket_update: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        bucketId?: number;
        bucketName?: string;
        displayColor?: string | null;
        requiresApprovalToWithdraw?: boolean;
        allocationBps?: number;
        percentIsFixed?: boolean;
        // BUG-024: same shape as the wire-level BucketUpdateInput in
        // packages/core/src/client. Optional; absent leaves alone, set
        // flips. Children submitting it are rejected by the child-self
        // gate further down (parallel to percent_is_fixed).
        isGuardianManaged?: boolean;
        // US-INTEREST-01 — guardian-only interest config. Children
        // submitting any of these are rejected by the gate added in
        // the child-self block below.
        interestEnabled?: boolean;
        interestRateBps?: number;
        interestPeriodCode?: 'daily' | 'weekly' | 'monthly' | 'yearly' | null;
        interestActuallyCredits?: boolean;
      };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }

    // Auth.
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorIsGuardian = !!state.guardian && state.guardian.accountId === actorId;
    const actorIsChild = !actorIsGuardian && state.children.some((c) => c.accountId === actorId);
    if (!actorIsGuardian && !actorIsChild) {
      throw {
        kind: 'unauthorized',
        message: "only guardians or the bucket's child owner can edit a bucket",
      };
    }

    // Find target bucket.
    const bucketId = input.bucketId ?? -1;
    const target = state.buckets.find((b) => b.bucketId === bucketId);
    if (!target) {
      throw {
        kind: 'not_found',
        message: `bucket ${bucketId} not found or not active`,
      };
    }

    // Path-specific gates (mirror commands::buckets::bucket_update).
    if (actorIsGuardian) {
      if (!target.isGuardianManaged) {
        throw {
          kind: 'validation',
          message: 'this bucket is managed by the child and cannot be edited by a guardian',
        };
      }
    } else {
      // Child-self path (US-BUCKET-08).
      if (target.childAccountId !== actorId) {
        throw {
          kind: 'not_found',
          message: `bucket ${bucketId} not found or not accessible`,
        };
      }
      if (target.isGuardianManaged) {
        throw {
          kind: 'unauthorized',
          message: 'this bucket is guardian-managed and you cannot edit it',
        };
      }
      if (input.percentIsFixed !== undefined) {
        throw {
          kind: 'validation',
          message: 'you cannot change the fixed-percentage flag on your own buckets',
        };
      }
      // BUG-024: same gate for is_guardian_managed on the child-self path.
      if (input.isGuardianManaged !== undefined) {
        throw {
          kind: 'validation',
          message: 'you cannot change the guardian-managed flag on your own buckets',
        };
      }
      // US-INTEREST-01: interest config is guardian-only. Children
      // submitting any of the four fields are rejected here, parallel
      // to the backend gate in db::buckets::update_bucket.
      if (
        input.interestEnabled !== undefined ||
        input.interestRateBps !== undefined ||
        input.interestPeriodCode !== undefined ||
        input.interestActuallyCredits !== undefined
      ) {
        throw {
          kind: 'validation',
          message: 'interest can only be configured by a guardian',
        };
      }
    }

    // Resolve new values.
    const newName = input.bucketName !== undefined ? input.bucketName.trim() : target.bucketName;
    if (newName.length === 0) {
      throw { kind: 'validation', message: 'bucket name cannot be empty' };
    }
    if ([...newName].length > 50) {
      throw { kind: 'validation', message: 'bucket name must be 50 characters or fewer' };
    }

    const newColor = input.displayColor === undefined ? target.displayColor : input.displayColor; // may be null = clear
    if (newColor !== null && newColor !== undefined) {
      if (!/^#[0-9a-fA-F]{6}$/.test(newColor)) {
        throw { kind: 'validation', message: 'display_color must be #rrggbb hex format' };
      }
    }
    const newRequiresApproval =
      input.requiresApprovalToWithdraw ?? target.requiresApprovalToWithdraw;
    const newBps = input.allocationBps ?? target.allocationBps;
    if (newBps <= 0 || newBps >= 10000) {
      throw {
        kind: 'validation',
        message: `bucket percentage must be between 1 and 9999 bps (got ${newBps})`,
      };
    }
    const newFixed = input.percentIsFixed ?? target.percentIsFixed;
    // BUG-024: parallel to newFixed — fold the optional flag into the
    // effective new value, defaulting to the bucket's current state.
    const newGuardianManaged = input.isGuardianManaged ?? target.isGuardianManaged;
    // US-INTEREST-01: derive the four new interest values. None on each
    // means leave alone; a present value applies. Same Some/None shape
    // as the backend Rust code uses. interestPeriodCode null means
    // "clear it"; absent means "leave alone" — JS undefined-vs-null
    // gives that distinction natively.
    const newInterestEnabled = input.interestEnabled ?? target.interestEnabled;
    const newInterestRateBps = input.interestRateBps ?? target.interestRateBps;
    if (newInterestRateBps < 0) {
      throw { kind: 'validation', message: 'interest rate cannot be negative' };
    }
    if (newInterestRateBps > 100_000) {
      throw {
        kind: 'validation',
        message: `interest rate must be ≤ 100000 bps (got ${newInterestRateBps})`,
      };
    }
    const newInterestPeriodCode =
      input.interestPeriodCode === undefined ? target.interestPeriodCode : input.interestPeriodCode;
    const newInterestActuallyCredits =
      input.interestActuallyCredits ?? target.interestActuallyCredits;

    // Uniqueness — a rename must not collide with any OTHER bucket of the same child.
    if (newName.toLowerCase() !== target.bucketName.toLowerCase()) {
      const collision = state.buckets.some(
        (b) =>
          b.childAccountId === target.childAccountId &&
          b.bucketId !== target.bucketId &&
          b.bucketName.toLowerCase() === newName.toLowerCase(),
      );
      if (collision) {
        throw {
          kind: 'conflict',
          message: `a bucket named '${newName}' already exists for this child`,
        };
      }
    }

    // Build existing layout for the rebalance math.
    const existing = state.buckets
      .filter((b) => b.childAccountId === target.childAccountId)
      .map((b) => ({
        bucketId: b.bucketId,
        allocationBps: b.allocationBps,
        percentIsFixed: b.percentIsFixed,
        sortOrder: b.sortOrder,
        priority: b.sortOrder + 1,
      }));

    const rebalance = rebalanceForEditedBucket(existing, bucketId, newBps);
    if (!rebalance.ok) {
      switch (rebalance.error.kind) {
        case 'newBucketBpsOutOfRange':
          throw {
            kind: 'validation',
            message: `bucket percentage must be between 1 and 9999 bps (got ${rebalance.error.bps})`,
          };
        case 'currentAllocationsDoNotSumTo10000':
          throw {
            kind: 'internal',
            message: `child's current bucket allocations sum to ${rebalance.error.actual} bps, not 10000; data inconsistency`,
          };
        case 'noFlexibleBuckets':
          throw {
            kind: 'validation',
            message:
              'no other flexible buckets available to absorb this change. Edit a different bucket first (make it flexible), then try again.',
          };
        case 'flexiblePoolTooSmall':
          throw {
            kind: 'validation',
            message: `requested ${rebalance.error.requestedBps} bps, but after other fixed buckets are deducted only ${rebalance.error.availableBps} bps remain for this one. Reduce a different fixed bucket first.`,
          };
      }
    }

    // Apply rebalance to others.
    for (const rr of rebalance.value) {
      const sibling = state.buckets.find((b) => b.bucketId === rr.bucketId);
      if (sibling) sibling.allocationBps = rr.newAllocationBps;
    }

    // Apply edit.
    target.bucketName = newName;
    target.displayColor = newColor ?? null;
    target.requiresApprovalToWithdraw = newRequiresApproval;
    target.allocationBps = newBps;
    target.percentIsFixed = newFixed;
    // BUG-024: persist the guardian-managed flip, same shape as the
    // other flag updates above. Already gated on the child-self path
    // above so reaching this point with a child actor + a flag value
    // is not possible.
    target.isGuardianManaged = newGuardianManaged;
    // US-INTEREST-01: persist the four interest fields. Same defence-
    // in-depth: the child-self path is gated above, so reaching here
    // with a child actor + interest fields is not possible.
    target.interestEnabled = newInterestEnabled;
    target.interestRateBps = newInterestRateBps;
    target.interestPeriodCode = newInterestPeriodCode;
    target.interestActuallyCredits = newInterestActuallyCredits;

    safeLog('info', '[dev-mock] bucket_update OK', {
      bucketId,
      newBps,
      newFixed,
      newGuardianManaged,
      newInterestEnabled,
    });

    return {
      bucketId,
      flexibleRebalance: rebalance.value.map((rr) => ({
        bucketId: rr.bucketId,
        newAllocationBps: rr.newAllocationBps,
      })),
    };
  },

  auth_login: async (payload) => {
    const p = payload as { input?: { accountId?: number; password?: string } };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    const accountId = input.accountId ?? -1;
    const password = input.password ?? '';
    const now = Date.now();

    // ---- Lockout pre-check (REQ-9.6) ----
    const lockoutUntil = state.lockoutExpiresAtMs.get(accountId);
    if (lockoutUntil && lockoutUntil > now) {
      const retrySec = Math.ceil((lockoutUntil - now) / 1000);
      safeLog('info', `[dev-mock] auth_login: account ${accountId} is locked for ${retrySec}s`);
      return {
        outcome: 'locked',
        retryAfterSeconds: retrySec,
        unlocksAtUtc: new Date(lockoutUntil).toISOString(),
      };
    }
    // Lockout expired — clear it.
    if (lockoutUntil) state.lockoutExpiresAtMs.delete(accountId);

    // REQ-9.5: same generic error for wrong-password / missing / inactive.
    const failure = {
      kind: 'unauthorized',
      message: 'incorrect password or account is no longer available',
    };

    // ---- Look up the account (guardian or child) ----
    type Acct = { accountId: number; password: string };
    let acct: Acct | null = null;
    if (state.guardian && state.guardian.accountId === accountId) {
      acct = { accountId: state.guardian.accountId, password: state.guardian.password };
    } else {
      const child = state.children.find((c) => c.accountId === accountId);
      if (child) acct = { accountId: child.accountId, password: child.password };
    }
    if (!acct) {
      // Don't count failures for non-existent accounts (anti-DoS, matches
      // backend behaviour).
      throw failure;
    }

    // ---- Password check ----
    if (acct.password !== password) {
      // Record failure in rolling window.
      const stamps = (state.failureTimestampsMs.get(accountId) ?? []).filter(
        (t) => now - t < FAILURE_WINDOW_MS,
      );
      stamps.push(now);
      state.failureTimestampsMs.set(accountId, stamps);

      if (stamps.length >= FAILURE_THRESHOLD) {
        const expiresAt = now + LOCKOUT_DURATION_MS;
        state.lockoutExpiresAtMs.set(accountId, expiresAt);
        const child = state.children.find((entry) => entry.accountId === accountId);
        const existingAlert = state.loginSecurityAlerts.some(
          (entry) =>
            entry.targetAccountId === accountId &&
            entry.credentialKind === 'password' &&
            new Date(entry.lockoutExpiresAtUtc).getTime() > now,
        );
        if (!existingAlert) {
          state.loginSecurityAlerts.push({
            loginSecurityAlertId: state.nextLoginSecurityAlertId++,
            targetAccountId: accountId,
            targetDisplayName: child?.displayName ?? state.guardian?.displayName ?? 'Guardian',
            targetRoleCode: child ? 'child' : 'guardian',
            credentialKind: 'password',
            lockedAtUtc: new Date(now).toISOString(),
            lockoutExpiresAtUtc: new Date(expiresAt).toISOString(),
          });
        }
        safeLog(
          'info',
          `[dev-mock] auth_login: locking account ${accountId} until ${new Date(expiresAt).toISOString()}`,
        );
        return {
          outcome: 'locked',
          retryAfterSeconds: Math.ceil(LOCKOUT_DURATION_MS / 1000),
          unlocksAtUtc: new Date(expiresAt).toISOString(),
        };
      }
      throw failure;
    }

    // ---- Success ----
    state.failureTimestampsMs.delete(accountId);
    state.lockoutExpiresAtMs.delete(accountId);
    const sessionId = `dev-${Math.random().toString(36).slice(2)}-${now}`;
    const expiresAt = new Date(now + 15 * 60_000).toISOString();
    state.activeSessionId = sessionId;
    state.activeSessionExpiresAtUtc = expiresAt;
    state.lastAuthAccountId = accountId;
    safeLog('info', '[dev-mock] auth_login OK for account', accountId);
    return {
      outcome: 'success',
      sessionId,
      accountId,
      expiresAtUtc: expiresAt,
    };
  },

  auth_logout: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const requested = p?.input?.sessionId;
    const wasActive = state.activeSessionId === requested && state.activeSessionId !== null;
    if (wasActive) {
      state.activeSessionId = null;
      state.activeSessionExpiresAtUtc = null;
      state.lastAuthAccountId = null;
      safeLog('info', '[dev-mock] auth_logout OK; session invalidated');
    } else {
      safeLog('info', '[dev-mock] auth_logout: no matching session');
    }
    return { sessionWasActive: wasActive };
  },

  session_touch: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const requested = p?.input?.sessionId;
    if (!requested || state.activeSessionId !== requested) {
      return { outcome: 'expired' };
    }
    const current = state.activeSessionExpiresAtUtc
      ? new Date(state.activeSessionExpiresAtUtc).getTime()
      : 0;
    if (current <= Date.now()) {
      state.activeSessionId = null;
      state.activeSessionExpiresAtUtc = null;
      return { outcome: 'expired' };
    }
    const newExpiry = new Date(Date.now() + 15 * 60_000).toISOString();
    state.activeSessionExpiresAtUtc = newExpiry;
    return { outcome: 'alive', expiresAtUtc: newExpiry };
  },

  // US-BUCKET-05 — deactivate bucket (guardian-managed). Mirrors the
  // backend contract so Playwright sees representative behaviour: zero
  // balance deactivates cleanly, non-zero balance requires a target, and
  // the target must be another active guardian-managed bucket for the
  // same child.
  bucket_deactivate: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; bucketId?: number; targetBucketId?: number | null };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.guardian.accountId !== actorId) {
      throw { kind: 'unauthorized', message: 'only guardians can deactivate buckets' };
    }

    const bucketId = input.bucketId ?? -1;
    const src = state.buckets.find((b) => b.bucketId === bucketId);
    if (!src) {
      throw { kind: 'not_found', message: `bucket ${bucketId} not found` };
    }
    if (!src.isActive) {
      throw { kind: 'conflict', message: 'bucket is already deactivated' };
    }
    if (!src.isGuardianManaged) {
      throw {
        kind: 'validation',
        message: 'this bucket is managed by the child and cannot be deactivated by a guardian',
      };
    }

    const srcBalance = src.balanceMinorUnits;

    // Single-bucket-per-child guard.
    const siblingsActive = state.buckets.filter(
      (b) => b.childAccountId === src.childAccountId && b.isActive && b.bucketId !== bucketId,
    );
    if (siblingsActive.length === 0) {
      throw { kind: 'validation', message: 'cannot deactivate the only active bucket' };
    }

    // Non-zero balance path needs a valid target.
    let transferId: number | null = null;
    let moved = 0;
    if (srcBalance > 0) {
      const tgtId = input.targetBucketId ?? null;
      if (tgtId === null) {
        throw {
          kind: 'validation',
          message: `bucket ${bucketId} has a balance — pick a target bucket to receive it`,
        };
      }
      if (tgtId === bucketId) {
        throw {
          kind: 'validation',
          message: 'target bucket must be different from the bucket being deactivated',
        };
      }
      const tgt = state.buckets.find((b) => b.bucketId === tgtId);
      if (!tgt) {
        throw { kind: 'not_found', message: `target bucket ${tgtId} not found` };
      }
      if (tgt.childAccountId !== src.childAccountId) {
        throw { kind: 'validation', message: 'target bucket must belong to the same child' };
      }
      if (!tgt.isActive) {
        throw { kind: 'validation', message: 'target bucket is not active' };
      }
      if (!tgt.isGuardianManaged) {
        throw { kind: 'validation', message: 'target bucket must be guardian-managed' };
      }
      tgt.balanceMinorUnits += srcBalance;
      src.balanceMinorUnits = 0;
      moved = srcBalance;
      transferId = Math.floor(Math.random() * 100000) + 1;
    }

    // Redistribute source's bps across remaining flex buckets in the child.
    const releasedBps = src.allocationBps;
    src.isActive = false;
    src.allocationBps = 0;

    const remainingFlex = siblingsActive.filter((b) => !b.percentIsFixed);
    const rebalance: { bucketId: number; newAllocationBps: number }[] = [];
    if (remainingFlex.length > 0 && releasedBps > 0) {
      const flexTotal = remainingFlex.reduce((acc, b) => acc + b.allocationBps, 0);
      // Add releasedBps proportionally; fix rounding on the largest bucket.
      let distributed = 0;
      for (let i = 0; i < remainingFlex.length - 1; i += 1) {
        const b = remainingFlex[i];
        if (!b) continue;
        const add = Math.round((releasedBps * b.allocationBps) / flexTotal);
        b.allocationBps += add;
        distributed += add;
        rebalance.push({ bucketId: b.bucketId, newAllocationBps: b.allocationBps });
      }
      const last = remainingFlex[remainingFlex.length - 1];
      if (last) {
        last.allocationBps += releasedBps - distributed;
        rebalance.push({ bucketId: last.bucketId, newAllocationBps: last.allocationBps });
      }
    }

    safeLog('info', '[dev-mock] bucket_deactivate', { bucketId, moved, transferId });
    return {
      bucketId,
      flexibleRebalance: rebalance,
      movedAmountMinorUnits: moved,
      transferId,
    };
  },

  // ---------------------------------------------------------------------------
  // US-BACKUP-01 / US-BACKUP-02 mocks
  //
  // The real backend does crypto + filesystem I/O neither of which apply in a
  // browser sandbox. We mock outcomes based on sentinel password values so
  // Playwright tests can exercise each branch:
  //   - backupPassword "weak"      → weak_password
  //   - backupPassword "wrong"     → wrong_backup_password (restore only)
  //   - loginPassword  "wrong"     → wrong_login_password (restore only)
  //   - archivePath    containing "newer"  → backup_newer_than_app
  //   - archivePath    containing "bad"    → archive_malformed
  //   - otherwise                  → success
  // ---------------------------------------------------------------------------

  backup_export: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        outputPath?: string;
        backupPassword?: string;
      };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw {
        kind: 'unauthorized',
        message: 'no active session — please sign in again',
      };
    }
    const password = input.backupPassword ?? '';
    if (password.length < 10) {
      return { outcome: 'weak_password', minLength: 10 };
    }
    const outputPath = input.outputPath ?? '';
    if (!outputPath) {
      throw { kind: 'validation', message: 'output path is required' };
    }
    return {
      outcome: 'success',
      filePath: outputPath,
      // Pretend the archive is ~200KB — a plausible size for a small
      // family's encrypted DB. Tests just care that a number comes back.
      sizeBytes: 210_944,
    };
  },

  backup_restore: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        archivePath?: string;
        backupPassword?: string;
        loginPassword?: string;
      };
    };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw {
        kind: 'unauthorized',
        message: 'no active session — please sign in again',
      };
    }
    const archivePath = input.archivePath ?? '';
    if (!archivePath) {
      throw { kind: 'validation', message: 'archive path is required' };
    }
    if ((input.loginPassword ?? '') === 'wrong') {
      return { outcome: 'wrong_login_password' };
    }
    if (archivePath.includes('bad')) {
      return {
        outcome: 'archive_malformed',
        reason: 'file is not a valid zip archive',
      };
    }
    if (archivePath.includes('newer')) {
      return {
        outcome: 'backup_newer_than_app',
        backupSchemaVersion: 99,
        appSchemaVersion: 3,
      };
    }
    if ((input.backupPassword ?? '') === 'wrong') {
      return { outcome: 'wrong_backup_password' };
    }
    return { outcome: 'success', needsRestart: true };
  },

  backup_app_exit: async () => {
    // In dev there's nothing to exit; log and return success. The real
    // backend calls app.exit(0).
    safeLog('info', '[dev-mock] backup_app_exit — would exit app');
    return null;
  },

  // ---- US-AUTH-07 — PIN handlers ----

  pin_set: async (payload) => {
    const p = payload as { input?: { sessionId?: string; pin?: string } };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const pin = input.pin ?? '';
    if (!/^\d{6}$/.test(pin)) {
      throw { kind: 'validation', message: 'PIN must be exactly 6 numeric digits' };
    }
    const replaced = state.pinsByAccount.has(actorId);
    state.pinsByAccount.set(actorId, pin);
    state.pinFailuresByAccount.delete(actorId);
    state.pinLockoutsByAccount.delete(actorId);
    safeLog('info', '[dev-mock] pin_set OK', { actorId, replaced });
    return { replaced };
  },

  pin_remove: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const removed = state.pinsByAccount.delete(actorId);
    state.pinFailuresByAccount.delete(actorId);
    state.pinLockoutsByAccount.delete(actorId);
    safeLog('info', '[dev-mock] pin_remove', { actorId, removed });
    return { removed };
  },

  pin_status: async (payload) => {
    const p = payload as { input?: { accountId?: number } };
    const input = p?.input ?? {};
    const accountId = input.accountId ?? -1;
    return { hasPin: state.pinsByAccount.has(accountId) };
  },

  pin_status_for_session: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) {
      throw { kind: 'validation', message: 'Missing input payload.' };
    }
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    if (actorId === null) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    return { hasPin: state.pinsByAccount.has(actorId) };
  },

  pin_login: async (payload) => {
    const p = payload as { input?: { accountId?: number; pin?: string } };
    const input = p?.input ?? {};
    const accountId = input.accountId ?? -1;
    const pin = input.pin ?? '';
    const now = Date.now();

    // PIN not configured?
    const stored = state.pinsByAccount.get(accountId);
    if (stored === undefined) {
      return { outcome: 'not_configured' as const };
    }

    // Lockout check.
    const lockoutUntil = state.pinLockoutsByAccount.get(accountId);
    if (lockoutUntil !== undefined && lockoutUntil > now) {
      return {
        outcome: 'locked' as const,
        expiresAtUtc: new Date(lockoutUntil).toISOString(),
      };
    }
    if (lockoutUntil !== undefined) {
      // Expired — clear and let the attempt proceed.
      state.pinLockoutsByAccount.delete(accountId);
      state.pinFailuresByAccount.delete(accountId);
    }

    // Obvious-garbage fast path — don't leak format info.
    if (!/^\d{6}$/.test(pin)) {
      return { outcome: 'failed' as const, attemptsRemaining: -1 };
    }

    if (stored === pin) {
      // Success — mint a session and clear failure state.
      const sessionId = `mock-pin-session-${Math.random().toString(36).slice(2, 10)}`;
      const expiresAt = new Date(now + 15 * 60_000).toISOString();
      state.activeSessionId = sessionId;
      state.activeSessionExpiresAtUtc = expiresAt;
      state.lastAuthAccountId = accountId;
      state.pinFailuresByAccount.delete(accountId);
      state.pinLockoutsByAccount.delete(accountId);
      safeLog('info', '[dev-mock] pin_login success', { accountId });
      return {
        outcome: 'success' as const,
        sessionId,
        accountId,
        expiresAtUtc: expiresAt,
      };
    }

    // Miss — record and maybe lock.
    const prior = state.pinFailuresByAccount.get(accountId);
    let count: number;
    if (prior === undefined || now - prior.firstMissAtMs > 60_000) {
      // New window.
      count = 1;
      state.pinFailuresByAccount.set(accountId, { count, firstMissAtMs: now });
    } else {
      count = prior.count + 1;
      state.pinFailuresByAccount.set(accountId, { count, firstMissAtMs: prior.firstMissAtMs });
    }
    const threshold = FAILURE_THRESHOLD;
    if (count >= threshold) {
      const expiresAtMs = now + 5 * 60_000;
      state.pinLockoutsByAccount.set(accountId, expiresAtMs);
      const child = state.children.find((entry) => entry.accountId === accountId);
      const existingAlert = state.loginSecurityAlerts.some(
        (entry) =>
          entry.targetAccountId === accountId &&
          entry.credentialKind === 'pin' &&
          new Date(entry.lockoutExpiresAtUtc).getTime() > now,
      );
      if (!existingAlert) {
        state.loginSecurityAlerts.push({
          loginSecurityAlertId: state.nextLoginSecurityAlertId++,
          targetAccountId: accountId,
          targetDisplayName: child?.displayName ?? state.guardian?.displayName ?? 'Guardian',
          targetRoleCode: child ? 'child' : 'guardian',
          credentialKind: 'pin',
          lockedAtUtc: new Date(now).toISOString(),
          lockoutExpiresAtUtc: new Date(expiresAtMs).toISOString(),
        });
      }
      return {
        outcome: 'locked' as const,
        expiresAtUtc: new Date(expiresAtMs).toISOString(),
      };
    }
    return {
      outcome: 'failed' as const,
      attemptsRemaining: threshold - count,
    };
  },

  // US-FX-03 — list recent rates newest-first by effective_at_utc.
  exchange_rates_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string; limit?: number | null } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can manage exchange rates',
      };
    }
    const cap = Math.min(Math.max(input.limit ?? 50, 1), 200);
    const sorted = state.exchangeRates
      .slice()
      .sort((a, b) => {
        if (a.effectiveAtUtc !== b.effectiveAtUtc) {
          return b.effectiveAtUtc.localeCompare(a.effectiveAtUtc);
        }
        return b.exchangeRateId - a.exchangeRateId;
      })
      .slice(0, cap);
    return sorted;
  },

  // US-FX-03 — add a new rate. Validates rate > 0, codes are known,
  // codes differ. Append-only; no update or delete in MVP.
  exchange_rate_add: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        fromCurrencyCode?: string;
        toCurrencyCode?: string;
        ratePpm?: number;
        effectiveAtUtc?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can manage exchange rates',
      };
    }
    const validCodes = new Set([
      'GBP',
      'USD',
      'EUR',
      'CAD',
      'AUD',
      'JPY',
      'CHF',
      'SEK',
      'NOK',
      'DKK',
    ]);
    const fromCode = (input.fromCurrencyCode ?? '').toUpperCase();
    const toCode = (input.toCurrencyCode ?? '').toUpperCase();
    const rate = input.ratePpm ?? 0;
    const effective = (input.effectiveAtUtc ?? '').trim();
    if (rate <= 0) {
      throw { kind: 'validation', message: 'exchange rate must be positive' };
    }
    if (fromCode === toCode) {
      throw {
        kind: 'validation',
        message: 'from-currency and to-currency cannot be the same',
      };
    }
    if (!validCodes.has(fromCode)) {
      throw { kind: 'validation', message: `from-currency '${fromCode}' is not recognised` };
    }
    if (!validCodes.has(toCode)) {
      throw { kind: 'validation', message: `to-currency '${toCode}' is not recognised` };
    }
    if (effective === '') {
      throw { kind: 'validation', message: 'effective date is required' };
    }
    const id = state.nextExchangeRateId++;
    const createdAtUtc = new Date().toISOString();
    state.exchangeRates.push({
      exchangeRateId: id,
      fromCurrencyCode: fromCode,
      toCurrencyCode: toCode,
      ratePpm: rate,
      effectiveAtUtc: effective,
      enteredByDisplayName: state.guardian.displayName,
      createdAtUtc,
    });
    safeLog('info', '[dev-mock] exchange_rate_add OK', {
      exchangeRateId: id,
      fromCode,
      toCode,
      ratePpm: rate,
    });
    return { exchangeRateId: id };
  },

  // US-FX-03 AC #3 — pre-populate the deposit rate field. Returns
  // the most-recent effective rate for a from→to pair, or null
  // when no rate has been entered for it yet.
  exchange_rate_most_recent_for_pair: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; fromCurrencyCode?: string; toCurrencyCode?: string };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw {
        kind: 'unauthorized',
        message: 'only guardians can manage exchange rates',
      };
    }
    const fromCode = (input.fromCurrencyCode ?? '').toUpperCase();
    const toCode = (input.toCurrencyCode ?? '').toUpperCase();
    const matches = state.exchangeRates
      .filter((r) => r.fromCurrencyCode === fromCode && r.toCurrencyCode === toCode)
      .sort((a, b) => {
        if (a.effectiveAtUtc !== b.effectiveAtUtc) {
          return b.effectiveAtUtc.localeCompare(a.effectiveAtUtc);
        }
        return b.exchangeRateId - a.exchangeRateId;
      });
    return { ratePpm: matches[0]?.ratePpm ?? null };
  },

  // US-CHORE-08 — child browses the master list. Visibility is gated
  // by family membership: only chores created by the calling child's
  // guardian appear. Has-pending-claim is computed from current
  // pendingChoreClaims state.
  master_list_chores_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    const child = state.children.find((c) => c.accountId === actorId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'this action is only available to child accounts',
      };
    }
    const guardianId = state.guardian?.accountId ?? null;
    return Array.from(state.chores.values())
      .filter(
        (c) =>
          c.childAccountId === null &&
          c.isActive &&
          guardianId !== null &&
          c.createdByAccountId === guardianId,
      )
      .sort((a, b) => {
        if (a.createdAtUtc !== b.createdAtUtc) return b.createdAtUtc.localeCompare(a.createdAtUtc);
        return b.choreId - a.choreId;
      })
      .map((c) => ({
        choreId: c.choreId,
        choreName: c.choreName,
        choreDescription: c.choreDescription,
        choreTypeCode: c.choreTypeCode,
        choreTypeLabel: c.choreTypeLabel,
        zoneName: c.zoneName,
        rewardMinorUnits: c.rewardMinorUnits,
        recurrencePattern: c.recurrencePattern,
        requiresApprovalToClaim: c.requiresApprovalToClaim,
        hasPendingClaimFromMe: Array.from(state.pendingChoreClaims.values()).some(
          (pc) =>
            pc.choreId === c.choreId &&
            pc.requestingChildAccountId === child.accountId &&
            pc.status === 'pending',
        ),
      }));
  },

  // US-CHORE-08 — child picks a master-list chore. Immediate path
  // flips child_account_id; gated path creates a pending claim row.
  chore_pick: async (payload) => {
    const p = payload as { input?: { sessionId?: string; choreId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    const child = state.children.find((c) => c.accountId === actorId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'this action is only available to child accounts',
      };
    }
    const chore = state.chores.get(input.choreId ?? -1);
    if (!chore) {
      throw { kind: 'not_found', message: `chore ${input.choreId} not found` };
    }
    if (!chore.isActive) {
      throw { kind: 'conflict', message: `chore ${chore.choreId} is no longer active` };
    }
    if (chore.childAccountId !== null) {
      throw { kind: 'conflict', message: `chore ${chore.choreId} is already assigned` };
    }
    if (chore.createdByAccountId !== state.guardian?.accountId) {
      throw { kind: 'not_found', message: `chore ${chore.choreId} not found` };
    }

    if (!chore.requiresApprovalToClaim) {
      chore.childAccountId = child.accountId;
      safeLog('info', '[dev-mock] chore_pick OK (immediate)', { choreId: chore.choreId });
      return {
        choreId: chore.choreId,
        outcome: 'assigned' as const,
        pendingChoreClaimId: null,
      };
    }
    // Gated: ensure no existing pending claim from this child.
    const dup = Array.from(state.pendingChoreClaims.values()).find(
      (pc) =>
        pc.choreId === chore.choreId &&
        pc.requestingChildAccountId === child.accountId &&
        pc.status === 'pending',
    );
    if (dup) {
      throw {
        kind: 'conflict',
        message: `you already have a pending claim (${dup.pendingChoreClaimId}) on this chore`,
      };
    }
    const id = state.nextPendingChoreClaimId++;
    state.pendingChoreClaims.set(id, {
      pendingChoreClaimId: id,
      choreId: chore.choreId,
      requestingChildAccountId: child.accountId,
      status: 'pending',
      requestedAtUtc: new Date().toISOString(),
      decidedAtUtc: null,
      decidedByAccountId: null,
      decisionReason: null,
    });
    safeLog('info', '[dev-mock] chore_pick OK (gated)', {
      choreId: chore.choreId,
      pendingChoreClaimId: id,
    });
    return {
      choreId: chore.choreId,
      outcome: 'pending' as const,
      pendingChoreClaimId: id,
    };
  },

  // US-CHORE-09 — guardian lists pending claims awaiting decision.
  pending_chore_claims_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'guardian-only' };
    }
    const guardianId = state.guardian.accountId;
    return Array.from(state.pendingChoreClaims.values())
      .filter((pc) => {
        if (pc.status !== 'pending') return false;
        const chore = state.chores.get(pc.choreId);
        return chore?.createdByAccountId === guardianId;
      })
      .sort((a, b) => {
        if (a.requestedAtUtc !== b.requestedAtUtc)
          return a.requestedAtUtc.localeCompare(b.requestedAtUtc);
        return a.pendingChoreClaimId - b.pendingChoreClaimId;
      })
      .map((pc) => {
        const chore = state.chores.get(pc.choreId);
        const c = state.children.find((ch) => ch.accountId === pc.requestingChildAccountId);
        return {
          pendingChoreClaimId: pc.pendingChoreClaimId,
          choreId: pc.choreId,
          choreName: chore?.choreName ?? '',
          rewardMinorUnits: chore?.rewardMinorUnits ?? 0,
          requestingChildAccountId: pc.requestingChildAccountId,
          requestingChildDisplayName: c?.displayName ?? '',
          requestedAtUtc: pc.requestedAtUtc,
        };
      });
  },

  // US-CHORE-09 — guardian decides on a pending claim. Approve flips
  // chore + auto-rejects siblings; reject just stamps the row.
  pending_chore_claim_decide: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        pendingChoreClaimId?: number;
        approve?: boolean;
        reason?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'guardian-only' };
    }
    const claim = state.pendingChoreClaims.get(input.pendingChoreClaimId ?? -1);
    if (!claim) {
      throw {
        kind: 'not_found',
        message: `pending chore claim ${input.pendingChoreClaimId} not found`,
      };
    }
    const chore = state.chores.get(claim.choreId);
    if (!chore || chore.createdByAccountId !== state.guardian.accountId) {
      throw {
        kind: 'not_found',
        message: `pending chore claim ${input.pendingChoreClaimId} not found`,
      };
    }
    if (claim.status !== 'pending') {
      throw {
        kind: 'conflict',
        message: `claim ${claim.pendingChoreClaimId} is ${claim.status}, not pending`,
      };
    }
    const approve = input.approve === true;
    if (!approve) {
      const reason = (input.reason ?? '').trim();
      if (reason === '') {
        throw {
          kind: 'validation',
          message: 'a reason is required when rejecting a chore claim',
        };
      }
      claim.status = 'rejected';
      claim.decidedAtUtc = new Date().toISOString();
      claim.decidedByAccountId = state.guardian.accountId;
      claim.decisionReason = reason;
      return {
        pendingChoreClaimId: claim.pendingChoreClaimId,
        outcome: 'rejected' as const,
        autoRejectedSiblingClaimCount: 0,
      };
    }
    // Approve: chore must still be unassigned + active.
    if (chore.childAccountId !== null) {
      throw {
        kind: 'conflict',
        message: `chore ${chore.choreId} was already assigned — refresh the queue`,
      };
    }
    if (!chore.isActive) {
      throw { kind: 'conflict', message: `chore ${chore.choreId} is no longer active` };
    }
    chore.childAccountId = claim.requestingChildAccountId;
    const now = new Date().toISOString();
    claim.status = 'approved';
    claim.decidedAtUtc = now;
    claim.decidedByAccountId = state.guardian.accountId;
    claim.decisionReason = null;
    // Auto-reject sibling pending claims on the same chore.
    let autoRejected = 0;
    for (const other of state.pendingChoreClaims.values()) {
      if (
        other.pendingChoreClaimId !== claim.pendingChoreClaimId &&
        other.choreId === chore.choreId &&
        other.status === 'pending'
      ) {
        other.status = 'rejected';
        other.decidedAtUtc = now;
        other.decidedByAccountId = state.guardian.accountId;
        other.decisionReason = 'claimed by another child';
        autoRejected++;
      }
    }
    return {
      pendingChoreClaimId: claim.pendingChoreClaimId,
      outcome: 'approved' as const,
      autoRejectedSiblingClaimCount: autoRejected,
    };
  },

  // ===================================================================
  // US-BUCKET-16 / 09 / 10 / 11 — bucket transfer handlers
  // ===================================================================

  // US-BUCKET-16 — guardian immediately moves money between two buckets.
  // Unlike a child request this is posted at once, but it must still appear
  // in the child's unified history (REQ-8.3).
  guardian_bucket_transfer: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        sourceBucketId?: number;
        targetBucketId?: number;
        amountMinorUnits?: number;
        note?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'guardian-only' };
    }
    if (
      !input.sourceBucketId ||
      !input.targetBucketId ||
      input.sourceBucketId === input.targetBucketId
    ) {
      throw { kind: 'validation', message: 'source and target buckets must be different' };
    }
    const amount = input.amountMinorUnits ?? 0;
    if (amount <= 0) throw { kind: 'validation', message: 'transfer amount must be positive' };
    const source = state.buckets.find((bucket) => bucket.bucketId === input.sourceBucketId);
    const target = state.buckets.find((bucket) => bucket.bucketId === input.targetBucketId);
    if (!source || !target || !source.isActive || !target.isActive) {
      throw { kind: 'not_found', message: 'source or target bucket not found' };
    }
    if (source.childAccountId !== target.childAccountId) {
      throw {
        kind: 'validation',
        message: 'source and target buckets must belong to the same child',
      };
    }
    if (source.balanceMinorUnits < amount) {
      throw { kind: 'validation', message: `not enough in ${source.bucketName}` };
    }
    source.balanceMinorUnits -= amount;
    target.balanceMinorUnits += amount;
    const transferId = state.nextBucketTransferId++;
    state.ledger.push({
      kind: 'transfer',
      transferId,
      transferMode: 'guardian_direct',
      childAccountId: source.childAccountId,
      atUtc: new Date().toISOString(),
      amountMinorUnits: amount,
      sourceBucketId: source.bucketId,
      sourceBucketName: source.bucketName,
      targetBucketId: target.bucketId,
      targetBucketName: target.bucketName,
      note: (input.note ?? '').trim() || null,
      statusCode: 'posted',
      statusLabel: 'Moved',
      decisionReason: null,
      decidedByDisplayName: state.guardian.displayName,
    });
    return {
      transferId,
      sourceBucketId: source.bucketId,
      sourceBucketName: source.bucketName,
      targetBucketId: target.bucketId,
      targetBucketName: target.bucketName,
      amountMinorUnits: amount,
      sourceBalanceAfter: source.balanceMinorUnits,
      targetBalanceAfter: target.balanceMinorUnits,
    };
  },

  // US-BUCKET-09 — child submits a transfer request between two of
  // their own buckets. Validates ownership + active + sufficient
  // balance + different source/target. Creates a pending row; no
  // balances move.
  transfer_submit: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        sourceBucketId?: number;
        targetBucketId?: number;
        amountMinorUnits?: number;
        note?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const child = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'this action is only available to child accounts' };
    }
    const amount = input.amountMinorUnits ?? 0;
    if (amount <= 0) {
      throw { kind: 'validation', message: 'amount must be greater than zero' };
    }
    if (input.sourceBucketId === input.targetBucketId) {
      throw {
        kind: 'validation',
        message: 'source and target buckets must be different',
      };
    }
    const source = state.buckets.find((b) => b.bucketId === input.sourceBucketId);
    const target = state.buckets.find((b) => b.bucketId === input.targetBucketId);
    if (!source || source.childAccountId !== child.accountId || !source.isActive) {
      throw { kind: 'not_found', message: 'source bucket not found' };
    }
    if (!target || target.childAccountId !== child.accountId || !target.isActive) {
      throw { kind: 'not_found', message: 'target bucket not found' };
    }
    if (source.balanceMinorUnits < amount) {
      throw {
        kind: 'validation',
        message: `not enough in ${source.bucketName} — only ${(source.balanceMinorUnits / 100).toFixed(2)} available`,
      };
    }
    const note = input.note?.trim();
    if (note && note.length > 500) {
      throw { kind: 'validation', message: 'note must be 500 characters or fewer' };
    }
    const id = state.nextBucketTransferId++;
    state.bucketTransfers.set(id, {
      bucketTransferId: id,
      childAccountId: child.accountId,
      sourceBucketId: source.bucketId,
      targetBucketId: target.bucketId,
      amountMinorUnits: amount,
      note: note && note !== '' ? note : null,
      status: 'pending',
      requestedAtUtc: new Date().toISOString(),
      decidedAtUtc: null,
      decidedByAccountId: null,
      decisionReason: null,
      deactivateSourceOnApprove: false,
    });
    safeLog('info', '[dev-mock] transfer_submit OK', { bucketTransferId: id, amount });
    return {
      bucketTransferId: id,
      sourceBucketId: source.bucketId,
      sourceBucketName: source.bucketName,
      targetBucketId: target.bucketId,
      targetBucketName: target.bucketName,
      amountMinorUnits: amount,
    };
  },

  // US-BUCKET-10 — guardian lists pending transfers across linked
  // children. Oldest-first to match other approval queues.
  pending_transfers_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'guardian-only' };
    }
    const linkedChildren = new Set(state.children.map((c) => c.accountId));
    return Array.from(state.bucketTransfers.values())
      .filter((t) => t.status === 'pending' && linkedChildren.has(t.childAccountId))
      .sort((a, b) => {
        if (a.requestedAtUtc !== b.requestedAtUtc)
          return a.requestedAtUtc.localeCompare(b.requestedAtUtc);
        return a.bucketTransferId - b.bucketTransferId;
      })
      .map((t) => {
        const child = state.children.find((c) => c.accountId === t.childAccountId);
        const src = state.buckets.find((b) => b.bucketId === t.sourceBucketId);
        const tgt =
          t.targetBucketId !== null
            ? state.buckets.find((b) => b.bucketId === t.targetBucketId)
            : null;
        return {
          bucketTransferId: t.bucketTransferId,
          childAccountId: t.childAccountId,
          childDisplayName: child?.displayName ?? '',
          sourceBucketId: t.sourceBucketId,
          sourceBucketName: src?.bucketName ?? '',
          targetBucketId: t.targetBucketId,
          targetBucketName: tgt?.bucketName ?? null,
          amountMinorUnits: t.amountMinorUnits,
          note: t.note,
          requestedAtUtc: t.requestedAtUtc,
          deactivateSourceOnApprove: t.deactivateSourceOnApprove,
        };
      });
  },

  // US-BUCKET-10 — guardian approves a pending transfer. Atomic
  // debit-source + credit-target + status flip. Re-validates source
  // balance at decision time (it may have drained between submit
  // and approve via withdrawals).
  transfer_approve: async (payload) => {
    const p = payload as { input?: { sessionId?: string; bucketTransferId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'guardian-only' };
    }
    const t = state.bucketTransfers.get(input.bucketTransferId ?? -1);
    if (!t) {
      throw {
        kind: 'not_found',
        message: `bucket transfer ${input.bucketTransferId} not found`,
      };
    }
    if (t.status !== 'pending') {
      throw {
        kind: 'conflict',
        message: `bucket transfer ${t.bucketTransferId} is ${t.status}, not pending`,
      };
    }
    const src = state.buckets.find((b) => b.bucketId === t.sourceBucketId);
    if (!src || !src.isActive) {
      throw { kind: 'conflict', message: 'source bucket is no longer active' };
    }
    const tgt =
      t.targetBucketId !== null ? state.buckets.find((b) => b.bucketId === t.targetBucketId) : null;
    if (t.amountMinorUnits > 0) {
      if (!tgt || !tgt.isActive) {
        throw { kind: 'conflict', message: 'target bucket is no longer active' };
      }
      if (src.balanceMinorUnits < t.amountMinorUnits) {
        // Per AC: insufficient balance at approval time leaves the
        // transfer pending so the guardian can decide what to do —
        // same pattern as withdrawal_approve.
        throw {
          kind: 'validation',
          message: `not enough in ${src.bucketName} — only ${(src.balanceMinorUnits / 100).toFixed(2)} available now`,
        };
      }
      src.balanceMinorUnits -= t.amountMinorUnits;
      tgt.balanceMinorUnits += t.amountMinorUnits;
    }
    // US-BUCKET-12: deactivate source bucket if this was a delete-with-
    // target request (deactivateSourceOnApprove=true).
    if (t.deactivateSourceOnApprove) {
      src.isActive = false;
    }
    t.status = 'approved';
    t.decidedAtUtc = new Date().toISOString();
    t.decidedByAccountId = state.guardian.accountId;
    return {
      bucketTransferId: t.bucketTransferId,
      sourceBucketId: src.bucketId,
      sourceBucketName: src.bucketName,
      targetBucketId: tgt?.bucketId ?? null,
      targetBucketName: tgt?.bucketName ?? null,
      amountMinorUnits: t.amountMinorUnits,
      newSourceBalanceMinorUnits: src.balanceMinorUnits,
      sourceDeactivated: t.deactivateSourceOnApprove,
    };
  },

  // US-BUCKET-10 — guardian rejects a pending transfer. Reason is
  // required (per AC). No balance changes.
  transfer_reject: async (payload) => {
    const p = payload as {
      input?: { sessionId?: string; bucketTransferId?: number; reason?: string };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    if (!state.guardian || state.lastAuthAccountId !== state.guardian.accountId) {
      throw { kind: 'unauthorized', message: 'guardian-only' };
    }
    const reason = (input.reason ?? '').trim();
    if (reason === '') {
      throw {
        kind: 'validation',
        message: 'a reason is required when rejecting a transfer',
      };
    }
    const t = state.bucketTransfers.get(input.bucketTransferId ?? -1);
    if (!t) {
      throw {
        kind: 'not_found',
        message: `bucket transfer ${input.bucketTransferId} not found`,
      };
    }
    if (t.status !== 'pending') {
      throw {
        kind: 'conflict',
        message: `bucket transfer ${t.bucketTransferId} is ${t.status}, not pending`,
      };
    }
    t.status = 'rejected';
    t.decidedAtUtc = new Date().toISOString();
    t.decidedByAccountId = state.guardian.accountId;
    t.decisionReason = reason;
    return { bucketTransferId: t.bucketTransferId };
  },

  // US-BUCKET-11 — child lists their own pending transfers (for the
  // My requests screen).
  my_pending_transfers_list: async (payload) => {
    const p = payload as { input?: { sessionId?: string } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const child = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'this action is only available to child accounts' };
    }
    return Array.from(state.bucketTransfers.values())
      .filter((t) => t.status === 'pending' && t.childAccountId === child.accountId)
      .sort((a, b) => {
        if (a.requestedAtUtc !== b.requestedAtUtc)
          return b.requestedAtUtc.localeCompare(a.requestedAtUtc);
        return b.bucketTransferId - a.bucketTransferId;
      })
      .map((t) => {
        const src = state.buckets.find((b) => b.bucketId === t.sourceBucketId);
        const tgt = state.buckets.find((b) => b.bucketId === t.targetBucketId);
        return {
          bucketTransferId: t.bucketTransferId,
          sourceBucketId: t.sourceBucketId,
          sourceBucketName: src?.bucketName ?? '',
          targetBucketId: t.targetBucketId,
          targetBucketName: tgt?.bucketName ?? '',
          amountMinorUnits: t.amountMinorUnits,
          note: t.note,
          requestedAtUtc: t.requestedAtUtc,
        };
      });
  },

  // US-BUCKET-11 — child cancels their own pending transfer.
  // NotFound for non-owner (no existence leak); Conflict on already-
  // decided.
  transfer_cancel: async (payload) => {
    const p = payload as { input?: { sessionId?: string; bucketTransferId?: number } };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const child = state.children.find((c) => c.accountId === state.lastAuthAccountId);
    if (!child) {
      throw { kind: 'unauthorized', message: 'this action is only available to child accounts' };
    }
    const t = state.bucketTransfers.get(input.bucketTransferId ?? -1);
    if (!t || t.childAccountId !== child.accountId) {
      throw {
        kind: 'not_found',
        message: `bucket transfer ${input.bucketTransferId} not found`,
      };
    }
    if (t.status !== 'pending') {
      throw {
        kind: 'conflict',
        message: `bucket transfer ${t.bucketTransferId} is ${t.status}, not pending`,
      };
    }
    t.status = 'cancelled';
    t.decidedAtUtc = new Date().toISOString();
    t.decidedByAccountId = child.accountId;
    return { bucketTransferId: t.bucketTransferId };
  },

  // US-BUCKET-12 — child requests deletion of one of their own
  // buckets, with an optional transfer target for any remaining
  // balance. Inserts a single bucket_transfers row with the
  // `deactivateSourceOnApprove` flag set. Approval atomically moves
  // the balance (if any) AND deactivates the source. Mirrors the
  // backend's submit_pending_bucket_deletion validation chain.
  bucket_delete_request: async (payload) => {
    const p = payload as {
      input?: {
        sessionId?: string;
        sourceBucketId?: number;
        targetBucketId?: number | null;
        note?: string;
      };
    };
    const input = p?.input;
    if (!input) throw { kind: 'validation', message: 'Missing input payload.' };
    if (!input.sessionId || state.activeSessionId !== input.sessionId) {
      throw { kind: 'unauthorized', message: 'no active session — please sign in again' };
    }
    const actorId = state.lastAuthAccountId;
    const child = state.children.find((c) => c.accountId === actorId);
    if (!child) {
      throw {
        kind: 'unauthorized',
        message: 'this action is only available to child accounts',
      };
    }
    const source = state.buckets.find(
      (b) => b.bucketId === input.sourceBucketId && b.childAccountId === child.accountId,
    );
    if (!source) {
      throw { kind: 'not_found', message: 'source bucket not found' };
    }
    if (!source.isActive) {
      throw {
        kind: 'validation',
        message: `bucket '${source.bucketName}' is no longer active`,
      };
    }
    if (source.isGuardianManaged) {
      throw {
        kind: 'validation',
        message: `bucket '${source.bucketName}' is guardian-managed and can't be deleted by you`,
      };
    }
    if (source.percentIsFixed) {
      throw {
        kind: 'validation',
        message: `bucket '${source.bucketName}' is a fixed-percent bucket and can't be deleted`,
      };
    }

    // Target validation depends on whether there's a balance to move.
    let targetId: number | null = null;
    let targetName: string | null = null;
    if (source.balanceMinorUnits > 0) {
      if (input.targetBucketId == null) {
        throw {
          kind: 'validation',
          message: `bucket '${source.bucketName}' has a balance — pick another bucket to receive it`,
        };
      }
      if (input.targetBucketId === source.bucketId) {
        throw {
          kind: 'validation',
          message: "target must be a different bucket than the one you're deleting",
        };
      }
      const target = state.buckets.find(
        (b) => b.bucketId === input.targetBucketId && b.childAccountId === child.accountId,
      );
      if (!target) {
        throw { kind: 'not_found', message: 'target bucket not found' };
      }
      if (!target.isActive) {
        throw {
          kind: 'validation',
          message: `target bucket '${target.bucketName}' is no longer active`,
        };
      }
      targetId = target.bucketId;
      targetName = target.bucketName;
    }

    const id = state.nextBucketTransferId++;
    const note = (input.note ?? '').trim();
    state.bucketTransfers.set(id, {
      bucketTransferId: id,
      childAccountId: child.accountId,
      sourceBucketId: source.bucketId,
      targetBucketId: targetId,
      amountMinorUnits: source.balanceMinorUnits,
      note: note === '' ? null : note,
      status: 'pending',
      requestedAtUtc: new Date().toISOString(),
      decidedAtUtc: null,
      decidedByAccountId: null,
      decisionReason: null,
      deactivateSourceOnApprove: true,
    });
    safeLog('info', '[dev-mock] bucket_delete_request OK', {
      bucketTransferId: id,
      sourceBucketId: source.bucketId,
      targetBucketId: targetId,
      amountMinorUnits: source.balanceMinorUnits,
    });
    return {
      bucketTransferId: id,
      sourceBucketId: source.bucketId,
      sourceBucketName: source.bucketName,
      targetBucketId: targetId,
      targetBucketName: targetName,
      amountMinorUnits: source.balanceMinorUnits,
    };
  },
};

/** Literal Tauri controls that intentionally remain outside the public web
 * demo because they require a signed mobile shell, OS service, local database,
 * or authenticated cloud entitlement. Keep this list exhaustive: the website
 * contract test compares it with every literal `invoke()` command. */
export const PUBLIC_DEMO_INSTALLED_APP_ONLY_COMMANDS = [
  'backup_export_bytes',
  'backup_restore_bytes',
  'biometric_disable_for_account',
  'biometric_enable_for_account',
  'biometric_login',
  'biometric_support',
  'cloud_restore_complete',
  'cloud_subscription_link_existing',
  'cloud_sync_apply',
  'device_assign',
  'device_get_id',
  'device_list',
  'device_revoke',
  'device_unassign',
  'network_connection_check',
  'network_disable',
  'network_enable_master',
  'network_get_pairing_qr',
  'network_list_conflicts',
  'network_pair_at_setup',
  'network_pair_with_master',
  'network_resolve_conflict',
  'network_sync_now',
  'plugin:barcode-scanner|scan',
  'plugin:google-signin|sign_in',
  'plugin:siwa|sign_in',
  'report_export_csv',
  'trial_migration_export',
] as const;

/** Deliberately not registered as a mock handler. Rejecting this one command
 * makes the reward-audio controller use its real Web Audio fallback. */
export const PUBLIC_DEMO_WEB_AUDIO_FALLBACK_COMMANDS = [
  'plugin:chorepop-reward-audio-ios|play_reward_sound',
] as const;

const publicDemoInstalledAppOnlyCommands = new Set<string>(PUBLIC_DEMO_INSTALLED_APP_ONLY_COMMANDS);
const publicDemoWebAudioFallbackCommands = new Set<string>(PUBLIC_DEMO_WEB_AUDIO_FALLBACK_COMMANDS);

function publicDemoUnavailable(command: string): never {
  throw {
    kind: 'not_supported',
    message:
      'This control works only in the installed ChorePop app. The website demo keeps all family data temporary.',
    command,
  };
}

export function installDevTauriMock(): void {
  const publicDemo = isPublicDemoRequested();
  if (!import.meta.env.DEV && !publicDemo) return;

  const w = window as unknown as {
    // biome-ignore lint/style/useNamingConvention: Tauri's internal global is named this exactly; we don't get to rename it
    __TAURI_INTERNALS__?: { invoke?: AnyHandler };
  };
  if (w.__TAURI_INTERNALS__?.invoke) {
    // Real Tauri is present — don't overwrite.
    return;
  }

  if (publicDemo) seedPublicDemoState();

  w.__TAURI_INTERNALS__ = w.__TAURI_INTERNALS__ ?? {};
  w.__TAURI_INTERNALS__.invoke = async (command: unknown, payload?: unknown) => {
    const name = String(command);
    const h = handlers[name] ?? (publicDemo ? publicDemoHandlers[name] : undefined);
    if (!h) {
      if (publicDemoWebAudioFallbackCommands.has(name)) {
        throw {
          kind: 'not_supported',
          message: 'Native reward audio is unavailable in the browser demo.',
        };
      }
      if (publicDemo && publicDemoInstalledAppOnlyCommands.has(name)) {
        return publicDemoUnavailable(name);
      }
      if (publicDemo) return publicDemoUnavailable(name);
      throw {
        kind: 'internal',
        message: `[dev-mock] No handler registered for command '${name}'. Real Tauri backend is not running (this is a browser dev session).`,
      };
    }
    return h(payload);
  };

  safeLog(
    'info',
    '[dev-mock] Tauri invoke shim installed for browser dev. Commands available:',
    Object.keys(handlers),
  );
}

// Helper for tests and for the dev console — reset the mock between runs.
export function resetDevTauriMock(): void {
  state.accountsExist = false;
  state.guardian = null;
  state.children = [];
  state.buckets = [];
  state.activeSessionId = null;
  state.activeSessionExpiresAtUtc = null;
  state.failureTimestampsMs.clear();
  state.lockoutExpiresAtMs.clear();
  state.nextAccountId = 2;
  state.nextBucketId = 1;
  state.nextDepositId = 1;
  state.nextWithdrawalId = 1;
  state.lastAuthAccountId = null;
  state.nextChoreId = 1;
  state.nextChoreCompletionId = 1;
  state.choreSchedules = [];
  state.nextChoreScheduleId = 1;
  state.legacyBucketTemplates = [];
  state.bucketTemplates = [];
  state.nextBucketTemplateId = 1;
  state.routines = [];
  state.routineInstances = [];
  state.nextRecurringTaskId = 1;
  state.nextRoutineInstanceId = 1;
  state.goals = [];
  state.nextGoalId = 1;
  state.allowances = [];
  state.nextAllowanceScheduleId = 1;
  state.ledger = [];
  state.guardianTypeByChildId = new Map();
  state.chores = new Map();
  state.openClaims = new Set();
  state.choreCompletions = new Map();
  state.pendingDeposits = [];
  state.childApprovalNotices = [];
  state.nextChildApprovalNoticeId = 1;
  state.recoverableChildren = [];
  state.loginSecurityAlerts = [];
  state.nextLoginSecurityAlertId = 1;
  state.validSettingsTokens = new Set();
  state.pendingWithdrawals = [];
  state.additionalGuardians = [];
  state.pinsByAccount = new Map();
  state.pinFailuresByAccount = new Map();
  state.pinLockoutsByAccount = new Map();
  state.exchangeRates = [];
  state.nextExchangeRateId = 1;
  state.pendingChoreClaims = new Map();
  state.nextPendingChoreClaimId = 1;
  state.bucketTransfers = new Map();
  state.nextBucketTransferId = 1;
  state.familyChallenge = null;
  state.nextFamilyChallengeId = 1;
}

// Expose the reset helper on window for one-liner dev use:
//   window.__chorepopResetMock()
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__chorepopResetMock = resetDevTauriMock;
}
